/**
 * Product Customization Table
 * Handles product customization UI and interactions
 * @version 1.0.1 - Fixed getVariantId method call
 */

/**
 * @typedef {Object} StyleOption
 * @property {string} name
 * @property {string} displayName
 * @property {number} selected
 * @property {string} [monogram]
 * @property {Array<any>} [options]
 */

/**
 * @typedef {Object} CollarOption
 * @property {string} title
 * @property {string} image
 * @property {string} description
 */

/**
 * @typedef {Object} CuffOption
 * @property {string} title
 * @property {string} image
 * @property {string} description
 */

/**
 * @typedef {Object} PocketOption
 * @property {string} title
 * @property {string|null} image
 * @property {string} description
 */

/**
 * @typedef {Object} TailOption
 * @property {string} title
 * @property {string|null} image
 * @property {string} description
 * @property {string} [key]
 * @property {string} [summaryName]
 */

/**
 * @typedef {Object} FrontOption
 * @property {string} title
 * @property {string|null} image
 * @property {string} description
 * @property {string} [key]
 * @property {string} [summaryName]
 */

/**
 * @typedef {Object<string, string|null>} Metafields
 * @property {string|null} collar
 * @property {string|null} chest
 * @property {string|null} sleeve
 * @property {string|null} fit
 * @property {string|null} waist
 * @property {string|null} monogram
 * @property {string|null} monogram_initials
 * @property {string|null} pocket_style
 * @property {string|null} cuff_style
 * @property {string|null} collar_style
 * @property {string|null} tail_style
 */

/**
 * @typedef {Object<string, any>} CustomizationData
 */

/**
 * @typedef {Object} FitSelection
 * @property {string} name
 * @property {string} value
 */

/**
 * @typedef {Object} StyleSelection
 * @property {string} group
 * @property {string} value
 */

class ProductCustomizationTable {
  /**
   * @type {HTMLElement|null}
   */
  customizeButton = null;

  /**
   * @type {HTMLElement|null}
   */
  productDetailsContainer = null;

  /**
   * @type {HTMLElement|null}
   */
  originalContent = null;

  /**
   * @type {boolean}
   */
  isCustomizationShown = false;

  /**
   * @type {number}
   */
  activeTab = 0;

  /**
   * @type {string|null}
   */
  selectedPath = null;

  /** @type {Set<string>} Fields currently showing "As Last" in the summary */
  fitMostRecentFields = new Set();

  /** @param {unknown} value */
  isLastOrderSentinel(value) {
    const s = String(value ?? '').trim().toLowerCase();
    return s === 'as last' || s === 'as last.' || s === 'most recent' || s === 'most recent.';
  }

  /** @type {Array<{name: string, value: string}>} Snapshot of fit values as loaded from the server at session start */
  fitMetafieldBaseline = [];

  /**
   * @type {string}
   */
  shirtStyle = '';

  /**
   * @type {string}
   */
  productOrganizationType = '';

  /**
   * @type {Array<StyleOption>}
   */
  styleOptions = [];

  /**
   * @type {number|null}
   */
  editingOptionIndex = null;

  /**
   * @type {((e: MouseEvent) => void)|null}
   */
  clickHandlerBound = null;

  /**
   * @type {boolean}
   */
  customerMetafieldsLoaded = false;

  /**
   * @type {boolean}
   */
  editIconListenerAttached = false;

  /** Used to cancel in-flight {@link ProductCustomizationTable#switchTab} after a newer navigation */
  switchTabGeneration = 0;

  /**
   * @type {any|null}
   */
  customerData = null;

  /**
   * @type {Array<{size: string, chest: string, sleeve: string}>}
   */
  standardSizes = [];

  /**
   * @type {Record<string, string[]>}
   */
  fitOptions = {};

  /**
   * @type {Array<CollarOption>}
   */
  collarOptions = [];

  /**
   * @type {Array<CuffOption>}
   */
  cuffOptions = [];

  /**
   * @type {Array<PocketOption>}
   */
  pocketOptions = [];

  /**
   * @type {Array<TailOption>}
   */
  tailOptions = [];

  /**
   * @type {Array<FrontOption>}
   */
  frontOptions = [];

  /**
   * @type {Object}
   */
  cachedSelections = {};

  /**
   * @type {number}
   */
  baseProductPrice = 0;

  /**
   * @type {boolean}
   */
  priceSyncSetup = false;

  /**
   * @type {HTMLElement|null}
   */
  customizationFormContainer = null;

  /**
   * @type {string|null}
   */
  authView = null; // 'login', 'signup', 'forgot-password', or null

  /**
   * @type {boolean}
   */
  authLoading = false;

  /**
   * @type {string|null}
   */
  tempNavigationIntent = null;

  constructor() {
    this.customizeButton = null;
    this.productDetailsContainer = null;
    this.originalContent = null;
    this.isCustomizationShown = false;
    this.activeTab = 0;
    this.shirtStyle = '';
    this.styleOptions = [];
    this.editingOptionIndex = null;
    this.clickHandlerBound = null;
    this.customerMetafieldsLoaded = false;
    this.customerData = null; // Store fetched customer data
    this.tempNavigationIntent = null;
    this.editIconListenerAttached = false;

    this.standardSizes = [
      { size: '14.5', chest: '36', sleeve: '33' },
      { size: '15', chest: '38', sleeve: '34' },
      { size: '15.5', chest: '40', sleeve: '35' },
      { size: '16', chest: '42', sleeve: '35' },
      { size: '16.5', chest: '44', sleeve: '36' },
      { size: '17', chest: '46', sleeve: '36' },
      { size: '17.5', chest: '48', sleeve: '36' },
      { size: '18', chest: '50', sleeve: '37' },
      { size: '18.5', chest: '52', sleeve: '37' }
    ];

    this.fitOptions = {
      collar: ["14½", "15", "15½", "16", "16½", "17", "17½", "18", "18½", "19", "19½", "20"],
      chest: ["36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "50", "51", "52"],
      fit: ["regular", "trim"],
      sleeve: ["30", "31", "32", "33", "34", "35", "36", "37", "38"]
    };

    // Style option arrays are built per Shirt Style in populateStyleOptions()
    this.collarOptions = [];
    this.cuffOptions = [];
    this.pocketOptions = [];
    this.tailOptions = [];
    this.frontOptions = [];

    this.authView = null;
    this.authLoading = false;
    this.init();
    this.initEditButtons();
    this.initOutsideClick();
    this.cachedSelections = { fit: [], style: [] };
    this.initAddToCartHook();
    this.checkAndClearOnLogout(); // Check if user logged out and clear cached data
  }

  /**
   * Check if user logged out and clear cached selections
   * This prevents old selections from persisting after logout
   */
  checkAndClearOnLogout() {
    const isCurrentlyLoggedIn = window.ShopifyUtils?.isCustomerLoggedIn?.() ||
      window.isCustomerLoggedIn === true ||
      String(window.isCustomerLoggedIn) === 'true';

    const wasLoggedInBefore = localStorage.getItem('customization_user_logged_in') === 'true';
    const storedCustomerId = localStorage.getItem('customization_customer_id');
    const currentCustomerId = window.CUSTOMER_ID || null;

    // User was logged in before but is now logged out - clear all cached data
    if (wasLoggedInBefore && !isCurrentlyLoggedIn) {
      this.clearAllCachedSelections();
    }

    // Different user logged in - clear previous user's data
    if (isCurrentlyLoggedIn && storedCustomerId && currentCustomerId && storedCustomerId !== String(currentCustomerId)) {
      this.clearAllCachedSelections();
    }

    // Update the stored login state and customer ID
    if (isCurrentlyLoggedIn) {
      localStorage.setItem('customization_user_logged_in', 'true');
      if (currentCustomerId) {
        localStorage.setItem('customization_customer_id', String(currentCustomerId));
      }
    } else {
      // User is logged out - clear all customization data
      this.clearAllCachedSelections();
    }
  }

  /**
   * Clear all cached selections from memory and storage
   */
  clearAllCachedSelections() {

    // Clear window.cachedSelections
    window.cachedSelections = { fit: [], style: [] };
    this.cachedSelections = { fit: [], style: [] };

    // Clear sessionStorage - all customization related items
    sessionStorage.removeItem('cachedSelections');
    sessionStorage.removeItem('customization_fit');
    sessionStorage.removeItem('customization_style');

    // Clear localStorage items related to customization
    localStorage.removeItem('redirectedForLogin');
    localStorage.removeItem('customization_user_logged_in');
    localStorage.removeItem('customization_customer_id');
    localStorage.removeItem('customization_selections');
    localStorage.removeItem('customization_fit_data');
    localStorage.removeItem('customization_style_data');

    // Clear any keys that start with 'customization_'
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('customization_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Also clear from sessionStorage
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('customization_')) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));

    // Reset style options to Option 1 defaults
    if (this.styleOptions && Array.isArray(this.styleOptions)) {
      this.styleOptions.forEach(option => {
        option.selected = 0;
        if (option.name === 'monogram') {
          option.monogram = '';
        }
      });
    }

    // Reset customer metafields loaded flag
    this.customerMetafieldsLoaded = false;
    this.customerData = null;

  }

  /**
   * Static method to clear customization data (can be called from outside)
   * Useful for logout handlers
   */
  static clearCustomizationOnLogout() {

    // Clear window.cachedSelections
    if (typeof window !== 'undefined') {
      window.cachedSelections = { fit: [], style: [] };
    }

    // Clear sessionStorage
    sessionStorage.removeItem('cachedSelections');

    // Clear all customization-related localStorage items
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('customization_') || key === 'redirectedForLogin')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear all customization-related sessionStorage items
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('customization_')) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));

  }

  /**
   * Fetch customer data from API
   */
  async fetchCustomerData() {
    if (this.customerData) {
      return this.customerData;
    }

    try {
      // Check if user is logged in
      if (!window.ShopifyUtils || !window.ShopifyUtils.isCustomerLoggedIn()) {
        return null;
      }

      // Get access token
      const token = window.ShopifyUtils.getCustomerToken();
      if (!token) {
        return null;
      }

      // Fetch customer data
      if (window.shopifyCustomer) {
        const result = await window.shopifyCustomer.getCustomer(token);
        if (result.success && result.customer) {
          this.customerData = result.customer;

          // Update global variables for backward compatibility
          window.CUSTOMER_ID = this.customerData.id;
          window.CUSTOMER_EMAIL = this.customerData.email;
          window.CUSTOMER_LOGGED_IN = true;
          window.isCustomerLoggedIn = true;

          return this.customerData;
        }
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
    }

    return null;
  }

  /**
   * Get customer metafields from fetched data
   * @returns {Record<string, string|null>}
   */
  getCustomerMetafields() {
    if (!this.customerData || !this.customerData.metafields) {
      return {};
    }

    /** @type {Record<string, string|null>} */
    const metafields = {};
    const customMetafields = this.customerData.metafields;

    // Map metafields to expected structure
    metafields.collar = customMetafields.collar || null;
    metafields.chest = customMetafields.chest || null;
    metafields.sleeve = customMetafields.sleeve || null;
    metafields.fit = customMetafields.fit || null;
    metafields.waist = customMetafields.waist || null;
    metafields.monogram = customMetafields.monogram || null;
    metafields.monogram_initials = customMetafields.monogram_initials || null;
    metafields.pocket_style = customMetafields.pocket_style || null;
    metafields.cuff_style = customMetafields.cuff_style || null;
    metafields.collar_style = customMetafields.collar_style || null;
    metafields.tail_style = customMetafields.tail_style || null;

    return metafields;
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async loadCustomerMetafields() {
    // Only load once
    if (this.customerMetafieldsLoaded) {
      return;
    }

    // Fetch customer data first
    const customer = await this.fetchCustomerData();
    if (!customer) {
      return;
    }

    // Get metafields
    const metafields = this.getCustomerMetafields();

    // Initialize cachedSelections if not exists
    if (!window.cachedSelections) {
      window.cachedSelections = { fit: [], style: [] };
    }

    // Fit measurements are driven by hamilton_customization localStorage (created on add to cart),
    // not account metafields — see hasSavedFitData().

    // === LOAD STYLE PREFERENCES ===
    let styleLoadedCount = 0;

    // Handle Collar Style (customer preference may override Option 1 default)
    const collarStyle = metafields.collar_style;
    if (collarStyle && this.styleOptions) {
      const collarOption = this.styleOptions.find(opt => opt.name === 'collar');
      if (collarOption && this.collarOptions?.length) {
        const collarIndex = this.findStyleChoiceIndex(this.collarOptions, collarStyle);
        if (collarIndex !== -1) {
          collarOption.selected = collarIndex;
          styleLoadedCount++;
        }
      }
    }

    // Handle Cuff Style
    const cuffStyle = metafields.cuff_style;
    if (cuffStyle && this.styleOptions) {
      const cuffOption = this.styleOptions.find(opt => opt.name === 'cuff');
      if (cuffOption && this.cuffOptions?.length) {
        const cuffIndex = this.findStyleChoiceIndex(this.cuffOptions, cuffStyle);
        if (cuffIndex !== -1) {
          cuffOption.selected = cuffIndex;
          styleLoadedCount++;
        }
      }
    }

    // Handle Pocket Style
    const pocketStyle = metafields.pocket_style;
    if (pocketStyle && this.styleOptions) {
      const pocketOption = this.styleOptions.find(opt => opt.name === 'pocket');
      if (pocketOption && this.pocketOptions?.length) {
        const pocketIndex = this.findStyleChoiceIndex(this.pocketOptions, pocketStyle);
        if (pocketIndex !== -1) {
          pocketOption.selected = pocketIndex;
          styleLoadedCount++;
        }
      }
    }

    // Handle Tail Style
    const tailStyle = metafields.tail_style;
    if (tailStyle && this.styleOptions) {
      const tailOption = this.styleOptions.find(opt => opt.name === 'tail');
      if (tailOption && this.tailOptions?.length) {
        const tailIndex = this.findStyleChoiceIndex(this.tailOptions, tailStyle);
        if (tailIndex !== -1) {
          tailOption.selected = tailIndex;
          styleLoadedCount++;
        }
      }
    }

    // Handle Monogram and Monogram Initials
    const monogramValue = metafields.monogram;
    const monogramInitials = metafields.monogram_initials;

    if (this.styleOptions) {
      const monogramOption = this.styleOptions.find(opt => opt.name === 'monogram');
      if (monogramOption) {
        let processedMonogram = false;

        // Priority 1: Check monogram_initials field first
        if (monogramInitials !== null && monogramInitials !== undefined && String(monogramInitials).trim() !== '') {
          const initials = String(monogramInitials).trim().toUpperCase();
          if (initials.length > 0 && initials.length <= 3) {
            monogramOption.selected = 1;
            monogramOption.monogram = initials;
            styleLoadedCount++;
            processedMonogram = true;
          }
        }

        // Priority 2: Check monogram field
        if (!processedMonogram && monogramValue !== null && monogramValue !== undefined) {
          const monogramStr = String(monogramValue).toLowerCase().trim();

          if (monogramStr === 'yes' || monogramStr === 'true' || monogramStr === '1') {
            monogramOption.selected = 1;
            if (!monogramOption.monogram) {
              monogramOption.monogram = '';
            }
            styleLoadedCount++;
            processedMonogram = true;
          } else if (monogramStr === 'no' || monogramStr === 'false' || monogramStr === '0' || monogramStr === '') {
            monogramOption.selected = 0;
            monogramOption.monogram = '';
            styleLoadedCount++;
            processedMonogram = true;
          } else if (monogramStr.length > 0 && monogramStr.length <= 3) {
            monogramOption.selected = 1;
            monogramOption.monogram = String(monogramValue).toUpperCase();
            styleLoadedCount++;
            processedMonogram = true;
          }
        }

        if (!processedMonogram) {
          monogramOption.selected = 0;
          monogramOption.monogram = '';
        }
      }
    }

    this.customerMetafieldsLoaded = true;

    // Sync priced variant to saved monogram Yes or No (not only Yes).
    // Only while customize is open — standard PDP must stay on Monogram No.
    if (this.isCustomizationShown) {
      setTimeout(() => this.syncMonogramVariantFromSelection(), 100);
    }
  }


  /**
   * Build current metafields object from form selections
   * Returns the metafields object that would be sent to API
   * @returns {Record<string, string>}
   */
  buildCurrentMetafields() {
    // Build style array from this.styleOptions
    /** @type {Array<{group: string, value: string}>} */
    const styleArray = [];
    if (this.styleOptions && Array.isArray(this.styleOptions)) {
      this.styleOptions.forEach(option => {
        if (!option || option.selected === -1 || option.selected === undefined) {
          return;
        }

        if (option.name === 'monogram') {
          const productCode = this.getMonogramProductCode(this.getVariantId());
          if (option.selected === 1 && option.monogram) {
            const initials = option.monogram.trim().toUpperCase();
            const monogramValue = productCode
              ? `${productCode} / Yes / ${initials}`
              : `Yes / ${initials}`;
            styleArray.push({ group: 'monogram', value: monogramValue });
          } else {
            styleArray.push({ group: 'monogram', value: productCode ? `${productCode} / No` : 'No' });
          }
        } else if (option.name === 'collar' && this.collarOptions && this.collarOptions[option.selected]) {
          const selectedCollar = this.collarOptions[option.selected];
          if (selectedCollar) {
            styleArray.push({ group: 'collar', value: selectedCollar.title || '' });
          }
        } else if (option.name === 'cuff' && this.cuffOptions && this.cuffOptions[option.selected]) {
          const selectedCuff = this.cuffOptions[option.selected];
          if (selectedCuff) {
            styleArray.push({ group: 'cuff', value: selectedCuff.title || '' });
          }
        } else if (option.name === 'pocket' && this.pocketOptions && this.pocketOptions[option.selected]) {
          const selectedPocket = this.pocketOptions[option.selected];
          if (selectedPocket) {
            styleArray.push({ group: 'pocket', value: selectedPocket.title || '' });
          }
        } else if (option.name === 'tail' && this.tailOptions && this.tailOptions[option.selected]) {
          const selectedTail = this.tailOptions[option.selected];
          if (selectedTail) {
            styleArray.push({ group: 'tail', value: selectedTail.title || '' });
          }
        } else if (option.name === 'front' && this.frontOptions && this.frontOptions[option.selected]) {
          const selectedFront = this.frontOptions[option.selected];
          if (selectedFront) {
            styleArray.push({ group: 'front', value: selectedFront.title || '' });
          }
        } else if (option.selected !== -1 && option.options && option.options[option.selected]) {
          const selectedOption = option.options[option.selected];
          styleArray.push({
            group: option.name,
            value: selectedOption.summaryName || selectedOption.name || selectedOption.title
          });
        }
      });
    }

    // Build customization data object
    /** @type {Record<string, any>} */
    const customizationData = {};
    const fitFieldsAdded = new Set();

    if (window.cachedSelections?.fit && Array.isArray(window.cachedSelections.fit)) {
      window.cachedSelections.fit.forEach(item => {
        if (item && item.name && item.value) {
          const valueToUse = this.fitMostRecentFields.has(item.name)
            ? this.getTrueMostRecentValue(item.name)
            : item.value;
          customizationData[item.name] = valueToUse;
          fitFieldsAdded.add(item.name.toLowerCase());
        }
      });
    }

    styleArray.forEach(item => {
      if (item && item.group && item.value) {
        const styleKey = item.group.toLowerCase();
        if (fitFieldsAdded.has(styleKey)) {
          customizationData[`style_${item.group}`] = item.value;
        } else {
          customizationData[item.group] = item.value;
        }
      }
    });

    // Build metafields object
    /** @type {Record<string, string>} */
    const metafields = {};

    const fitFields = ['waist', 'chest', 'collar', 'sleeve', 'fit'];
    fitFields.forEach(fieldName => {
      if (customizationData[fieldName]) {
        if (fieldName === 'collar') {
          metafields[fieldName] = this.convertCollarToDecimal(customizationData[fieldName]);
        } else {
          metafields[fieldName] = customizationData[fieldName];
        }
      }
    });

    const styleFields = ['collar', 'cuff', 'pocket', 'monogram'];
    styleFields.forEach(fieldName => {
      const stylePrefixedKey = `style_${fieldName}`;
      if (customizationData[stylePrefixedKey]) {
        if (fieldName === 'collar') {
          metafields['collar_style'] = customizationData[stylePrefixedKey];
        } else {
          metafields[fieldName] = customizationData[stylePrefixedKey];
        }
      } else if (customizationData[fieldName] && !metafields[fieldName]) {
        if (fieldName === 'collar') {
          metafields['collar_style'] = customizationData[fieldName];
        } else {
          metafields[fieldName] = customizationData[fieldName];
        }
      }
    });

    if (this.styleOptions && Array.isArray(this.styleOptions)) {
      this.styleOptions.forEach(option => {
        if (option.name === 'monogram') {
          if (option.selected === 1) {
            metafields['monogram'] = 'yes';
            if (option.monogram) {
              metafields['monogram_initials'] = option.monogram.trim().toUpperCase();
            }
          } else {
            metafields['monogram'] = 'no';
            metafields['monogram_initials'] = '';
          }
        } else if (option.name === 'collar' && option.selected !== -1 && this.collarOptions) {
          const selectedCollar = this.collarOptions[option.selected];
          if (selectedCollar) {
            metafields['collar_style'] = selectedCollar.title;
          }
        } else if (option.name === 'cuff' && option.selected !== -1 && this.cuffOptions) {
          const selectedCuff = this.cuffOptions[option.selected];
          if (selectedCuff) {
            metafields['cuff_style'] = selectedCuff.title;
          }
        } else if (option.name === 'pocket' && option.selected !== -1 && this.pocketOptions) {
          const selectedPocket = this.pocketOptions[option.selected];
          if (selectedPocket) {
            metafields['pocket_style'] = selectedPocket.title;
          }
        } else if (option.name === 'tail' && option.selected !== -1 && this.tailOptions) {
          const selectedTail = this.tailOptions[option.selected];
          if (selectedTail) {
            metafields['tail_style'] = selectedTail.title;
          }
        } else if (option.name === 'front' && option.selected !== -1 && this.frontOptions) {
          const selectedFront = this.frontOptions[option.selected];
          if (selectedFront) {
            metafields['front_style'] = selectedFront.title;
          }
        }
      });
    }

    const allowedKeys = [
      'monogram_initials',
      'monogram',
      'pocket_style',
      'cuff_style',
      'collar_style',
      'tail_style',
      'front_style',
      'fit',
      'chest',
      'waist',
      'collar',
      'sleeve'
    ];

    /** @type {Record<string, string>} */
    const filteredMetafields = {};
    allowedKeys.forEach(key => {
      if (metafields[key] !== undefined && metafields[key] !== null && metafields[key] !== '') {
        filteredMetafields[key] = String(metafields[key]);
      }
    });

    return filteredMetafields;
  }

  /**
   * Normalize a metafield value for comparison
   * Handles all the format differences between customer and current metafields
   * @param {string} key
   * @param {any} value
   * @returns {string|null}
   */
  normalizeMetafieldValue(key, value) {
    if (value === null || value === undefined || value === '') {
      return null; // Treat null/undefined/empty as equivalent
    }

    const stringValue = String(value).trim();

    // Handle numeric fields (remove .0 trailing zeros)
    if (['chest', 'sleeve', 'waist'].includes(key)) {
      return stringValue.replace(/\.0+$/, '');
    }

    // Handle collar (convert fractions to decimal, remove .0)
    if (key === 'collar') {
      if (/^\d+\.?\d*$/.test(stringValue)) {
        return stringValue.replace(/\.0+$/, '');
      } else {
        return this.convertCollarToDecimal(stringValue);
      }
    }

    // Handle monogram (normalize to 'yes'/'no')
    if (key === 'monogram') {
      const monogramStr = stringValue.toLowerCase();
      if (monogramStr === 'yes' || monogramStr === 'true' || monogramStr === '1') {
        return 'yes';
      } else {
        return 'no';
      }
    }

    // Handle style fields (lowercase, normalize "No Pocket" to "no pocket")
    if (['fit', 'collar_style', 'cuff_style', 'pocket_style', 'tail_style'].includes(key)) {
      const lowerValue = stringValue.toLowerCase();
      // Normalize "no pocket" variations
      if (key === 'pocket_style' && (lowerValue === 'no pocket' || lowerValue === 'nopocket')) {
        return 'no pocket';
      }
      return lowerValue;
    }

    // Handle monogram_initials (uppercase, trim)
    if (key === 'monogram_initials') {
      return stringValue.toUpperCase();
    }

    // Default: return trimmed string
    return stringValue;
  }

  /**
   * Check if current metafields differ from customer's saved metafields
   * @param {Record<string, string>} currentMetafields
   * @returns {boolean}
   */
  hasMetafieldsChanged(currentMetafields) {
    const customerMetafields = this.getCustomerMetafields();

    // Get all unique keys from both objects
    const allKeys = new Set([
      ...Object.keys(currentMetafields || {}),
      ...Object.keys(customerMetafields || {})
    ]);

    // Compare each key
    for (const key of allKeys) {
      const currentValue = this.normalizeMetafieldValue(key, currentMetafields[key]);
      const customerValue = this.normalizeMetafieldValue(key, customerMetafields[key]);

      // If values differ, metafields have changed
      if (currentValue !== customerValue) {
        return true;
      }
    }

    return false;
  }

  /**
   * Helper: Check if value is empty/null/undefined
   * @param {any} value
   * @returns {boolean}
   */
  isEmptyValue(value) {
    return value === null || value === undefined || value === '';
  }

  /**
   * Helper: Check if value matches a known default
   * @param {string} key
   * @param {any} value
   * @param {Record<string, any>} defaults
   * @returns {boolean}
   */
  isDefaultValue(key, value, defaults) {
    if (!defaults || !defaults[key]) {
      return false;
    }

    const defaultValue = defaults[key];
    const normalizedValue = String(value).toLowerCase().trim();

    if (Array.isArray(defaultValue)) {
      return defaultValue.some(def =>
        String(def).toLowerCase().trim() === normalizedValue
      );
    }

    return String(defaultValue).toLowerCase().trim() === normalizedValue;
  }

  /**
   * Helper: Normalize value for comparison
   * @param {string} key
   * @param {any} value
   * @returns {string}
   */
  normalizeForComparison(key, value) {
    if (this.isEmptyValue(value)) return '';

    let normalized = String(value).toLowerCase().trim();

    // Remove spaces and special chars for certain fields
    if (['pocket_style', 'cuff_style', 'collar_style', 'tail_style'].includes(key)) {
      normalized = normalized.replace(/[\s_-]/g, '');
    }

    // For measurements, normalize decimal representation
    if (['chest', 'waist', 'collar', 'sleeve'].includes(key)) {
      const num = parseFloat(normalized);
      if (!isNaN(num)) {
        normalized = String(num);
      }
    }

    return normalized;
  }



  initializeBasePrice() {
    if (!this.baseProductPrice) {
      const priceElement = document.querySelector('.product-price, .price, [data-product-price], .money');
      const priceText = priceElement ? priceElement.textContent.trim() : '$278.00';
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        this.baseProductPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
      } else {
        this.baseProductPrice = 278.00;
      }
    }
  }

  setup() {
    this.productDetailsContainer = document.querySelector('.product-details');

    if (this.productDetailsContainer) {
      if (!this.productDetailsContainer.querySelector('.original-product-content')) {
        const originalContentWrapper = document.createElement('div');
        originalContentWrapper.className = 'original-product-content';
        // Move live nodes (do NOT use innerHTML) — innerHTML destroys hydrated
        // <variant-swatch-king> and leaves an empty shell after refresh.
        while (this.productDetailsContainer.firstChild) {
          originalContentWrapper.appendChild(this.productDetailsContainer.firstChild);
        }
        this.productDetailsContainer.appendChild(originalContentWrapper);
      }

      if (!this.clickHandlerBound) {
        this.clickHandlerBound = (/** @type {MouseEvent} */ e) => {
          const target = e.target;
          if (!(target instanceof HTMLElement)) return;

          const customizeBtn = target.closest('.pdt-customize-btn');
          if (customizeBtn) {
            e.preventDefault();
            e.stopPropagation();
            this.toggleCustomizationTable();
          }
        };
        document.addEventListener('click', this.clickHandlerBound);
      }

      this.customizeButton = document.querySelector('.pdt-customize-btn');
      if (this.customizeButton && this.customizeButton instanceof HTMLElement) {
        const existingHandler = this.customizeButton.getAttribute('data-customize-handler');
        if (!existingHandler) {
          const directHandler = (/** @type {MouseEvent} */ e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleCustomizationTable();
          };
          this.customizeButton.addEventListener('click', directHandler);
          this.customizeButton.setAttribute('data-customize-handler', 'attached');
        }
      }

      // Selecting Monogram Yes writes ?variant= to the monogram-priced id. A refresh
      // would otherwise keep that price on the standard PDP. Reset to No on load;
      // opening customize re-applies Yes from styleOptions when needed.
      this.ensureStandardPdpMonogramPrice();
      this.bindMonogramPriceUnloadGuard();
    }
  }

  /**
   * Standard size + ATC must use Monogram No pricing. Monogram Yes is only for
   * the customize flow. Clears a stale Yes variant left in the URL/form after refresh.
   */
  ensureStandardPdpMonogramPrice() {
    if (this.isCustomizationShown) return;

    let attempts = 0;
    const maxAttempts = 20;
    const tryReset = () => {
      if (this.isCustomizationShown) return;
      attempts += 1;
      const product = this.getProductData();
      if (product?.variants?.length || attempts >= maxAttempts) {
        this.resetMonogramOnProductForm();
        return;
      }
      setTimeout(tryReset, 50);
    };
    setTimeout(tryReset, 0);
  }

  /**
   * Before refresh/navigate away, strip Monogram Yes from the shared URL so the
   * next Liquid paint is the base (No) price — not the monogram surcharge.
   */
  bindMonogramPriceUnloadGuard() {
    if (this._monogramUnloadBound) return;
    this._monogramUnloadBound = true;

    const clearMonogramFromUrl = () => {
      // Keep Yes on the form only for an in-flight customize ATC; URL must not
      // persist Yes across a full page load of the standard PDP.
      if (this.isCustomizationShown) {
        // Still clear URL so refresh lands on No; form id is irrelevant after unload.
        try {
          const noId = this.resolveMonogramVariantId(false);
          if (!noId) return;
          const newUrl = new URL(window.location.href);
          if (newUrl.searchParams.get('variant') === String(noId)) return;
          newUrl.searchParams.set('variant', String(noId));
          window.history.replaceState({ path: newUrl.href }, '', newUrl.href);
        } catch (e) {
          // ignore
        }
        return;
      }
      this.resetMonogramOnProductForm();
    };

    window.addEventListener('pagehide', clearMonogramFromUrl);
    window.addEventListener('beforeunload', clearMonogramFromUrl);
  }

  async toggleCustomizationTable() {
    if (this.isCustomizationShown) {
      this.restoreOriginalContent();
    } else {
      this.showCustomizationTable();
    }
  }

  async showCustomizationTable() {
    if (!this.productDetailsContainer) return;

    try {
      const originalContent = this.productDetailsContainer.querySelector('.original-product-content');
      if (originalContent && originalContent instanceof HTMLElement) {
        originalContent.style.display = 'none';
      }

      const authContainer = this.productDetailsContainer.querySelector('.auth-container');
      if (authContainer && authContainer instanceof HTMLElement) {
        authContainer.style.display = 'none';
      }

      let customizationContainer = this.productDetailsContainer.querySelector('.customization-container');

      if (!customizationContainer) {
        const productTitle = this.getProductTitle();
        const productPrice = this.getProductPrice();
        this.shirtStyle = this.getShirtStyle();
        this.productOrganizationType = this.getProductOrganizationType();
        this.styleOptions = this.populateStyleOptions() || [];

        this.initializeBasePrice();

        // Restore browser session first so last-order choices are not wiped by metafields
        this.populateCachedSelectionsFromLocalStorage();

        const customer = await this.fetchCustomerData();
        if (customer && !this.customerMetafieldsLoaded) {
          await this.loadCustomerMetafields();
        }

        this.activeTab = 0;
        const tabContent = this.createTabContent();

        const customizationHTML = `
        <div class="tool-container customization-container">
          <div class="tab-container tab-one">
            <div class="tab button--action active" data-tab="0">
              <h2 class="pl-5">style</h2>
            </div>
            <div class="tab button--action" data-tab="1">
              <h2 class="pl-5">fit</h2>
            </div>
            <div class="tab button--action disabled" data-tab="2">
              <h2 class="pl-5">summary</h2>
            </div>
          </div>
          <div class="content-container-outer">
            <div class="content-container">
              ${tabContent}
            </div>
          </div>
          <div class="footer-container">
            <div class="columns pb-4 footer-columns">
              <div class="column is-half">
                <h3 class="product-eyebrow">${this.productOrganizationType}</h3>
                <h3>${productTitle}</h3>
              </div>
              <h3 class="column is-half has-text-right price-label">
                ${productPrice}
              </h3>
            </div>
          </div>
        </div>
        `;

        this.productDetailsContainer.insertAdjacentHTML('beforeend', customizationHTML);
        customizationContainer = this.productDetailsContainer.querySelector('.customization-container');

        this.isCustomizationShown = true;
        setTimeout(() => this.syncMonogramVariantFromSelection(), 200);
      } else {
        if (customizationContainer instanceof HTMLElement) {
          customizationContainer.style.display = 'block';
        }

        // Restore browser session before metafields so last-order choices persist after refresh
        this.populateCachedSelectionsFromLocalStorage();

        const customer = await this.fetchCustomerData();
        if (customer && !this.customerMetafieldsLoaded && this.styleOptions) {
          await this.loadCustomerMetafields();
        }

        // Reset to Style tab
        this.activeTab = 0;
        const tabContainer = customizationContainer ? customizationContainer.querySelector('.tab-container') : null;
        if (tabContainer && tabContainer instanceof HTMLElement) {
          tabContainer.style.display = '';
          tabContainer.className = 'tab-container tab-one';
          tabContainer.querySelectorAll('.tab.button--action').forEach((tab, idx) => {
            if (tab instanceof HTMLElement) {
              tab.classList.remove('disabled', 'active');
              if (idx === 0) tab.classList.add('active');
              // Summary stays locked until fit path is completed this session
              if (idx === 2) tab.classList.add('disabled');
            }
          });
        }

        const tabContent = this.createTabContent();
        const contentContainer = customizationContainer ? customizationContainer.querySelector('.content-container') : null;
        if (contentContainer && contentContainer instanceof HTMLElement) {
          contentContainer.innerHTML = tabContent;
        }

        this.isCustomizationShown = true;
        this.syncMonogramVariantFromSelection();
      }

      this.isCustomizationShown = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });

      this.updateAddToCartButtonVisibility();
      this.setupCustomizationListeners();
      this.updateSummaryTabState();
    } catch (error) {
      console.error('Error showing customization table:', error);
    }
  }



  /**
   * Show authentication view instead of customization table
   * @param {string} viewType - 'login', 'signup', or 'forgot-password'
   */
  showAuthView(viewType = 'login') {
    if (!this.productDetailsContainer) return;

    try {
      this.authView = viewType;

      // Get or ensure customization container exists
      let customizationContainer = this.productDetailsContainer.querySelector('.customization-container');

      if (!customizationContainer) {
        // If customization container doesn't exist, create it first
        this.showCustomizationTable();
        customizationContainer = this.productDetailsContainer.querySelector('.customization-container');
      }

      // Don't hide customization container - just update its content
      const contentContainer = customizationContainer?.querySelector('.content-container');
      if (!contentContainer || !(contentContainer instanceof HTMLElement)) return;

      // Create auth HTML
      const authHTML = this.createAuthView(viewType);

      // Create tab wrapper to match production markup
      const tabWrapper = `
        <div class="tab">
          <div class="tab-inner-content-container">
            ${authHTML}
          </div>
          <div class="footer-nav inset-mto-contents">
            <div class="prev-next-nav-container">
              <h3 class="button--action button--minimal mb-0 auth-back-btn">
                previous
              </h3>
            </div>
          </div>
        </div>
      `;

      // Replace content-container innerHTML
      contentContainer.innerHTML = tabWrapper;

      // Update tab state to show we're in a special state
      const tabs = customizationContainer?.querySelectorAll('.tab.button--action');
      tabs?.forEach(tab => {
        if (tab instanceof HTMLElement) {
          tab.classList.remove('active');
        }
      });

      // Setup auth listeners
      this.setupAuthListeners();

    } catch (error) {
      console.error('Error showing auth view:', error);
    }
  }

  /**
   * Create HTML for authentication views
   * @param {string} viewType - 'login', 'signup', or 'forgot-password'
   * @returns {string}
   */
  createAuthView(viewType) {
    if (viewType === 'login') {
      return this.createLoginForm();
    } else if (viewType === 'signup') {
      return this.createSignupForm();
    } else if (viewType === 'forgot-password') {
      return this.createForgotPasswordForm();
    }
    return '';
  }

  /**
   * Create login form HTML
   * @returns {string}
   */
  createLoginForm() {
    return `
      <div class="inset-mto-contents">
        <h2 class="mb-5 big-mobile">Sign-in</h2>
        <p class="description-label">
          If you have a fit profile, log in below.
        </p>
          <div class="auth-spinner ${this.authLoading ? '' : 'is-hidden'}">
            <div class="spinner">
              <svg viewBox="0 0 48 48">
                <svg width="48" height="24">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-8 h-8 mr-3 border-0">
                    <style>
                      @keyframes spin{
                        0%{transform: rotate(0deg); }
                        100%{transform: rotate(10000deg);}
                      }
                      #spins{
                      animation: spin 35s;
                      transform-origin: 50%;
                      }
                    </style>
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="#ccc" stroke-width="4"></circle>
                    <path class="opacity-75 animate-spin" fill="#ddd" id="spins" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-50 -200 300 300" data-v-61d08a43="">
                  <path d="M204.20336,41.17205c-.27477-1.77878-2.19818-1.95232-3.13817-1.85109-2.89233.33261-5.90035,3.572-7.80928,4.00587.2603-1.44616,1.18587-3.78895.2603-5.06157s-4.61327-.46277-8.12744-.10123-2.61755.53508-5.52434.17354c-2.27048-.27477-6.43543,3.52864-7.23082,8.57575-3.65879.53508-8.677,4.917-9.3856,3.7311-.91108-1.44617,9.35668-11.41023,14.46164-17.354,1.64862-1.9234,5.04711-5.78466,5.04711-5.78466s12.14778,2.02463,14.75088,2.05356c1.20031,0,2.89232-.14462,3.12372-1.07016.31815-1.56186-4.729-1.99571-7.05729-2.27048s-9.15422-1.07016-9.15422-1.07016,2.03909-2.68987,3.78894-4.96034c1.98124-2.55972,4.12158-5.264,3.22494-5.857-2.21263-1.44616-4.33849,2.34279-6.31974,4.888-1.44616,1.88-4.33849,5.37972-4.33849,5.37972l-9.3133-.66523s11.13548-13.66625,7.86714-15.51733c-4.03479-2.24156-14.37487,11.90192-16.24041,14.46163-2.53079-.20246-7.53451-2.08247-7.925.70862-.28923,2.11141,4.94587,1.57632,6.58005,1.77879-3.008,4.49757-17.71551,22.70477-19.63891,23.57246s-14.46164,7.925-15.69088,5.78466,4.85911-7.23082,7.85267-10.61484c1.44616-1.67755,9.51576-10.34008,7.59236-11.36685-1.57631-.83878-5.78465,4.71449-7.85267,7.31759-7.04282,8.47453-18.72783,12.22009-19.7546,10.87516s1.96678-5.076,3.572-7.11513c2.47294-3.15264,5.52435-5.98712,7.83821-9.35668.91108-1.34493,2.37171-3.62987.91108-4.61326s-9.47238,5.13388-15.58965,9.41452c1.3594-2.53078,4.49757-8.25759,3.03694-9.35668s-8.24313,3.93357-11.56931,6.31974-6.99943,5.00373-10.745,7.34651c2.16925-3.62987,8.489-9.96407,7.0862-11.13546-3.196-2.632-36.51564,21.35984-39.04643,18.80013-.73754-.73754,8.46006-8.95176,10.90408-11.39577S81.27942,29.93535,80.86,28.67719c-.54955-1.6197-2.632,1.34494-3.28279,1.01232-2.37171-7.99729-13.46379,1.21478-16.83335,3.08033-2.48741-3.36957-7.07174-3.21049-11.19331-2.9791C53.6577,23.29746,58.1408,15.12664,62.8553,9.41429c1.5474-1.86555,4.55542-5.669,5.30743-5.06158s-5.14835,6.50774-4.5265,7.12959c1.44616,1.44617,10.12314-9.7616,8.3299-11.17884s-6.58,2.4874-8.34436,4.33849c-6.58,6.73912-12.249,18.49643-17.44074,25.52479a143.77851,143.77851,0,0,0-15.18472,1.25817c.188-2.31387,6.26189-9.54469,7.85267-11.121,1.22924-1.22924,3.86125-3.18156,5.00372-4.56987,2.12586-2.55971,4.33849-6.18959,3.58649-7.33206s-5.61112,2.89233-7.33205,5.06158c-1.88,2.444-3.948,5.04711-5.56773,7.33205A227.04844,227.04844,0,0,1,12.28294,47.86779C8.42169,51.81582-1.25315,59.654.13517,61.5485S5.00874,60.218,6.46936,58.7574c1.79325-1.79324,12.52378-13.83978,16.18258-16.70319C22.49286,52.43767,21.20577,72.42366,26.2095,72.655,29.796,72.81412,37.692,52.40875,42.63792,42.56037c1.44617-2.89233,3.67326-8.74929,5.56773-9.86284s8.37329-.76647,9.09638.50616c1.56186,2.6754-1.30155,4.20834-6.8259,9.60253-3.12371,3.0514-7.31759,7.05728-6.31973,9.35668s8.90837-1.85109,12.89978-3.02248c.92554.72308-3.91911,4.888-2.256,5.97266,3.196,2.05355,17.354-6.88374,21.1863-9.26991,3.13817-1.96679,18.0192-10.81731,18.49644-10.369S85.90714,46.465,80.5708,51.382c-1.56186,1.44616-4.80126,4.4108-3.52864,5.56773s6.16066-3.18157,8.59021-5.06158c8.22869-6.31974,24.2811-19.046,24.93187-18.40967s-3.52864,5.92927-8.301,11.16438C97.67893,49.64657,92.357,54.202,94.16475,55.142s10.88961-7.93944,12.82748-9.64591c6.00158-5.293,12.5527-9.48684,13.01547-9.00961s-5.04711,5.92928-6.47882,7.79483-3.008,4.33849-2.89232,6.62343c.30369,5.32188,8.83606,2.4874,12.63947,1.01231-3.28279,3.49972-1.85109,6.91267,4.33849,6.07389,4.02033-.564,10.68715-5.36526,11.07761-4.917s-.98339,2.01017,0,2.89233c3.26833,2.77663,20.89706-8.19974,21.59123-7.53451s-2.35725,2.32832-1.85109,4.23726c1.128,4.33849,11.14992-3.25387,14.1001-3.22494,2.09693,4.7868,6.62343,3.49971,9.45791,0,1.83662-2.24156,3.008-6.07389,2.32833-8.38775-.3037-1.07017,5.56774-1.70648,6.50773-.47724a3.81787,3.81787,0,0,1-.72308,4.136c-1.25815,1.59078-6.7102,5.16281-5.13389,6.97051s5.17727-2.89232,6.7102-4.1071c2.53079-2.068,7.80928-5.14835,8.53236-4.74342,2.1837,1.25816-8.24313,12.26347-5.46649,13.4204,2.32832.96893,4.7-4.136,7.02835-8.677C203.42244,44.45484,204.43475,42.60375,204.20336,41.17205ZM26.947,67.34762c-3.10925,0-4.49756-24.91741-.752-30.10914,4.12157-3.55756,12.08993-4.58434,18.71336-4.80126C39.36959,44.51269,29.232,67.37654,26.947,67.34762ZM48.71181,49.16934c-.83877-.92555,3.73111-5.00373,6.00159-7.23082,1.73539-1.7354,4.00587-3.83234,6.2185-5.669a59.66835,59.66835,0,0,1,9.28438-6.392c1.96678-.98339,4.04925-1.51847,4.80126-.40493s-6.29081,8.83606-10.12315,12.14778C60.6282,45.23577,49.53613,50.0515,48.71181,49.16934Zm129.2726-38.82951c1.76432.88216-8.09851,13.27579-8.09851,13.27579l-4.10712-.47724S175.81517,9.25521,177.98441,10.33983ZM159.18428,32.43722c1.83662-2.2994,3.45633-4.62773,5.20619-6.88374-.08677,0,3.8034.23138,3.8034.23138S154.383,39.81265,153.819,39.34988s3.601-4.64218,5.42313-6.91266ZM143.0017,53.00167c-.88216-.99785,4.06372-5.06157,6.37758-7.15851,6.78251-6.1462,14.17241-13.24686,20.70907-19.97153,0,0,6.5222.70862,8.93729.94a109.48951,109.48951,0,0,1-12.71177,13.85425C156.263,49.285,143.59463,53.65244,143.0017,53.00167ZM176.58163,49.473c-2.41509-1.01231.83878-9.34221,3.96249-7.925C182.80014,42.57483,179.56074,50.93366,176.58163,49.473ZM147.962,24.23747a1.83675,1.83675,0,0,0,.80985.188,2.05356,2.05356,0,0,0,1.80771-1.27262,2.51633,2.51633,0,0,0,.26031-1.67755,1.721,1.721,0,0,0-.91108-1.15694,2.01019,2.01019,0,0,0-2.62749,1.08532q-.0177.04265-.03345.08608a1.93786,1.93786,0,0,0,.57861,2.67877Q147.90305,24.205,147.962,24.23747Z" style="fill: gray;" data-v-61d08a43=""></path>
                </svg>
              </svg>
            </div>
          </div>
          <div class="${this.authLoading ? 'is-hidden' : ''} auth-form-container">
            <form class="form" id="customization-login-form" novalidate>
              <div class="form-inputs-container">
                <div class="input-container">
                  <label for="auth-email"><p class="mb-3 has-text-weight-medium">Email Address*</p></label>
                  <input
                    type="email"
                    id="auth-email"
                    name="email"
                    class="input mb-2"
                    placeholder="Enter email address"
                    required
                  />
                  <div class="auth-error-container">
                    <p class="help is-danger mb-5 auth-error" id="login-email-error" aria-live="polite"></p>
                  </div>
                </div>
                <div class="input-container">
                  <label for="auth-password"><p class="mb-3 has-text-weight-medium">Password*</p></label>
                  <input
                    type="password"
                    id="auth-password"
                    name="password"
                    class="input mb-2"
                    placeholder="Enter password"
                    required
                  />
                  <div class="auth-error-container">
                    <p class="help is-danger mb-5 auth-error" id="login-password-error" aria-live="polite"></p>
                  </div>
                </div>
              </div>
              <div class="button-bar">
                <button class="form-submit-btn p-0" type="submit">
                  <h3 class="button--action mb-0">Sign-in</h3>
                </button>
                <div class="option-container">
                  <h3 class="button--action mb-0 forgot-pass-label auth-link" data-auth-view="forgot-password">
                    I forgot my password
                  </h3>
                  <h3 class="btn-bar mb-0">|</h3>
                  <h3 class="button--action mb-0 forgot-pass-label auth-link" data-auth-view="signup">
                    Sign-up
                  </h3>
                </div>
              </div>
            </form>
          </div>

      </div>
    `;
  }

  /**
   * Create signup form HTML
   * @returns {string}
   */
  createSignupForm() {
    return `
      <div class="inset-mto-contents mb-mobile">
        <h2 class="mb-5 big-mobile">Sign-up</h2>
        <p class="description-label">
          If you don't have a fit profile, sign-up below.
        </p>
          <form class="form auth-form-container" id="customization-signup-form" novalidate>
              <div class="form-inputs-container">
                <div class="input-container">
                  <label for="signup-firstname"><p class="mb-3 has-text-weight-medium">First Name*</p></label>
                  <input
                    type="text"
                    id="signup-firstname"
                    name="firstName"
                    class="input mb-2"
                    placeholder="Enter first name"
                    required
                  />
                  <div class="auth-error-container">
                    <p class="help is-danger mb-5 auth-error" id="signup-firstname-error" aria-live="polite"></p>
                  </div>
                </div>
                <div class="input-container">
                  <label for="signup-lastname"><p class="mb-3 has-text-weight-medium">Last Name*</p></label>
                  <input
                    type="text"
                    id="signup-lastname"
                    name="lastName"
                    class="input mb-2"
                    placeholder="Enter last name"
                    required
                  />
                  <div class="auth-error-container">
                    <p class="help is-danger mb-5 auth-error" id="signup-lastname-error" aria-live="polite"></p>
                  </div>
                </div>
                <div class="input-container">
                  <label for="signup-email"><p class="mb-3 has-text-weight-medium">Email Address*</p></label>
                  <input
                    type="email"
                    id="signup-email"
                    name="email"
                    class="input mb-2"
                    placeholder="Enter email address"
                    required
                  />
                  <div class="auth-error-container">
                    <p class="help is-danger mb-5 auth-error" id="signup-email-error" aria-live="polite"></p>
                  </div>
                </div>
                <div class="input-container">
                  <label for="signup-password"><p class="mb-3 has-text-weight-medium">Password*</p></label>
                  <input
                    type="password"
                    id="signup-password"
                    name="password"
                    class="input mb-2"
                    placeholder="Enter password"
                    required
                    minlength="6"
                    maxlength="40"
                  />
                  <div class="auth-error-container">
                    <p class="help is-danger mb-5 auth-error" id="signup-password-error" aria-live="polite"></p>
                  </div>
                </div>
              </div>
              <div class="button-bar">
                <button class="form-submit-btn p-0" type="submit">
                  <h3 class="button--action mb-0">Sign-up</h3>
                </button>
                <div class="option-container">
                  <h3 class="button--action mb-0 forgot-pass-label auth-link" data-auth-view="forgot-password">
                    I forgot my password
                  </h3>
                  <h3 class="btn-bar mb-0">|</h3>
                  <h3 class="button--action mb-0 forgot-pass-label auth-link" data-auth-view="login">
                    Sign-in
                  </h3>
                </div>
              </div>
            </form>
      </div>
    `;
  }


  /**
   * Create forgot password form HTML
   * @returns {string}
   */
  createForgotPasswordForm() {
    return `
      <div class="inset-mto-contents">
        <h2 class="big-mobile mb-5">Password Reset</h2>
        <p class="description-label">
          Instructions to reset your password will be emailed to you. If you require further assistance, 
          please feel free to email us at info@hamiltonshirts.com or call 713.264.8800.
        </p>
          <div class="auth-spinner ${this.authLoading ? '' : 'is-hidden'}">
            <div class="spinner">
              <svg viewBox="0 0 48 48">
                <svg width="48" height="24">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-8 h-8 mr-3 border-0">
                    <style>
                      @keyframes spin{
                        0%{transform: rotate(0deg); }
                        100%{transform: rotate(10000deg);}
                      }
                      #spins{
                      animation: spin 35s;
                      transform-origin: 50%;
                      }
                    </style>
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="#ccc" stroke-width="4"></circle>
                    <path class="opacity-75 animate-spin" fill="#ddd" id="spins" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-50 -200 300 300" data-v-61d08a43="">
                  <path d="M204.20336,41.17205c-.27477-1.77878-2.19818-1.95232-3.13817-1.85109-2.89233.33261-5.90035,3.572-7.80928,4.00587.2603-1.44616,1.18587-3.78895.2603-5.06157s-4.61327-.46277-8.12744-.10123-2.61755.53508-5.52434.17354c-2.27048-.27477-6.43543,3.52864-7.23082,8.57575-3.65879.53508-8.677,4.917-9.3856,3.7311-.91108-1.44617,9.35668-11.41023,14.46164-17.354,1.64862-1.9234,5.04711-5.78466,5.04711-5.78466s12.14778,2.02463,14.75088,2.05356c1.20031,0,2.89232-.14462,3.12372-1.07016.31815-1.56186-4.729-1.99571-7.05729-2.27048s-9.15422-1.07016-9.15422-1.07016,2.03909-2.68987,3.78894-4.96034c1.98124-2.55972,4.12158-5.264,3.22494-5.857-2.21263-1.44616-4.33849,2.34279-6.31974,4.888-1.44616,1.88-4.33849,5.37972-4.33849,5.37972l-9.3133-.66523s11.13548-13.66625,7.86714-15.51733c-4.03479-2.24156-14.37487,11.90192-16.24041,14.46163-2.53079-.20246-7.53451-2.08247-7.925.70862-.28923,2.11141,4.94587,1.57632,6.58005,1.77879-3.008,4.49757-17.71551,22.70477-19.63891,23.57246s-14.46164,7.925-15.69088,5.78466,4.85911-7.23082,7.85267-10.61484c1.44616-1.67755,9.51576-10.34008,7.59236-11.36685-1.57631-.83878-5.78465,4.71449-7.85267,7.31759-7.04282,8.47453-18.72783,12.22009-19.7546,10.87516s1.96678-5.076,3.572-7.11513c2.47294-3.15264,5.52435-5.98712,7.83821-9.35668.91108-1.34493,2.37171-3.62987.91108-4.61326s-9.47238,5.13388-15.58965,9.41452c1.3594-2.53078,4.49757-8.25759,3.03694-9.35668s-8.24313,3.93357-11.56931,6.31974-6.99943,5.00373-10.745,7.34651c2.16925-3.62987,8.489-9.96407,7.0862-11.13546-3.196-2.632-36.51564,21.35984-39.04643,18.80013-.73754-.73754,8.46006-8.95176,10.90408-11.39577S81.27942,29.93535,80.86,28.67719c-.54955-1.6197-2.632,1.34494-3.28279,1.01232-2.37171-7.99729-13.46379,1.21478-16.83335,3.08033-2.48741-3.36957-7.07174-3.21049-11.19331-2.9791C53.6577,23.29746,58.1408,15.12664,62.8553,9.41429c1.5474-1.86555,4.55542-5.669,5.30743-5.06158s-5.14835,6.50774-4.5265,7.12959c1.44616,1.44617,10.12314-9.7616,8.3299-11.17884s-6.58,2.4874-8.34436,4.33849c-6.58,6.73912-12.249,18.49643-17.44074,25.52479a143.77851,143.77851,0,0,0-15.18472,1.25817c.188-2.31387,6.26189-9.54469,7.85267-11.121,1.22924-1.22924,3.86125-3.18156,5.00372-4.56987,2.12586-2.55971,4.33849-6.18959,3.58649-7.33206s-5.61112,2.89233-7.33205,5.06158c-1.88,2.444-3.948,5.04711-5.56773,7.33205A227.04844,227.04844,0,0,1,12.28294,47.86779C8.42169,51.81582-1.25315,59.654.13517,61.5485S5.00874,60.218,6.46936,58.7574c1.79325-1.79324,12.52378-13.83978,16.18258-16.70319C22.49286,52.43767,21.20577,72.42366,26.2095,72.655,29.796,72.81412,37.692,52.40875,42.63792,42.56037c1.44617-2.89233,3.67326-8.74929,5.56773-9.86284s8.37329-.76647,9.09638.50616c1.56186,2.6754-1.30155,4.20834-6.8259,9.60253-3.12371,3.0514-7.31759,7.05728-6.31973,9.35668s8.90837-1.85109,12.89978-3.02248c.92554.72308-3.91911,4.888-2.256,5.97266,3.196,2.05355,17.354-6.88374,21.1863-9.26991,3.13817-1.96679,18.0192-10.81731,18.49644-10.369S85.90714,46.465,80.5708,51.382c-1.56186,1.44616-4.80126,4.4108-3.52864,5.56773s6.16066-3.18157,8.59021-5.06158c8.22869-6.31974,24.2811-19.046,24.93187-18.40967s-3.52864,5.92927-8.301,11.16438C97.67893,49.64657,92.357,54.202,94.16475,55.142s10.88961-7.93944,12.82748-9.64591c6.00158-5.293,12.5527-9.48684,13.01547-9.00961s-5.04711,5.92928-6.47882,7.79483-3.008,4.33849-2.89232,6.62343c.30369,5.32188,8.83606,2.4874,12.63947,1.01231-3.28279,3.49972-1.85109,6.91267,4.33849,6.07389,4.02033-.564,10.68715-5.36526,11.07761-4.917s-.98339,2.01017,0,2.89233c3.26833,2.77663,20.89706-8.19974,21.59123-7.53451s-2.35725,2.32832-1.85109,4.23726c1.128,4.33849,11.14992-3.25387,14.1001-3.22494,2.09693,4.7868,6.62343,3.49971,9.45791,0,1.83662-2.24156,3.008-6.07389,2.32833-8.38775-.3037-1.07017,5.56774-1.70648,6.50773-.47724a3.81787,3.81787,0,0,1-.72308,4.136c-1.25815,1.59078-6.7102,5.16281-5.13389,6.97051s5.17727-2.89232,6.7102-4.1071c2.53079-2.068,7.80928-5.14835,8.53236-4.74342,2.1837,1.25816-8.24313,12.26347-5.46649,13.4204,2.32832.96893,4.7-4.136,7.02835-8.677C203.42244,44.45484,204.43475,42.60375,204.20336,41.17205ZM26.947,67.34762c-3.10925,0-4.49756-24.91741-.752-30.10914,4.12157-3.55756,12.08993-4.58434,18.71336-4.80126C39.36959,44.51269,29.232,67.37654,26.947,67.34762ZM48.71181,49.16934c-.83877-.92555,3.73111-5.00373,6.00159-7.23082,1.73539-1.7354,4.00587-3.83234,6.2185-5.669a59.66835,59.66835,0,0,1,9.28438-6.392c1.96678-.98339,4.04925-1.51847,4.80126-.40493s-6.29081,8.83606-10.12315,12.14778C60.6282,45.23577,49.53613,50.0515,48.71181,49.16934Zm129.2726-38.82951c1.76432.88216-8.09851,13.27579-8.09851,13.27579l-4.10712-.47724S175.81517,9.25521,177.98441,10.33983ZM159.18428,32.43722c1.83662-2.2994,3.45633-4.62773,5.20619-6.88374-.08677,0,3.8034.23138,3.8034.23138S154.383,39.81265,153.819,39.34988s3.601-4.64218,5.42313-6.91266ZM143.0017,53.00167c-.88216-.99785,4.06372-5.06157,6.37758-7.15851,6.78251-6.1462,14.17241-13.24686,20.70907-19.97153,0,0,6.5222.70862,8.93729.94a109.48951,109.48951,0,0,1-12.71177,13.85425C156.263,49.285,143.59463,53.65244,143.0017,53.00167ZM176.58163,49.473c-2.41509-1.01231.83878-9.34221,3.96249-7.925C182.80014,42.57483,179.56074,50.93366,176.58163,49.473ZM147.962,24.23747a1.83675,1.83675,0,0,0,.80985.188,2.05356,2.05356,0,0,0,1.80771-1.27262,2.51633,2.51633,0,0,0,.26031-1.67755,1.721,1.721,0,0,0-.91108-1.15694,2.01019,2.01019,0,0,0-2.62749,1.08532q-.0177.04265-.03345.08608a1.93786,1.93786,0,0,0,.57861,2.67877Q147.90305,24.205,147.962,24.23747Z" style="fill: gray;" data-v-61d08a43=""></path>
                </svg>
              </svg>
            </div>
          </div>
          <div class="${this.authLoading ? 'is-hidden' : ''}">
            <div id="forgot-password-success" style="display: none;">
              <p class="mb-4">Successfully sent email.</p>
              <div class="button-container mb-5">
                <h3 class="mb-0 button--action auth-link" data-auth-view="login">
                  Sign-in
                </h3>
              </div>
            </div>
            <form class="form auth-form-container" id="customization-forgot-password-form" novalidate style="display: block;">
              <div class="mb-4">
                <label for="forgot-email"><p class="mb-3 has-text-weight-medium">Email Address*</p></label>
                <input
                  type="email"
                  id="forgot-email"
                  name="email"
                  class="input mb-2"
                  placeholder="Enter email address"
                  required
                />
                <div class="auth-error-container">
                  <p class="help is-danger mb-mobile auth-error" id="forgot-email-error" aria-live="polite"></p>
                </div>
              </div>
              <div class="button-container mb-5">
                <button class="form-submit-btn p-0" type="submit">
                  <h3 class="mb-0 button--action">Submit</h3>
                </button>
                <div class="option-container">
                  <h3 class="button--action mb-0 auth-link" data-auth-view="signup">
                    Sign-up
                  </h3>
                </div>
              </div>
            </form>
          </div>

      </div>
    `;
  }

  /**
   * Setup event listeners for authentication forms
   */
  setupAuthListeners() {
    if (!this.productDetailsContainer) return;

    // Exit button
    const exitBtn = this.productDetailsContainer.querySelector('.close-mto-button');
    if (exitBtn && exitBtn instanceof HTMLElement) {
      exitBtn.addEventListener('click', () => this.restoreOriginalContent());
    }

    // Back button from auth view
    const authBackBtn = this.productDetailsContainer.querySelector('.auth-back-btn');
    if (authBackBtn && authBackBtn instanceof HTMLElement) {
      authBackBtn.addEventListener('click', () => {
        // Clear the navigation intent and go back
        this.tempNavigationIntent = null;

        // Determine which tab to go back to
        if (this.activeTab === 1) {
          // If we were on fit tab, recreate fit tab content
          this.switchTab(1);
        } else if (this.activeTab === 2) {
          // If we were trying to access summary, go to fit tab
          this.switchTab(1);
        } else {
          // Default to style tab
          this.switchTab(0);
        }
      });
    }

    // Auth view navigation links
    const authLinks = this.productDetailsContainer.querySelectorAll('.auth-link[data-auth-view]');
    authLinks.forEach(link => {
      if (link instanceof HTMLElement) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const viewType = link.dataset.authView;
          if (viewType) {
            this.showAuthView(viewType);
          }
        });
      }
    });

    // Login form
    const loginForm = this.productDetailsContainer.querySelector('#customization-login-form');
    if (loginForm && loginForm instanceof HTMLFormElement) {
      loginForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleLoginSubmit(loginForm);
      });
    }

    // Signup form
    const signupForm = this.productDetailsContainer.querySelector('#customization-signup-form');
    if (signupForm && signupForm instanceof HTMLFormElement) {
      signupForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSignupSubmit(signupForm);
      });
    }

    // Forgot password form
    const forgotForm = this.productDetailsContainer.querySelector('#customization-forgot-password-form');
    if (forgotForm && forgotForm instanceof HTMLFormElement) {
      forgotForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleForgotPasswordSubmit(forgotForm);
      });
    }

    // Match login.liquid behavior: validate on blur + clear on input
    this.setupAuthInputValidation();
  }

  /**
   * Handle login form submission
   * @param {HTMLFormElement} form
   */
  async handleLoginSubmit(form) {
    const emailInput = form.querySelector('#auth-email');
    const passwordInput = form.querySelector('#auth-password');
    const email = (emailInput instanceof HTMLInputElement ? emailInput.value : '')?.trim() || '';
    const password = (passwordInput instanceof HTMLInputElement ? passwordInput.value : '') || '';

    this.clearAuthErrors();

    let hasError = false;

    if (!email) {
      this.showAuthError('login-email-error', 'This field is required', emailInput instanceof HTMLInputElement ? emailInput : null);
      hasError = true;
    } else if (!this.isValidEmail(email)) {
      this.showAuthError('login-email-error', 'Email Address is not valid', emailInput instanceof HTMLInputElement ? emailInput : null);
      hasError = true;
    }

    if (!password) {
      this.showAuthError('login-password-error', 'This field is required', passwordInput instanceof HTMLInputElement ? passwordInput : null);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    this.authLoading = true;
    this.updateAuthLoadingState();

    try {
      let shopifyAuth = window.shopifyAuthInstance;
      if (!shopifyAuth && window.ShopifyAuth && window.shopifyAPI) {
        shopifyAuth = new window.ShopifyAuth(window.shopifyAPI);
        window.shopifyAuthInstance = shopifyAuth;
      }

      if (!shopifyAuth) {
        throw new Error('Authentication service not available. Please refresh the page.');
      }

      const result = await shopifyAuth.login(email, password);

      if (result.success) {
        // Clear cache and refresh customer data
        this.customerData = null;
        this.customerMetafieldsLoaded = false;
        await this.fetchCustomerData();
        await this.loadCustomerMetafields();

        if (window.updateHeaderAuthState) {
          window.updateHeaderAuthState();
        }

        const hasFitProfile = this.hasSavedFitData();

        // Route based on navigation intent
        if (this.tempNavigationIntent === 'summary') {
          // User was trying to access summary tab
          this.switchTab(2);
          this.tempNavigationIntent = null;
        } else if (this.tempNavigationIntent === 'enter-measurements' || !hasFitProfile) {
          // New user or user trying to enter measurements - show measurements form directly
          this.switchTab(1);
          this.tempNavigationIntent = null;

          // Show the fit-content-view (measurements form) and hide fit-options-view
          setTimeout(() => {
            const contentContainer = this.productDetailsContainer?.querySelector('.content-container');
            if (contentContainer && contentContainer instanceof HTMLElement) {
              const tabInnerContainer = contentContainer.querySelector('.tab-inner-content-container');
              if (tabInnerContainer) {
                const fitOptionsView = tabInnerContainer.querySelector('.fit-options-view');
                const fitContentView = tabInnerContainer.querySelector('.fit-content-view');
                const fitPrevBtn = contentContainer.querySelector('.fit-prev-btn');
                const nextContainer = contentContainer.querySelector('.next-container');

                if (fitOptionsView && fitContentView) {
                  if (fitOptionsView instanceof HTMLElement) {
                    fitOptionsView.style.display = 'none';
                  }
                  if (fitContentView instanceof HTMLElement) {
                    fitContentView.style.display = 'block';
                  }

                  // Show previous button and next button
                  if (fitPrevBtn && fitPrevBtn instanceof HTMLElement) {
                    fitPrevBtn.style.display = 'block';
                  }
                  if (nextContainer && nextContainer instanceof HTMLElement) {
                    nextContainer.style.display = 'block';
                  }

                  // Hide the regular previous button
                  const prevBtn = contentContainer.querySelector('[data-prev-tab="0"]');
                  if (prevBtn && prevBtn instanceof HTMLElement) {
                    prevBtn.style.display = 'none';
                  }
                }
              }
            }
          }, 100);
        } else {
          // User has fit profile - Welcome Back is shown by switchTab(1)
          this.switchTab(1);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'Login failed. Please check your credentials.';
      this.showAuthError('login-password-error', errorMessage);
    } finally {
      this.authLoading = false;
      this.updateAuthLoadingState();
    }
  }

  /**
   * Handle signup form submission
   * @param {HTMLFormElement} form
   */
  async handleSignupSubmit(form) {
    const firstNameInput = form.querySelector('#signup-firstname');
    const lastNameInput = form.querySelector('#signup-lastname');
    const emailInput = form.querySelector('#signup-email');
    const passwordInput = form.querySelector('#signup-password');
    const firstName = (firstNameInput instanceof HTMLInputElement ? firstNameInput.value : '')?.trim() || '';
    const lastName = (lastNameInput instanceof HTMLInputElement ? lastNameInput.value : '')?.trim() || '';
    const email = (emailInput instanceof HTMLInputElement ? emailInput.value : '')?.trim() || '';
    const password = (passwordInput instanceof HTMLInputElement ? passwordInput.value : '') || '';

    this.clearAuthErrors();

    let hasError = false;

    if (!firstName) {
      this.showAuthError('signup-firstname-error', 'This field is required', firstNameInput instanceof HTMLInputElement ? firstNameInput : null);
      hasError = true;
    }

    if (!lastName) {
      this.showAuthError('signup-lastname-error', 'This field is required', lastNameInput instanceof HTMLInputElement ? lastNameInput : null);
      hasError = true;
    }

    if (!email) {
      this.showAuthError('signup-email-error', 'This field is required', emailInput instanceof HTMLInputElement ? emailInput : null);
      hasError = true;
    } else if (!this.isValidEmail(email)) {
      this.showAuthError('signup-email-error', 'Email Address is not valid', emailInput instanceof HTMLInputElement ? emailInput : null);
      hasError = true;
    }

    if (!password) {
      this.showAuthError('signup-password-error', 'This field is required', passwordInput instanceof HTMLInputElement ? passwordInput : null);
      hasError = true;
    } else if (password.length < 6 || password.length > 40) {
      this.showAuthError('signup-password-error', 'Password must be between 6 and 40 characters', passwordInput instanceof HTMLInputElement ? passwordInput : null);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      let shopifyAuth = window.shopifyAuthInstance;
      if (!shopifyAuth && window.ShopifyAuth && window.shopifyAPI) {
        shopifyAuth = new window.ShopifyAuth(window.shopifyAPI);
        window.shopifyAuthInstance = shopifyAuth;
      }

      if (!shopifyAuth) {
        throw new Error('Authentication service not available. Please refresh the page.');
      }

      const result = await shopifyAuth.register({
        firstName,
        lastName,
        email,
        password
      });

      if (result.success) {
        // Clear cache and refresh customer data
        this.customerData = null;
        this.customerMetafieldsLoaded = false;
        await this.fetchCustomerData();

        // Load metafields to check if user has fit profile
        if (this.customerData && !this.customerMetafieldsLoaded) {
          await this.loadCustomerMetafields();
        }

        if (window.updateHeaderAuthState) {
          window.updateHeaderAuthState();
        }

        const hasFitProfile = this.hasSavedFitData();

        // Route based on navigation intent
        if (this.tempNavigationIntent === 'summary') {
          // User was trying to access summary tab
          this.switchTab(2);
          this.tempNavigationIntent = null;
        } else if (this.tempNavigationIntent === 'enter-measurements' || !hasFitProfile) {
          // New user or user trying to enter measurements - show measurements form directly
          this.switchTab(1);
          this.tempNavigationIntent = null;

          // Show the fit-content-view (measurements form) and hide fit-options-view
          setTimeout(() => {
            const contentContainer = this.productDetailsContainer?.querySelector('.content-container');
            if (contentContainer && contentContainer instanceof HTMLElement) {
              const tabInnerContainer = contentContainer.querySelector('.tab-inner-content-container');
              if (tabInnerContainer) {
                const fitOptionsView = tabInnerContainer.querySelector('.fit-options-view');
                const fitContentView = tabInnerContainer.querySelector('.fit-content-view');
                const fitPrevBtn = contentContainer.querySelector('.fit-prev-btn');
                const nextContainer = contentContainer.querySelector('.next-container');

                if (fitOptionsView && fitContentView) {
                  if (fitOptionsView instanceof HTMLElement) {
                    fitOptionsView.style.display = 'none';
                  }
                  if (fitContentView instanceof HTMLElement) {
                    fitContentView.style.display = 'block';
                  }

                  // Show previous button and next button
                  if (fitPrevBtn && fitPrevBtn instanceof HTMLElement) {
                    fitPrevBtn.style.display = 'block';
                  }
                  if (nextContainer && nextContainer instanceof HTMLElement) {
                    nextContainer.style.display = 'block';
                  }

                  // Hide the regular previous button
                  const prevBtn = contentContainer.querySelector('[data-prev-tab="0"]');
                  if (prevBtn && prevBtn instanceof HTMLElement) {
                    prevBtn.style.display = 'none';
                  }
                }
              }
            }
          }, 100);
        } else {
          this.switchTab(1);
        }
      }

    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.message || 'Registration failed. Please try again.';
      this.showAuthError('signup-email-error', errorMessage);
    }
  }

  /**
   * Handle forgot password form submission
   * @param {HTMLFormElement} form
   */
  async handleForgotPasswordSubmit(form) {
    const emailInput = form.querySelector('#forgot-email');
    const email = (emailInput instanceof HTMLInputElement ? emailInput.value : '')?.trim() || '';

    // Clear previous errors
    this.clearAuthErrors();

    // Match login.liquid messages
    if (!email) {
      this.showAuthError('forgot-email-error', 'This field is required', emailInput instanceof HTMLInputElement ? emailInput : null);
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showAuthError('forgot-email-error', 'Email Address is not valid', emailInput instanceof HTMLInputElement ? emailInput : null);
      return;
    }

    this.authLoading = true;
    this.updateAuthLoadingState();

    try {
      // Get or create shopifyAuth instance
      let shopifyAuth = window.shopifyAuthInstance;
      if (!shopifyAuth && window.ShopifyAuth && window.shopifyAPI) {
        shopifyAuth = new window.ShopifyAuth(window.shopifyAPI);
        window.shopifyAuthInstance = shopifyAuth;
      }

      if (!shopifyAuth) {
        throw new Error('Authentication service not available. Please refresh the page.');
      }

      const result = await shopifyAuth.recoverPassword(email);

      if (result.success) {
        // Show success message
        const successDiv = this.productDetailsContainer?.querySelector('#forgot-password-success');
        if (successDiv && successDiv instanceof HTMLElement) {
          successDiv.style.display = 'block';
        }
        if (form) {
          form.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = error.message || 'Failed to send password recovery email. Please try again.';
      this.showAuthError('forgot-email-error', errorMessage);
    } finally {
      this.authLoading = false;
      this.updateAuthLoadingState();
    }
  }

  /**
   * Helper: Validate email format
   * @param {string} email
   * @returns {boolean}
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Helper: Show auth error message (matches login.liquid behavior)
   * @param {string} errorId
   * @param {string} message
   * @param {HTMLInputElement|null} [input]
   */
  showAuthError(errorId, message, input = null) {
    const errorElement = this.productDetailsContainer?.querySelector(`#${errorId}`);
    if (errorElement && errorElement instanceof HTMLElement) {
      errorElement.textContent = message;
      errorElement.classList.add('visible');
    }

    // Prefer explicit input; otherwise infer from errorId
    let targetInput = input;
    if (!targetInput && this.productDetailsContainer) {
      const map = {
        'login-email-error': '#auth-email',
        'login-password-error': '#auth-password',
        'signup-firstname-error': '#signup-firstname',
        'signup-lastname-error': '#signup-lastname',
        'signup-email-error': '#signup-email',
        'signup-password-error': '#signup-password',
        'forgot-email-error': '#forgot-email'
      };
      const selector = map[errorId];
      if (selector) {
        const el = this.productDetailsContainer.querySelector(selector);
        if (el instanceof HTMLInputElement) targetInput = el;
      }
    }

    if (targetInput) {
      targetInput.classList.add('error');
    }
  }

  /**
   * Helper: Clear a single auth error
   * @param {string} errorId
   * @param {HTMLInputElement|null} [input]
   */
  clearAuthError(errorId, input = null) {
    const errorElement = this.productDetailsContainer?.querySelector(`#${errorId}`);
    if (errorElement && errorElement instanceof HTMLElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('visible');
    }
    if (input) {
      input.classList.remove('error');
    }
  }

  /**
   * Helper: Clear all auth errors
   */
  clearAuthErrors() {
    const errorElements = this.productDetailsContainer?.querySelectorAll('.auth-error');
    errorElements?.forEach(el => {
      if (el instanceof HTMLElement) {
        el.textContent = '';
        el.classList.remove('visible');
      }
    });

    const erroredInputs = this.productDetailsContainer?.querySelectorAll('input.error');
    erroredInputs?.forEach(el => {
      if (el instanceof HTMLInputElement) {
        el.classList.remove('error');
      }
    });
  }

  /**
   * Attach blur/input validation for auth fields (email required + regex, password required)
   * Mirrors login.liquid behavior.
   */
  setupAuthInputValidation() {
    if (!this.productDetailsContainer) return;

    /** @type {Array<{inputSelector: string, errorId: string, type: 'email'|'password'|'text', minLen?: number, maxLen?: number}>} */
    const fields = [
      { inputSelector: '#auth-email', errorId: 'login-email-error', type: 'email' },
      { inputSelector: '#auth-password', errorId: 'login-password-error', type: 'password' },
      { inputSelector: '#signup-firstname', errorId: 'signup-firstname-error', type: 'text' },
      { inputSelector: '#signup-lastname', errorId: 'signup-lastname-error', type: 'text' },
      { inputSelector: '#signup-email', errorId: 'signup-email-error', type: 'email' },
      { inputSelector: '#signup-password', errorId: 'signup-password-error', type: 'password', minLen: 6, maxLen: 40 },
      { inputSelector: '#forgot-email', errorId: 'forgot-email-error', type: 'email' }
    ];

    fields.forEach(cfg => {
      const input = this.productDetailsContainer.querySelector(cfg.inputSelector);
      if (!(input instanceof HTMLInputElement)) return;

      // Avoid double-attaching when view is shown multiple times
      if (input.dataset.authValidationAttached === 'true') return;
      input.dataset.authValidationAttached = 'true';

      input.addEventListener('blur', () => {
        const value = (input.value || '').trim();

        // Required
        if (!value) {
          this.showAuthError(cfg.errorId, 'This field is required', input);
          return;
        }

        // Email format
        if (cfg.type === 'email' && !this.isValidEmail(value)) {
          this.showAuthError(cfg.errorId, 'Email Address is not valid', input);
          return;
        }

        // Password length (keep existing signup constraint)
        if (cfg.type === 'password' && (cfg.minLen || cfg.maxLen)) {
          if ((cfg.minLen && value.length < cfg.minLen) || (cfg.maxLen && value.length > cfg.maxLen)) {
            this.showAuthError(cfg.errorId, 'Password must be between 6 and 40 characters', input);
            return;
          }
        }

        // Valid
        this.clearAuthError(cfg.errorId, input);
      });

      input.addEventListener('input', () => {
        // Clear error state as user types (matches login.liquid)
        this.clearAuthError(cfg.errorId, input);
      });
    });
  }

  /**
   * Helper: Update loading state in auth views
   */
  updateAuthLoadingState() {
    const spinners = this.productDetailsContainer?.querySelectorAll('.auth-spinner');
    const formContainers = this.productDetailsContainer?.querySelectorAll('.auth-form, .auth-form-container');

    spinners?.forEach(spinner => {
      if (spinner instanceof HTMLElement) {
        if (this.authLoading) {
          spinner.classList.remove('is-hidden');
        } else {
          spinner.classList.add('is-hidden');
        }
      }
    });

    formContainers?.forEach(container => {
      if (container instanceof HTMLElement) {
        if (this.authLoading) {
          container.classList.add('is-hidden');
        } else {
          container.classList.remove('is-hidden');
        }
      }
    });
  }

  async addToCartAndCallAPI() {
    // Guard against concurrent calls — rapid double-clicks or async race
    // conditions between the custom button and the native button path could
    // both reach this method before the first fetch completes.
    // Shopify's cart merges identical properties into a qty increment rather
    // than two separate line items, so a second in-flight call = +2 qty.
    if (this._addToCartInProgress) return;
    this._addToCartInProgress = true;

    try {
    // Monogram Yes requires exactly 3 initials (blocks all ATC entry points)
    if (!this.validateMonogram()) {
      return;
    }

    // Fetch customer data if available — guests are allowed, login only required at checkout
    const customer = await this.fetchCustomerData();

    // Build current metafields from form selections
    const currentMetafields = this.buildCurrentMetafields();

    // Get variant ID - ensure method exists
    const variantId = this.getCorrectVariantId();

    // Build customization data object for cart properties
    /** @type {Record<string, any>} */
    const customizationData = {
      timestamp: new Date().toISOString(),
      product: {
        id: variantId, // Use calculated variant ID
        title: this.getProductTitle(),
        sku: this.getProductSKU()
      }
    };

    // Add fit data to customizationData
    if (window.cachedSelections?.fit && Array.isArray(window.cachedSelections.fit)) {
      window.cachedSelections.fit.forEach(item => {
        if (item && item.name && item.value) {
          const valueToUse = this.fitMostRecentFields.has(item.name)
            ? this.getTrueMostRecentValue(item.name)
            : item.value;
          customizationData[item.name] = valueToUse;
        }
      });
    }

    // Add style data to customizationData
    if (this.styleOptions && Array.isArray(this.styleOptions)) {
      this.styleOptions.forEach(option => {
        if (!option || option.selected === -1 || option.selected === undefined) {
          return;
        }

        if (option.name === 'monogram') {
          const productCode = this.getMonogramProductCode(variantId);
          if (option.selected === 1 && option.monogram) {
            const initials = option.monogram.trim().toUpperCase();
            customizationData.monogram = productCode
              ? `${productCode} / Yes / ${initials}`
              : `Yes / ${initials}`;
          } else {
            customizationData.monogram = productCode ? `${productCode} / No` : 'No';
          }
        } else if (option.name === 'collar' && this.collarOptions && this.collarOptions[option.selected]) {
          const selectedCollar = this.collarOptions[option.selected];
          if (selectedCollar) {
            // Use collar_style, not collar — collar is already the SIZE from the fit loop above.
            // Writing to collar here would overwrite the size measurement in cartData.properties.
            customizationData.collar_style = selectedCollar.title;
          }
        } else if (option.name === 'cuff' && this.cuffOptions && this.cuffOptions[option.selected]) {
          const selectedCuff = this.cuffOptions[option.selected];
          if (selectedCuff) {
            customizationData.cuff = selectedCuff.title;
          }
        } else if (option.name === 'pocket' && this.pocketOptions && this.pocketOptions[option.selected]) {
          const selectedPocket = this.pocketOptions[option.selected];
          if (selectedPocket) {
            customizationData.pocket = selectedPocket.title;
          }
        } else if (option.name === 'tail' && this.tailOptions && this.tailOptions[option.selected]) {
          const selectedTail = this.tailOptions[option.selected];
          if (selectedTail) {
            customizationData.tail = selectedTail.title;
          }
        } else if (option.name === 'front' && this.frontOptions && this.frontOptions[option.selected]) {
          const selectedFront = this.frontOptions[option.selected];
          if (selectedFront) {
            customizationData.front = selectedFront.title;
          }
        }
      });
    }

    // Call backend API only if logged in and metafields have changed
    if (customer && Object.keys(currentMetafields).length > 0 && this.hasMetafieldsChanged(currentMetafields)) {
      try {
        const requestBody = {
          customerId: this.customerData.id,
          customerEmail: this.customerData.email,
          metafields: currentMetafields
        };

        const apiResponse = await fetch('https://faas-nyc1-2ef2e6cc.doserverless.co/api/v1/namespaces/fn-53e38dee-a636-46cf-93b7-21004c4488bb/actions/shopify-dev-fucntion?blocking=true&result=true', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ZDg2YjFmNjktOTc3OC00YWE1LTkwOWEtM2RlMGMyNDRhZDE4OmJxUG80OHV1aDBxcnVURWhscHg1Ukd0QWpkNjNOM1FkOFJic2lKb0Z6dlNYaHhodTBrMTdWM2tjMTN5RW5mbVk='
          },
          body: JSON.stringify(requestBody)
        });

        if (!apiResponse.ok) {
          const errorData = await apiResponse.json();
          console.error('API error:', errorData);
        }
      } catch (apiError) {
        console.error('API error:', apiError);
      }
    }

    // Save to localStorage on add-to-cart — this is when persisted fit data is first created
    this.saveCustomizationToLocalStorage(!!customer, { allowCreate: true });

    // Add to cart
    const productForm = document.querySelector(
      'product-form-component form, form[data-type="add-to-cart-form"], .shopify-product-form, form[action*="/cart/add"]'
    );
    if (!productForm) {
      throw new Error('Product form not found');
    }

    const variantIdInput = productForm.querySelector('input[name="id"]');
    if (!variantIdInput || !(variantIdInput instanceof HTMLInputElement)) {
      throw new Error('Variant ID not found');
    }

    // Variant ID is already fetched above as 'variantId' using getCorrectVariantId()
    // We should verify if it matches if needed, or just use declared one.
    // However, logic below relies on variantId. Let's assume the first one is the source of truth.
    // But we need to check if the logic below intends to grab it fresh from form even if modified? 
    // Actually, getCorrectVariantId attempts form read.

    // Removing redeclaration. We will update the variable if invalid? 
    // Since 'variantId' is const at top, we cannot reassign.
    // The previous code had `const variantId = variantIdInput.value`.
    // I should remove this line and rely on the top `variantId`.

    // Check if variantId is valid or fallback to input value?
    // The top one `const variantId = this.getCorrectVariantId()` covers input value.

    let variantTitle = null;
    if (variantId && window.variantMetafields && window.variantMetafields[variantId]) {
      variantTitle = window.variantMetafields[variantId].title;
    }

    /** @type {{id: string, quantity: number, properties: Record<string, string>}} */
    const cartData = {
      id: variantId,
      quantity: 1,
      properties: {
        Customization: 'Custom',
      }
    };

    if (variantTitle && variantTitle.trim() !== '') {
      cartData.properties['Variant'] = variantTitle.trim();
    }

    Object.keys(customizationData).forEach(key => {
      if (key !== 'timestamp' && key !== 'product') {
        cartData.properties[key] = String(customizationData[key]);
      }
    });

    if (typeof window.hamiltonFinalizeCartProperties === 'function') {
      cartData.properties = window.hamiltonFinalizeCartProperties(cartData.properties, variantId);
    }

    const cartResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(cartData)
    });

    const cartText = await cartResponse.text();
    let cartResult = null;
    try {
      cartResult = cartText ? JSON.parse(cartText) : null;
    } catch {
      const fallback =
        cartResponse.status === 429
          ? 'Too many requests. Please wait a moment and try again.'
          : cartResponse.status === 401
            ? 'Session expired. Please refresh the page and try again.'
            : 'Could not add to cart. Please refresh the page and try again.';
      throw new Error(fallback);
    }

    if (!cartResponse.ok) {
      throw new Error(
        'Failed to add to cart: ' + (cartResult?.description || cartResult?.message || cartResponse.statusText)
      );
    }


    // Safely attempt to open drawer or redirect
    if (typeof this.openCartDrawer === 'function') {
      this.openCartDrawer();
    } else {
      // Fallback: Dispatch event or redirect
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartResult }
      }));
      // Also try common theme events
      document.dispatchEvent(new CustomEvent('cart:add', {
        bubbles: true,
        detail: { cart: cartResult }
      }));

      // If we want to force open drawer usually we need specific theme code.
      // For now, let's just ensure we return so the button resets.
      // We can add a simple logic to show cart count update if possible?

      // Dispatch fetch cart event for header update
      fetch('/cart.js').then(r => r.json()).then(c => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: c } }));
      });

      // Redirect to cart as reliable fallback if drawer doesn't open? 
      // User asked for "Add to cart" which usually opens drawer. 
      // If no drawer, maybe just alert or toast? 
      // For now, simply returning allows the button to reset.

      // Try to find a cart icon to click?
      const cartIcon = document.querySelector('.header__icon--cart, #cart-icon-bubble');
      if (cartIcon) cartIcon.click();
    }
    } finally {
      // Always release the lock — whether the call succeeded, threw, or was
      // interrupted by an await rejection. Without this the button would stay
      // permanently locked after any error.
      this._addToCartInProgress = false;
    }
  }



  getProductTitle() {
    const titleElement = document.querySelector('h1, .product-title, [data-product-title]');
    return titleElement ? titleElement.textContent.trim() : 'Pleated Front Tuxedo';
  }

  getProductPrice() {
    const priceElement = document.querySelector('.product-price, .price, [data-product-price], .money');
    return priceElement ? priceElement.textContent.trim() : '$278.00';
  }

  getShirtStyle() {
    const metaTag = document.querySelector('meta[name="product-shirt-style"]');
    if (metaTag) {
      const shirtStyle = metaTag.getAttribute('data-shirt-style') || metaTag.getAttribute('content') || '';
      if (shirtStyle) {
        return shirtStyle;
      }
    }

    if (window.productTags && Array.isArray(window.productTags)) {
      for (const tag of window.productTags) {
        if (tag.includes('Shirt Style:')) {
          const parts = tag.split(':');
          if (parts.length >= 2) {
            return parts.slice(1).join(':').trim();
          }
        }
      }
    }

    const dataAttr = this.productDetailsContainer?.getAttribute('data-shirt-style');
    if (dataAttr) {
      return dataAttr;
    }
    return '';
  }

  getProductOrganizationType() {
    const metaTag = document.querySelector('meta[name="product-organization-type"]');
    if (metaTag) {
      return metaTag.getAttribute('content')?.trim() || '';
    }
    return '';
  }

  /**
   * @param {any} decimalValue
   * @returns {any}
   */
  convertDecimalToCollarFraction(decimalValue) {
    if (!decimalValue) return decimalValue;
    const valueStr = String(decimalValue);
    const numValue = parseFloat(valueStr);
    if (isNaN(numValue)) return decimalValue;
    const wholePart = Math.floor(numValue);
    const decimalPart = numValue - wholePart;
    /** @type {Record<string, string>} */
    const decimalToFractionMap = {
      '0.5': '½', '0.25': '¼', '0.75': '¾', '0.33': '⅓', '0.667': '⅔',
      '0.125': '⅛', '0.375': '⅜', '0.625': '⅝', '0.875': '⅞'
    };
    const roundedDecimal = Math.round(decimalPart * 1000) / 1000;
    const fraction = decimalToFractionMap[String(roundedDecimal)];
    return fraction ? `${wholePart}${fraction}` : decimalValue;
  }

  /**
   * Restores the server-loaded baseline value for a fit field back into
   * window.cachedSelections.fit so that "most recent" always reflects the true
   * account value, never a value entered during the current session.
   * @param {string} fitName
   */
  restoreMetafieldValueToCache(fitName) {
    if (!window.cachedSelections) window.cachedSelections = { fit: [], style: [] };

    // Primary source: the snapshot saved when the fit tab first loaded
    const baselineItem = this.fitMetafieldBaseline.find(f => f.name === fitName);
    if (baselineItem && baselineItem.value && baselineItem.value !== 'None') {
      const existing = window.cachedSelections.fit.find(f => f.name === fitName);
      if (existing) {
        existing.value = baselineItem.value;
      } else {
        window.cachedSelections.fit.push({ name: fitName, value: baselineItem.value });
      }
      return;
    }

    // Fallback: read directly from customerData.metafields and format it
    if (this.customerData && this.customerData.metafields) {
      const raw = this.customerData.metafields[fitName];
      if (raw !== null && raw !== undefined && raw !== '') {
        let value = String(raw).replace(/\.0$/, '');
        if (fitName === 'collar') value = this.convertDecimalToCollarFraction(value);
        if (fitName === 'fit') value = value.toLowerCase();
        if (value) {
          const existing = window.cachedSelections.fit.find(f => f.name === fitName);
          if (existing) {
            existing.value = value;
          } else {
            window.cachedSelections.fit.push({ name: fitName, value });
          }
          return;
        }
      }
    }

    // No saved value — use last-order sentinel so cart and UI show "As Last"
    const existing = window.cachedSelections.fit.find(f => f.name === fitName);
    if (existing) {
      existing.value = 'as last';
    } else {
      window.cachedSelections.fit.push({ name: fitName, value: 'as last' });
    }
  }

  /**
   * Customer metafields formatted the same way as fit cache (collar fractions, fit lowercased).
   * @param {string} fitName
   * @returns {string|null}
   */
  resolveFitValueFromCustomerMetafields(fitName) {
    if (!this.customerData?.metafields) return null;
    const raw = this.customerData.metafields[fitName];
    if (raw === null || raw === undefined || raw === '') return null;
    let value = String(raw).replace(/\.0$/, '');
    if (fitName === 'collar') value = this.convertDecimalToCollarFraction(value);
    if (fitName === 'fit') value = value.toLowerCase();
    return value || null;
  }

  /**
   * When switching to manual ("new measurements"), replace literal `most recent` sentinel
   * values in the fit cache with real metafields when possible, otherwise drop those entries,
   * so inputs and dropdowns show real sizes — not the placeholder string.
   */
  sanitizeFitCacheForManualPath() {
    if (!window.cachedSelections?.fit?.length) return;

    /** @param {unknown} val */
    const isMostRecentSentinel = (val) => this.isLastOrderSentinel(val);

    window.cachedSelections.fit = window.cachedSelections.fit.filter((item) => {
      if (!item?.name || item.value === undefined || item.value === null) return true;
      if (!isMostRecentSentinel(item.value)) return true;

      const resolved = this.resolveFitValueFromCustomerMetafields(item.name);
      if (resolved) {
        item.value = resolved;
        return true;
      }
      return false;
    });
  }

  /**
   * Returns the value to use when a fit field is in "most recent" mode.
   * Logged-in users: returns their saved metafield value from their previous order.
   * Guest users (or logged-in with no saved measurement): returns the literal string
   * "as last" so the order team knows to look up their previous order.
   * @param {string} fitName
   * @returns {string}
   */
  getTrueMostRecentValue(fitName) {
    if (this.customerData?.metafields) {
      const raw = this.customerData.metafields[fitName];
      if (raw !== null && raw !== undefined && raw !== '') {
        let val = String(raw).replace(/\.0$/, '');
        if (fitName === 'collar') val = this.convertDecimalToCollarFraction(val);
        if (fitName === 'fit') val = val.toLowerCase();
        if (val) return val;
      }
    }
    return 'as last';
  }

  /**
   * @param {any} collarValue
   * @returns {string}
   */
  convertCollarToDecimal(collarValue) {
    if (!collarValue || typeof collarValue !== 'string') return collarValue;
    const fractionMap = {
      '½': '.5', '¼': '.25', '¾': '.75', '⅓': '.33', '⅔': '.67',
      '⅛': '.125', '⅜': '.375', '⅝': '.625', '⅞': '.875'
    };
    let converted = collarValue;
    for (const [fraction, decimal] of Object.entries(fractionMap)) {
      if (converted.includes(fraction)) {
        converted = converted.replace(fraction, decimal);
        break;
      }
    }
    const numValue = parseFloat(converted);
    return !isNaN(numValue) ? numValue.toString() : collarValue;
  }

  // ==========================================
  // LOCAL STORAGE — GUEST PERSISTENCE & SYNC
  // ==========================================

  /**
   * Save current customization selections to localStorage.
   * Created on add-to-cart; updated on later edits when storage already exists.
   * @param {boolean} isSynced - true when customer is logged in and metafields were already saved
   * @param {{ allowCreate?: boolean }} [options]
   */
  saveCustomizationToLocalStorage(isSynced, options = {}) {
    const { allowCreate = false } = options;
    if (!allowCreate && !this.loadCustomizationFromLocalStorage()) {
      return;
    }

    try {
      const data = {
        fit: {},
        style: {},
        styleOptions: this.styleOptions ? JSON.parse(JSON.stringify(this.styleOptions)) : [],
        selectedPath: this.selectedPath,
        fitMostRecentFields: [...this.fitMostRecentFields],
        synced: !!isSynced,
        timestamp: new Date().toISOString()
      };

      if (window.cachedSelections?.fit && Array.isArray(window.cachedSelections.fit)) {
        window.cachedSelections.fit.forEach(item => {
          if (item?.name && item?.value) {
            if (this.fitMostRecentFields.has(item.name)) {
              data.fit[item.name] = 'as last';
            } else {
              data.fit[item.name] = item.value;
            }
          }
        });
      }

      if (window.cachedSelections?.style && Array.isArray(window.cachedSelections.style)) {
        window.cachedSelections.style.forEach(item => {
          if (item?.name && item?.value) data.style[item.name] = item.value;
        });
      }

      localStorage.setItem('hamilton_customization', JSON.stringify(data));
    } catch (e) {
      console.warn('[LocalStorage] Failed to save customization:', e);
    }
  }

  /** @param {string} fitName */
  clearLastOrderForField(fitName) {
    this.fitMostRecentFields.delete(fitName);
    if (this.fitMostRecentFields.size === 0 && this.selectedPath === 'previous') {
      this.selectedPath = 'manual';
    }
  }

  /**
   * @returns {object|null}
   */
  loadCustomizationFromLocalStorage() {
    try {
      const raw = localStorage.getItem('hamilton_customization');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.warn('[LocalStorage] Failed to read customization:', e);
      return null;
    }
  }

  /**
   * Populate window.cachedSelections.fit from localStorage if no metafield data exists yet.
   * Also restores styleOptions so the fit overview and summary can be pre-filled.
   */
  populateCachedSelectionsFromLocalStorage() {
    try {
      const data = this.loadCustomizationFromLocalStorage();
      if (!data) return;

      if (!window.cachedSelections) window.cachedSelections = { fit: [], style: [] };

      if (data.selectedPath) {
        this.selectedPath = data.selectedPath;
      }
      if (Array.isArray(data.fitMostRecentFields)) {
        this.fitMostRecentFields = new Set(data.fitMostRecentFields);
      } else {
        this.fitMostRecentFields = new Set();
      }

      if (data.fit && Object.keys(data.fit).length > 0) {
        const fitFields = ['collar', 'chest', 'fit', 'sleeve'];
        fitFields.forEach(name => {
          if (!data.fit[name]) return;
          const existing = window.cachedSelections.fit.find(f => f.name === name);
          if (existing) {
            existing.value = data.fit[name];
          } else {
            window.cachedSelections.fit.push({ name, value: data.fit[name] });
          }
        });

        fitFields.forEach(name => {
          if (this.isLastOrderSentinel(data.fit[name])) {
            this.fitMostRecentFields.add(name);
          } else {
            this.fitMostRecentFields.delete(name);
          }
        });
      }

      // Restore style options if current product hasn't loaded its own
      if (data.styleOptions && data.styleOptions.length > 0 &&
          (!this.styleOptions || this.styleOptions.length === 0)) {
        this.styleOptions = data.styleOptions;
      }
    } catch (e) {
      console.warn('[LocalStorage] Failed to populate cachedSelections:', e);
    }
  }

  /**
   * If customer is now logged in but localStorage data was saved while guest,
   * push the fit measurements to their metafields and mark as synced.
   */
  async syncLocalStorageToMetafields() {
    try {
      const data = this.loadCustomizationFromLocalStorage();
      if (!data || data.synced || !this.customerData) return;

      // Never overwrite if the customer already has fit data in their metafields —
      // stale localStorage data could corrupt correct saved measurements
      const existingMetafields = this.getCustomerMetafields();
      const hasExistingFitData = existingMetafields.collar || existingMetafields.chest ||
        existingMetafields.fit || existingMetafields.sleeve;
      if (hasExistingFitData) {
        // Mark as synced so we don't check again
        data.synced = true;
        localStorage.setItem('hamilton_customization', JSON.stringify(data));
        return;
      }

      const metafields = {};
      const fitFields = ['collar', 'chest', 'fit', 'sleeve'];
      fitFields.forEach(key => {
        if (data.fit?.[key] && data.fit[key] !== 'None') {
          metafields[key] = data.fit[key];
        }
      });

      if (Object.keys(metafields).length === 0) return;

      const response = await fetch('https://faas-nyc1-2ef2e6cc.doserverless.co/api/v1/namespaces/fn-53e38dee-a636-46cf-93b7-21004c4488bb/actions/shopify-dev-fucntion?blocking=true&result=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ZDg2YjFmNjktOTc3OC00YWE1LTkwOWEtM2RlMGMyNDRhZDE4OmJxUG80OHV1aDBxcnVURWhscHg1Ukd0QWpkNjNOM1FkOFJic2lKb0Z6dlNYaHhodTBrMTdWM2tjMTN5RW5mbVk='
        },
        body: JSON.stringify({
          customerId: this.customerData.id,
          customerEmail: this.customerData.email,
          metafields
        })
      });

      if (response.ok) {
        data.synced = true;
        localStorage.setItem('hamilton_customization', JSON.stringify(data));
        console.log('[LocalStorage] Guest customization synced to customer metafields');
      }
    } catch (e) {
      console.warn('[LocalStorage] Failed to sync to metafields:', e);
    }
  }

  restoreOriginalContent() {
    if (!this.productDetailsContainer) return;

    const customizationContainer = this.productDetailsContainer.querySelector('.customization-container');
    if (customizationContainer && customizationContainer instanceof HTMLElement) {
      customizationContainer.style.display = 'none';
    }

    const authContainer = this.productDetailsContainer.querySelector('.auth-container');
    if (authContainer && authContainer instanceof HTMLElement) {
      authContainer.style.display = 'none';
    }

    const originalContent = this.productDetailsContainer.querySelector('.original-product-content');
    if (originalContent && originalContent instanceof HTMLElement) {
      originalContent.style.display = 'block';
    }

    // Monogram Yes/No is a priced product option shared with the standard PDP form.
    // Leaving customize must not leave Monogram Yes on size + ATC — only customize ATC
    // should use it. Keep styleOptions.monogram so reopening customize re-applies Yes.
    this.resetMonogramOnProductForm();

    // Restore PDP buy buttons if customize hid them for the Summary ATC clone
    if (this._originalBuyButtonsBlock instanceof HTMLElement) {
      this._originalBuyButtonsBlock.style.display = '';
    }
    const externalBtn = this.productDetailsContainer.querySelector('#add-to-cart-with-api');
    if (externalBtn instanceof HTMLElement) externalBtn.style.display = 'none';
    const externalContainer = this.productDetailsContainer.querySelector('.external-add-to-cart-container');
    if (externalContainer instanceof HTMLElement) externalContainer.style.display = 'none';

    this.isCustomizationShown = false;
    this.activeTab = -1;
    this.selectedPath = null;
    this.fitMostRecentFields = new Set();
    this.editingOptionIndex = null;
    this.authView = null;
    this.tempNavigationIntent = null;

    this.customizeButton = document.querySelector('.pdt-customize-btn');
  }

  /**
   * Restore Monogram No on the shared product form / PDP price.
   * Does not clear styleOptions.monogram — reopen customize re-applies Yes.
   */
  resetMonogramOnProductForm() {
    this.applyMonogramVariantSelection(0);
  }

  /**
   * Apply the priced Monogram Yes/No variant that matches current styleOptions.
   * Defaults to No when unset so UI and PDP price cannot diverge after refresh.
   */
  syncMonogramVariantFromSelection() {
    const monogramOption = this.styleOptions?.find((opt) => opt.name === 'monogram');
    const choice = monogramOption && monogramOption.selected === 1 ? 1 : 0;
    this.applyMonogramVariantSelection(choice);
  }

  getMonogramProductCode(variantId) {
    const shirtId = window.variantMetafields?.[variantId]?.shirt_id;
    if (shirtId != null && String(shirtId).trim() !== '') {
      return String(shirtId).trim();
    }
    return this.getProductSKU() || '';
  }

  /**
   * Product JSON used to map Monogram Yes/No to the correct priced variant.
   * @returns {{options?: string[], variants: Array<Record<string, any>>}|null}
   */
  getProductData() {
    if (this._productData) return this._productData;

    const jsonScript = document.getElementById('ProductJson-product-template');
    if (jsonScript && jsonScript.textContent) {
      try {
        this._productData = JSON.parse(jsonScript.textContent);
        return this._productData;
      } catch (e) {
        console.warn('ProductCustomizationTable: Failed to parse ProductJson', e);
      }
    }

    if (Array.isArray(window.productVariants) && window.productVariants.length > 0) {
      this._productData = { variants: window.productVariants };
      return this._productData;
    }

    return null;
  }

  /**
   * 0-based index of the Monogram option, or -1 if not found.
   * @returns {number}
   */
  getMonogramOptionIndex() {
    const product = this.getProductData();
    if (!product?.variants?.length) return -1;

    if (Array.isArray(product.options)) {
      const byName = product.options.findIndex(
        (opt) => typeof opt === 'string' && opt.toLowerCase().includes('monogram')
      );
      if (byName >= 0) return byName;
    }

    // Heuristic: option position whose values are only Yes/No
    for (let i = 1; i <= 3; i++) {
      const values = new Set();
      product.variants.forEach((v) => {
        const val = v[`option${i}`];
        if (val) values.add(String(val).toLowerCase());
      });
      if (values.size === 2 && values.has('yes') && values.has('no')) {
        return i - 1;
      }
    }

    return -1;
  }

  /**
   * Exact Yes/No option labels from catalog (preserves merchant casing).
   * @returns {{yesValue: string, noValue: string}|null}
   */
  getMonogramYesNoValues() {
    const product = this.getProductData();
    const index = this.getMonogramOptionIndex();
    if (!product || index < 0) return null;

    const key = `option${index + 1}`;
    let yesValue = null;
    let noValue = null;

    product.variants.forEach((v) => {
      const val = v[key];
      if (!val) return;
      if (String(val).toLowerCase() === 'yes' && !yesValue) yesValue = val;
      if (String(val).toLowerCase() === 'no' && !noValue) noValue = val;
    });

    if (!yesValue || !noValue) return null;
    return { yesValue, noValue };
  }

  /**
   * Resolve the variant that matches the current selection with Monogram Yes or No.
   * Keeps all other options the same so fabric/size pricing stays correct.
   * @param {boolean} monogramEnabled
   * @param {string|number|null} [baseVariantId]
   * @returns {string|null}
   */
  resolveMonogramVariantId(monogramEnabled, baseVariantId = null) {
    const product = this.getProductData();
    const monogramIndex = this.getMonogramOptionIndex();
    const yesNo = this.getMonogramYesNoValues();
    if (!product?.variants?.length || monogramIndex < 0 || !yesNo) return null;

    const currentId = baseVariantId || this.getVariantId();
    if (!currentId) return null;

    const currentVariant = product.variants.find((v) => String(v.id) === String(currentId));
    if (!currentVariant) return null;

    const targetMonogram = monogramEnabled ? yesNo.yesValue : yesNo.noValue;
    const targetOptions = [];

    for (let i = 0; i < 3; i++) {
      const key = `option${i + 1}`;
      if (i === monogramIndex) {
        targetOptions.push(String(targetMonogram).toLowerCase());
      } else if (currentVariant[key] != null) {
        targetOptions.push(String(currentVariant[key]).toLowerCase());
      } else {
        targetOptions.push('');
      }
    }

    const match = product.variants.find((variant) => {
      for (let i = 0; i < 3; i++) {
        const key = `option${i + 1}`;
        const current = variant[key] != null ? String(variant[key]).toLowerCase() : '';
        if (current !== targetOptions[i]) return false;
      }
      return true;
    });

    return match ? String(match.id) : null;
  }

  /**
   * Write the selected variant id into the product form (what cart/add uses).
   * @param {string|number} variantId
   */
  applyVariantIdToForm(variantId) {
    if (!variantId) return;

    const form = document.querySelector(
      'product-form-component form, form[data-type="add-to-cart-form"], .shopify-product-form, form[action*="/cart/add"]'
    );
    if (!form) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement)) return;

    if (String(variantIdInput.value) === String(variantId)) return;

    variantIdInput.value = String(variantId);
    variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));

    try {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('variant', String(variantId));
      window.history.replaceState({ path: newUrl.href }, '', newUrl.href);
    } catch (e) {
      // ignore URL update failures
    }
  }

  /**
   * Sync form variant + PDP price when Monogram Yes/No changes.
   * @param {number} choice 0 = No, 1 = Yes
   */
  applyMonogramVariantSelection(choice) {
    const enabled = choice === 1;
    const resolvedId = this.resolveMonogramVariantId(enabled);
    if (resolvedId) {
      this.applyVariantIdToForm(resolvedId);
    }

    this.updateMonogramPrice(choice);
  }

  getProductSKU() {
    const form = document.querySelector('.shopify-product-form, form[action="/cart/add"]');
    if (form) {
      const skuInput = form.querySelector('input[name="sku"], input[data-sku]');
      if (skuInput && skuInput instanceof HTMLInputElement) {
        return skuInput.value || (skuInput instanceof HTMLElement ? skuInput.dataset.sku || '' : '');
      }

      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput instanceof HTMLElement && variantInput.dataset.sku) {
        return variantInput.dataset.sku;
      }
    }

    if (window.productTags && Array.isArray(window.productTags)) {
      const skuTag = window.productTags.find(tag => tag.includes('SKU:') || tag.match(/^SX\.\d+/));
      if (skuTag) {
        if (skuTag.includes('SKU:')) {
          const parts = skuTag.split('SKU:');
          return parts[1] ? parts[1].trim() : '';
        }
        const match = skuTag.match(/^(SX\.\d+)/);
        if (match) {
          return match[1];
        }
      }
    }

    return '';
  }

  getVariantId() {
    const form = document.querySelector('.shopify-product-form, form[action="/cart/add"]');
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput && variantIdInput instanceof HTMLInputElement && variantIdInput.value) {
        return variantIdInput.value;
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      return variantParam;
    }

    return null;
  }

  getCorrectVariantId() {
    let baseVariantId = null;

    const form = document.querySelector('.shopify-product-form, form[action="/cart/add"]');
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput && variantIdInput instanceof HTMLInputElement && variantIdInput.value) {
        baseVariantId = variantIdInput.value;
      }
    }

    if (!baseVariantId) {
      const urlParams = new URLSearchParams(window.location.search);
      baseVariantId = urlParams.get('variant');
    }

    if (!baseVariantId) return null;

    // Ensure cart gets Monogram Yes/No priced variant, not just the base fabric variant
    const monogramOption = this.styleOptions?.find((opt) => opt.name === 'monogram');
    if (monogramOption && monogramOption.selected !== undefined && monogramOption.selected !== -1) {
      const resolvedId = this.resolveMonogramVariantId(monogramOption.selected === 1, baseVariantId);
      if (resolvedId) {
        this.applyVariantIdToForm(resolvedId);
        return resolvedId;
      }
    }

    return baseVariantId;
  }

  getProductId() {
    const form = document.querySelector('.shopify-product-form, form[action="/cart/add"]');
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput && variantIdInput instanceof HTMLElement && variantIdInput.dataset.productId) {
        return variantIdInput.dataset.productId;
      }
    }

    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      return window.ShopifyAnalytics.meta.product.id;
    }

    return null;
  }

  // ==========================================
  // TAB CONTENT CREATION METHODS
  // ==========================================

  createTabContent() {
    if (this.activeTab === 0) {
      // Style tab - check if editing an option
      if (this.editingOptionIndex !== null) {
        const option = this.styleOptions[this.editingOptionIndex];
        if (option) {
          if (option.name === 'monogram') {
            return this.createMonogramEditor();
          }
          if (option.name === 'collar') {
            return this.createCollarEditor();
          }
          if (option.name === 'cuff') {
            return this.createCuffEditor();
          }
          if (option.name === 'pocket') {
            return this.createPocketEditor();
          }
          if (option.name === 'tail') {
            return this.createTailEditor();
          }
          if (option.name === 'front') {
            return this.createFrontEditor();
          }
        }
      }

      // Style tab - show style options if available, otherwise show size tables
      if (this.styleOptions && this.styleOptions.length > 0) {
        return this.createStyleOptionsContent();
      }
      return `
        <div class="style-tab">
          <div class="tab-inner-content-container">
            ${this.createStandardSizesTable()}
            ${this.createMadeToOrderSection()}
          </div>
          <div class="footer-nav inset-mto-contents">
            <h3 class="button--action button--minimal close-mto-button">Exit</h3>
            <div class="prev-next-nav-container">
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0" data-next-tab="1">
                  next
                </h3>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    if (this.activeTab === 1) {
      const mostRecentBtn = this.hasSavedFitData() ? `
        <button type="button" class="button--action button--full-width most-recent-btn" data-most-recent>
          As Last
        </button>
      ` : '';
      return `
        <div class="fit-tab">
          <div class="tab-inner-content-container">
            <div class="inset-mto-contents">
              <div class="fit-content-view">
                ${this.createFitTab()}
              </div>
              ${mostRecentBtn}
            </div>
          </div>
          <div class="footer-nav inset-mto-contents">
            <h3 class="button--action button--minimal close-mto-button">Exit</h3>
            <div class="prev-next-nav-container">
              <h3 class="button--action button--minimal mb-0" data-prev-tab="0">
                previous
              </h3>
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="2">
                  Next
                </h3>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    if (this.activeTab === 2) {
      let summaryContent = '';
      let prevTabIndex = '1';

      if (this.selectedPath === 'previous') {
        summaryContent = `<div class="summary-details"></div>`;
        prevTabIndex = '1';
      } else if (this.selectedPath === 'tryon') {
        summaryContent = `
          <div class="simple-path-message">
            <h1>Request Try-On Shirts</h1>
            <p>For a $100 refundable deposit, we'll send a couple of different try-on shirts to help decide on the perfect fit. Just fill out a few questions to help us determine the right measurements.</p>
            <a href="${window.CUSTOMIZATION_CONFIG?.tryOnLink || '#'}" class="button try-on-button px-6" target="_blank">Learn More</a>
          </div>
        `;
        prevTabIndex = '1';
      } else if (this.selectedPath === 'bespoke') {
        summaryContent = `
          <div class="simple-path-message">
            <h1>Bespoke</h1>
            <p>The pinnacle of the Hamilton experience. We craft your personal paper pattern to achieve the perfect fit.</p>
            <p>We begin by taking a series of measurements and constructing a trial shirt for your approval. After making any necessary adjustments, we then produce the remainder of your order. For first time Bespoke purchases, there is a minimum order of five shirts. Bespoke shirts typically range in price from $295 to $395.</p>
            <p>To begin the process, please answer a few questions <a href="${window.CUSTOMIZATION_CONFIG?.bespokeLink || '#'}">here</a> so that we can send you any necessary Try-On Shirts for a remote at-home fitting. You can also schedule an in-person appointment at our Houston Workshop or visit a stockist.</p>
            <p>If you're an existing Bespoke client, please contact a Shirtmaker at <a href="mailto:${window.CUSTOMIZATION_CONFIG?.contactEmail || 'info@hamiltonshirts.com'}">${window.CUSTOMIZATION_CONFIG?.contactEmail || 'info@hamiltonshirts.com'}</a> or <a href="tel:${window.CUSTOMIZATION_CONFIG?.contactPhone || '713.264.8800'}">${window.CUSTOMIZATION_CONFIG?.contactPhone || '713.264.8800'}</a> to complete your order.</p>
          </div>
        `;
        prevTabIndex = '1';
      } else {
        summaryContent = `<div class="summary-details"></div>`;
      }

      // For non-manual paths the content already has its own h1 — only show the product title h1 on the manual/previous summary
      const showProductTitle = this.selectedPath === 'manual' || this.selectedPath === 'previous' || !this.selectedPath;

      return `
        <div class="summary-tab">
          <div class="summary-tab-container inset-mto-contents">
            <div class="title-container">
              <h3 class="product-eyebrow">${this.productOrganizationType}</h3>
              ${showProductTitle ? `<h1>${this.getProductTitle()}</h1>` : ''}
            </div>
            ${summaryContent}
          </div>
          <div class="footer-nav inset-mto-contents">
            <h3 class="button--action button--minimal close-mto-button">Exit</h3>
            <div class="prev-next-nav-container">
              <h3 class="button--action button--minimal mb-0" data-prev-tab="${prevTabIndex}">
                previous
              </h3>
              <div class="next-container">
                <!-- Add to Cart button moved to persistent container -->
              </div>
            </div>
          </div>
        </div>
      `;
    }

    return '';
  }

  createStandardSizesTable() {
    const rows = this.standardSizes.map(size => `
      <tr>
        <td>${size.size}</td>
        <td>${size.chest}"</td>
        <td>${size.sleeve}"</td>
      </tr>
    `).join('');
    return `
      <div class="customization-section">
        <h3>Standard Sizes</h3>
        <p class="customization-description">
          REGULAR is a tailored fit (not baggy) with chest & sleeves cut closer to the body and a comfortable waist.<br>
          TRIM is a slimmer fit in the body and sleeves. As compared to REGULAR:
        </p>
        <ul class="customization-features">
          <li>+ Trimmer at the chest by 1″</li>
          <li>+ Trimmer at the waist by 2″ on most sizes</li>
          <li>+ Higher armhole</li>
          <li>+ Trimmer sleeve</li>
          <li>+ Narrower across shoulders</li>
        </ul>
        <div class="customization-table-container">
          <table class="customization-table">
            <thead>
              <tr>
                <th>SIZE</th>
                <th>CHEST</th>
                <th>SLEEVE</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  createMadeToOrderSection() {
    return `
      <div class="customization-section">
        <h3>Made-To-Order</h3>
        <p class="customization-description">
          Your Made-to-Order fit is based on your COLLAR size, CHEST size, SLEEVE length and FIT (Regular or Trim).
        </p>
        <div class="customization-table-container">
          <table class="customization-table">
            <thead>
              <tr>
                <th>MEASUREMENT</th>
                <th>INSTRUCTIONS</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>COLLAR</strong></td>
                <td>Take a skin measurement of your neck and add 0.75"</td>
              </tr>
              <tr>
                <td><strong>CHEST</strong></td>
                <td>This is your chest measurement or coat size</td>
              </tr>
              <tr>
                <td><strong>FIT</strong></td>
                <td>Regular or Trim</td>
              </tr>
              <tr>
                <td><strong>SLEEVE</strong></td>
                <td>Measure from the center of the back of your neck to knuckle of your thumb and subtract 1"</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="customization-note">
          <p><strong>TRY-ON SHIRTS ARE AVAILABLE FOR A REFUNDABLE DEPOSIT OF $100.</strong></p>
        </div>
      </div>
    `;
  }

  /**
   * Option catalogs keyed by Custom Style Options.xlsx labels (Option 1 first in each style row).
   * @returns {Record<string, {title: string, summaryName?: string, image: string|null, description: string, key: string, aliases?: string[]}>}
   */
  getStyleOptionCatalog() {
    const imgs = window.CUSTOMIZATION_IMAGES || {};
    return {
      collar: {
        Spread: {
          key: 'Spread',
          title: 'spread collar',
          summaryName: 'Spread',
          image: imgs.collarSpread || '',
          description: 'The Spread Collar works well with or without a tie.',
          aliases: ['spread', 'spread collar', 's']
        },
        Wing: {
          key: 'Wing',
          title: 'wing collar',
          summaryName: 'Wing',
          image: imgs.collarWing || '',
          description: 'The Wing Collar is the classic formal choice for black-tie.',
          aliases: ['wing', 'wing collar', 'w']
        },
        'Button Down': {
          key: 'Button Down',
          title: 'button down',
          summaryName: 'Button Down',
          image: imgs.collarButtonDown || '',
          description: 'A Button Down Collar is usually worn open but can be paired with a tie as well for a classic American look.',
          aliases: ['button down', 'button-down', 'buttondown', 'bd', '1883 bd']
        }
      },
      cuff: {
        Button: {
          key: 'Button',
          title: 'button cuff',
          summaryName: 'Button',
          image: imgs.cuffButton || '',
          description: 'Button Cuffs are extremely versatile and always appropriate.',
          aliases: ['button', 'button cuff', 'b']
        },
        French: {
          key: 'French',
          title: 'french',
          summaryName: 'French',
          image: imgs.cuffFrench || '',
          description: 'Conversely, French Cuffs are typically more formal.',
          aliases: ['french', 'french cuff', 'f']
        }
      },
      pocket: {
        No: {
          key: 'No',
          title: 'No Pocket',
          summaryName: 'No Pocket',
          image: null,
          description: 'If you frequently use a Dress Shirt Pocket, then we recommend it. Otherwise, leave it off for a cleaner look.',
          aliases: ['no', 'no pocket', '0']
        },
        Yes: {
          key: 'Yes',
          title: 'Pocket',
          summaryName: 'Pocket',
          image: imgs.pocketPlain || '',
          description: 'A traditional pocket for a more functional, classic style.',
          aliases: ['yes', 'pocket', '1', 'h']
        }
      },
      tail: {
        Shorter: {
          key: 'Shorter',
          title: 'Shorter',
          summaryName: 'Shorter',
          image: imgs.tailShort || 'https://cdn.shopify.com/s/files/1/0673/5387/0638/files/Tail.png?v=1765430148',
          description: 'A shorter, more casual tail designed to be worn untucked. Perfect for a relaxed, modern look.',
          aliases: ['shorter', 'short', 'short tail']
        },
        Longer: {
          key: 'Longer',
          title: 'Longer',
          summaryName: 'Longer',
          image: imgs.tailRegular || 'https://cdn.shopify.com/s/files/1/0673/5387/0638/files/tail-longer.c3cebfd.png?v=1765436936',
          description: 'A traditional longer tail designed to be tucked in. Stays securely in place throughout the day.',
          aliases: ['longer', 'long', 'regular', 'regular tail']
        }
      },
      front: {
        'Full Button Front': {
          key: 'Full Button Front',
          title: 'Full Button Front',
          summaryName: 'Full Button Front',
          image: imgs.frontFullButton || '',
          description: 'A classic full button front placket.',
          aliases: ['full button front', 'full button', 'button front', 'cp']
        },
        Pullover: {
          key: 'Pullover',
          title: 'Pullover',
          summaryName: 'Pullover',
          image: imgs.frontPullover || '',
          description: 'A pullover front for a cleaner, more casual look.',
          aliases: ['pullover']
        }
      }
    };
  }

  /**
   * Style_Models Custom Options — Option 1 is default; omit NA groups.
   * @returns {Record<string, {collar?: string[], cuff?: string[], tail?: string[], front?: string[], pocket?: string[], groups: string[]}>}
   */
  getStyleModelConfig() {
    /** @type {Record<string, {groups: string[], collar?: string[], cuff?: string[], tail?: string[], front?: string[], pocket?: string[], monogramNote: string}>} */
    return {
      'Tux Spread': { groups: ['Collar', 'Monogram'], collar: ['Spread', 'Wing'], monogramNote: 'Monogram on left cuff' },
      'Tux Wing': { groups: ['Collar', 'Monogram'], collar: ['Wing', 'Spread'], monogramNote: 'Monogram on left cuff' },
      'Dress BC': { groups: ['Collar', 'Cuff', 'Pocket', 'Monogram'], collar: ['Spread', 'Button Down'], cuff: ['Button', 'French'], pocket: ['No', 'Yes'], monogramNote: 'Monogram on left cuff' },
      'Dress FC': { groups: ['Collar', 'Cuff', 'Pocket', 'Monogram'], collar: ['Spread', 'Button Down'], cuff: ['French', 'Button'], pocket: ['No', 'Yes'], monogramNote: 'Monogram on left cuff' },
      'HCS BD': { groups: ['Collar', 'Pocket', 'Monogram'], collar: ['Button Down', 'Spread'], pocket: ['Yes', 'No'], monogramNote: 'Monogram on left cuff' },
      'HCS JS': { groups: ['Collar', 'Pocket', 'Monogram'], collar: ['Spread', 'Button Down'], pocket: ['Yes', 'No'], monogramNote: 'Monogram on left cuff' },
      'HCS W': { groups: ['Collar', 'Monogram'], collar: ['Spread', 'Button Down'], monogramNote: 'Monogram on left cuff' },
      'HCS FP': { groups: ['Collar', 'Monogram'], collar: ['Spread', 'Button Down'], monogramNote: 'Monogram on left cuff' },
      '1883 BD LS': { groups: ['Collar', 'Tail', 'Pocket', 'Monogram'], collar: ['Button Down', 'Spread'], tail: ['Shorter', 'Longer'], pocket: ['Yes', 'No'], monogramNote: 'Monogram on left cuff' },
      '1883 M LS': { groups: ['Collar', 'Tail', 'Pocket', 'Monogram'], collar: ['Spread', 'Button Down'], tail: ['Shorter', 'Longer'], pocket: ['Yes', 'No'], monogramNote: 'Monogram on left cuff' },
      '1883 Camp': { groups: ['Pocket', 'Monogram'], pocket: ['Yes', 'No'], monogramNote: 'Monogram on pocket' },
      '1883 BD SS': { groups: ['Collar', 'Tail', 'Front', 'Pocket', 'Monogram'], collar: ['Button Down', 'Spread'], tail: ['Shorter', 'Longer'], front: ['Full Button Front', 'Pullover'], pocket: ['Yes', 'No'], monogramNote: 'Monogram on pocket' },
      '1883 M SS': { groups: ['Collar', 'Tail', 'Front', 'Pocket', 'Monogram'], collar: ['Spread', 'Button Down'], tail: ['Shorter', 'Longer'], front: ['Full Button Front', 'Pullover'], pocket: ['Yes', 'No'], monogramNote: 'Monogram on pocket' },
      '1883 Band LS': { groups: ['Tail', 'Pocket', 'Monogram'], tail: ['Shorter', 'Longer'], pocket: ['No', 'Yes'], monogramNote: 'Monogram on left cuff' },
      '1883 W LS': { groups: ['Tail', 'Monogram'], tail: ['Shorter', 'Longer'], monogramNote: 'Monogram on left cuff' },
      '1883 W SS': { groups: ['Tail', 'Monogram'], tail: ['Shorter', 'Longer'], monogramNote: 'Monogram on pocket' },
      '1883 OS': { groups: ['Monogram'], monogramNote: 'Monogram on left cuff' },
      'Lyle BD': { groups: ['Collar', 'Monogram'], collar: ['Button Down', 'Spread'], monogramNote: 'Monogram on left cuff' },
      'Lyle SM': { groups: ['Collar', 'Monogram'], collar: ['Spread', 'Button Down'], monogramNote: 'Monogram on left cuff' }
    };
  }

  /**
   * UI copy for monogram placement from the spreadsheet Notes column.
   * @returns {string}
   */
  getMonogramLocationCopy() {
    const note = this.getStyleModelConfig()[this.shirtStyle]?.monogramNote || 'Monogram on left cuff';
    if (/pocket/i.test(note)) {
      return 'Monogram will appear on the pocket top. If no pocket is selected, the monogram will appear on the chest.';
    }
    return 'Monogram will appear on the left cuff.';
  }

  /**
   * @param {'collar'|'cuff'|'pocket'|'tail'|'front'} group
   * @param {string[]|undefined} keys
   * @returns {Array<any>}
   */
  buildOrderedStyleChoices(group, keys) {
    if (!keys || !keys.length) return [];
    const catalog = this.getStyleOptionCatalog()[group] || {};
    return keys.map((key) => {
      const item = catalog[key];
      if (!item) return null;
      return { ...item };
    }).filter(Boolean);
  }

  /**
   * Apply per–Shirt Style option arrays so index 0 = spreadsheet Option 1.
   * @param {ReturnType<ProductCustomizationTable['getStyleModelConfig']>[string]|undefined} config
   */
  applyStyleArraysForShirtStyle(config) {
    this.collarOptions = this.buildOrderedStyleChoices('collar', config?.collar);
    this.cuffOptions = this.buildOrderedStyleChoices('cuff', config?.cuff);
    this.pocketOptions = this.buildOrderedStyleChoices('pocket', config?.pocket);
    this.tailOptions = this.buildOrderedStyleChoices('tail', config?.tail);
    this.frontOptions = this.buildOrderedStyleChoices('front', config?.front);
  }

  /**
   * Find option index by title/key/alias (customer metafields / construction codes).
   * @param {Array<{title?: string, key?: string, aliases?: string[]}>} options
   * @param {unknown} value
   * @returns {number}
   */
  findStyleChoiceIndex(options, value) {
    if (!options || !options.length || value === null || value === undefined) return -1;
    const raw = String(value).trim().toLowerCase();
    if (!raw) return -1;
    return options.findIndex((opt) => {
      const title = String(opt.title || '').toLowerCase();
      const key = String(opt.key || '').toLowerCase();
      const aliases = (opt.aliases || []).map((a) => String(a).toLowerCase());
      return title === raw || key === raw || aliases.includes(raw) ||
        title.includes(raw) || raw.includes(key);
    });
  }

  populateStyleOptions() {
    this.collarOptions = [];
    this.cuffOptions = [];
    this.pocketOptions = [];
    this.tailOptions = [];
    this.frontOptions = [];

    if (!this.shirtStyle) {
      return [];
    }

    const config = this.getStyleModelConfig()[this.shirtStyle];
    if (!config) {
      return [];
    }

    this.applyStyleArraysForShirtStyle(config);

    return (config.groups || []).map(/** @param {string} optionName */ optionName => ({
      name: optionName.toLowerCase(),
      displayName: optionName,
      selected: 0, // Option 1 default (Monogram No = index 0)
      monogram: '',
      options: []
    }));
  }

  createStyleOptionsContent() {
    const summaryItems = this.styleOptions.map((option, index) => {
      let displayText = '';
      const groupLabel = option.displayName || option.name;

      if (option.name === 'monogram') {
        if (option.selected === 0) {
          displayText = 'No monogram';
        } else if (option.selected === 1) {
          displayText = option.monogram
            ? `${option.monogram} monogram`
            : 'Yes monogram';
        }
      } else {
        let selectedOption = null;
        if (option.name === 'collar') selectedOption = this.collarOptions?.[option.selected];
        else if (option.name === 'cuff') selectedOption = this.cuffOptions?.[option.selected];
        else if (option.name === 'pocket') selectedOption = this.pocketOptions?.[option.selected];
        else if (option.name === 'tail') selectedOption = this.tailOptions?.[option.selected];
        else if (option.name === 'front') selectedOption = this.frontOptions?.[option.selected];
        else selectedOption = option.options?.[option.selected];

        if (selectedOption) {
          const title = selectedOption.summaryName || selectedOption.title || selectedOption.name;
          if (title.toLowerCase().includes(groupLabel.toLowerCase())) {
            displayText = title;
          } else {
            displayText = `${title} ${groupLabel}`;
          }
        } else {
          displayText = `Select ${groupLabel}`;
        }
      }

      return `
        <div class="summary-item-container">
          <h2>${displayText}</h2>
          <button class="button--minimal button--action has-text-right" data-edit-index="${index}">
            edit
          </button>
        </div>
      `;
    }).join('');

    return `
      <div class="style-tab">
        <div class="tab-inner-content-container">
          <div class="summary-container inset-mto-contents">
            ${summaryItems}
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            ${this.styleOptions.length > 0 ? `
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="1">
                  next
                </h3>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  // ==========================================
  // EDITOR METHODS
  // ==========================================

  createMonogramEditor() {
    if (this.editingOptionIndex === null) return '';
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option) return '';
    if (typeof option.selected !== 'number') option.selected = 0;
    const selectedClass = option.selected === 0 ? 'selected' : '';
    const selectedClassYes = option.selected === 1 ? 'selected' : '';
    const monogramLocationCopy = this.getMonogramLocationCopy();
    return `
      <div class="style-tab">
        <div class="tab-inner-content-container">
          <div class="tabbed-form-container">
            <div class="tab-nav-container">
              ${this.createStyleNavigationTabs(this.editingOptionIndex)}
            </div>
            <div class="inner-content-container">
              <div>
                <div class="mono-container inset-mto-contents">
                  <div>
                    <h2>add custom monogram?</h2>
                    <h3 class="description-label">optional for an additional $18</h3>
                  </div>
                  <div class="content-container">
                    <div class="left-container">
                      <div class="content-options-container">
                        <div class="content-option is-clickable ${selectedClass}" data-monogram-choice="0">
                          <h3 class="text-auto-width">${option.selected === 0 ? 'selected' : 'select'}</h3>
                          <h2 class="title mb-0">no</h2>
                        </div>
                        <div class="content-option is-clickable ${selectedClassYes}" data-monogram-choice="1">
                          <h3 class="text-auto-width">${option.selected === 1 ? 'selected' : 'select'}</h3>
                          <h2 class="title mb-0">yes</h2>
                        </div>
                      </div>
                      <div class="input-container">
                        <input
                          type="text"
                          placeholder="initials"
                          maxlength="3"
                          minlength="3"
                          class="initial-input"
                          data-monogram-input
                          value="${option.monogram || ''}"
                          ${option.selected !== 1 ? 'disabled' : ''}
                        >
                        <p class="field-error" id="monogram-error-message" style="display: none;">Monogram must be 3 letters</p>
                        <p class="mt-3 is-hidden-desktop location-copy">
                          ${monogramLocationCopy}
                        </p>
                      </div>
                    </div>
                    <div class="right-container">
                      <div class="white-box-constrainer">
                        <div class="white-box">
                          <div class="white-box-content">
                            <div class="img-container">
                              <img class="img" src="${window.CUSTOMIZATION_IMAGES?.monogramImage || ''}" alt="Monogram preview">
                            </div>
                          </div>
                        </div>
                      </div>
                      <p class="mt-3 is-hidden-mobile location-copy">
                        ${monogramLocationCopy}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-back-from-edit>
              previous
            </h3>
            ${this.styleOptions.length > 0 ? `
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="1">
                  next
                </h3>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  createCollarEditor() {
    if (this.editingOptionIndex === null) return '';
    if (!this.collarOptions?.length) return '';

    const option = this.styleOptions[this.editingOptionIndex];
    if (!option) return '';
    if (typeof option.selected !== "number" || option.selected < 0 || option.selected >= this.collarOptions.length) {
      option.selected = 0;
    }
    const collars = this.collarOptions;

    return `
      <div class="style-tab">
        <div class="tab-inner-content-container">
          <div class="tabbed-form-container">
            <div class="tab-nav-container">
              ${this.createStyleNavigationTabs(this.editingOptionIndex)}
            </div>
            <div class="inner-content-container">
              <div class="component-container inset-mto-contents">
                <div class="collar-options-container">
                  ${collars.map((collar, i) => `
                    <div class="collar-option is-clickable ${option.selected === i ? 'selected' : ''}" data-collar-index="${i}">
                      <h3 class="text-auto-width select-label-desktop">${option.selected === i ? 'selected' : 'select'}</h3>
                      ${collar.image ? `<img src="${collar.image}" class="img" alt="${collar.title}">` : ''}
                      <div class="text-container">
                        <div class="title-container-desktop">
                          <h2 class="title mb-0">${collar.title}</h2>
                        </div>
                        <h3 class="text-auto-width select-label-mobile">${option.selected === i ? 'selected' : 'select'}</h3>
                      </div>
                    </div>
                  `).join('')}
                </div>
                <p class="pt-5 pb-0 mb-0">${collars[option.selected]?.description || ''}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-back-from-edit>
              previous
            </h3>
            ${this.styleOptions.length > 0 ? `
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="1">
                  next
                </h3>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  createCuffEditor() {
    if (this.editingOptionIndex === null) return '';
    if (!this.cuffOptions?.length) return '';

    const option = this.styleOptions[this.editingOptionIndex];
    if (!option) return '';
    if (typeof option.selected !== "number" || option.selected < 0 || option.selected >= this.cuffOptions.length) {
      option.selected = 0;
    }
    const cuffs = this.cuffOptions;

    return `
      <div class="style-tab">
        <div class="tab-inner-content-container">
          <div class="tabbed-form-container">
            <div class="tab-nav-container">
              ${this.createStyleNavigationTabs(this.editingOptionIndex)}
            </div>
            <div class="inner-content-container">
              <div class="component-container inset-mto-contents">
                <div class="cuff-options-container collar-options-container">
                  ${cuffs.map((cuff, i) => `
                    <div class="cuff-option collar-option is-clickable ${option.selected === i ? 'selected' : ''}" data-cuff-index="${i}">
                      <h3 class="text-auto-width select-label-desktop">${option.selected === i ? 'selected' : 'select'}</h3>
                      <img src="${cuff.image}" class="img" alt="${cuff.title}">
                      <div class="text-container">
                        <div class="title-container-desktop">
                          <h2 class="title mb-0">${cuff.title}</h2>
                        </div>
                        <h3 class="text-auto-width select-label-mobile">${option.selected === i ? 'selected' : 'select'}</h3>
                      </div>
                    </div>
                  `).join('')}
                </div>
                <p class="pt-5 pb-0 mb-0">${cuffs[option.selected]?.description || ''}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-back-from-edit>
              previous
            </h3>
            ${this.styleOptions.length > 0 ? `
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="1">
                  next
                </h3>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  createPocketEditor() {
    if (this.editingOptionIndex === null) return '';
    if (!this.pocketOptions?.length) return '';

    const option = this.styleOptions[this.editingOptionIndex];
    if (!option) return '';
    if (typeof option.selected !== "number" || option.selected < 0 || option.selected >= this.pocketOptions.length) {
      option.selected = 0;
    }
    const selectedIndex = option.selected;
    const pockets = this.pocketOptions;

    return `
      <div class="style-tab">
        <div class="tab-inner-content-container">
          <div class="tabbed-form-container">
            <div class="tab-nav-container">
              ${this.createStyleNavigationTabs(this.editingOptionIndex)}
            </div>
            <div class="inner-content-container">
              <div>
                <div class="component-container inset-mto-contents">
                  <div class="collar-options-container pocket-options-container">
                    ${pockets.map((pocket, index) => `
                      <div 
                        class="collar-option is-clickable ${selectedIndex === index ? "selected" : ""}" 
                        data-pocket-index="${index}">
                        <h3 class="text-auto-width select-label-desktop">
                          ${selectedIndex === index ? "selected" : "select"}
                        </h3>
                        ${pocket.image ? `<img src="${pocket.image}" class="img">` : ""}
                        <div class="text-container">
                          <div class="title-container-desktop">
                            <h2 class="title mb-0">${pocket.title}</h2>
                          </div>
                          <h3 class="text-auto-width select-label-mobile">
                            ${selectedIndex === index ? "selected" : "select"}
                          </h3>
                        </div>
                      </div>
                    `).join("")}
                  </div>
                  <p class="pt-5 pb-0 mb-0">${pockets[selectedIndex]?.description || ''}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-back-from-edit>
              previous
            </h3>
            ${this.styleOptions.length > 0 ? `
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="1">
                  next
                </h3>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  createTailEditor() {
    if (this.editingOptionIndex === null) return '';
    if (!this.tailOptions?.length) return '';

    const option = this.styleOptions[this.editingOptionIndex];
    if (!option) return '';
    if (typeof option.selected !== "number" || option.selected < 0 || option.selected >= this.tailOptions.length) {
      option.selected = 0;
    }
    const selectedIndex = option.selected;
    const tails = this.tailOptions;

    return `
      <div class="style-tab">
        <div class="tab-inner-content-container">
          <div class="tabbed-form-container">
            <div class="tab-nav-container">
              ${this.createStyleNavigationTabs(this.editingOptionIndex)}
            </div>
            <div class="inner-content-container">
              <div>
                <div class="component-container inset-mto-contents">
                  <div class="tail-options-container collar-options-container">
                    ${tails.map((tail, index) => `
                      <div 
                        class="tail-option collar-option is-clickable ${selectedIndex === index ? "selected" : ""}" 
                        data-tail-index="${index}">
                        <h3 class="text-auto-width select-label-desktop">
                          ${selectedIndex === index ? "selected" : "select"}
                        </h3>
                        ${tail.image ? `<img src="${tail.image}" class="img" alt="${tail.title}">` : ""}
                        <div class="text-container">
                          <div class="title-container-desktop">
                            <h2 class="title mb-0">${tail.title}</h2>
                          </div>
                          <h3 class="text-auto-width select-label-mobile">
                            ${selectedIndex === index ? "selected" : "select"}
                          </h3>
                        </div>
                      </div>
                    `).join("")}
                  </div>
                  <p class="pt-5 pb-0 mb-0">${tails[selectedIndex]?.description || ''}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-back-from-edit>
              previous
            </h3>
            ${this.styleOptions.length > 0 ? `
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="1">
                  next
                </h3>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * @param {number} index
   */
  handleTailChoice(index) {
    if (this.editingOptionIndex === null) return;
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option || option.name !== 'tail') return;

    option.selected = index;

    const contentContainer = this.productDetailsContainer?.querySelector('.content-container');
    if (contentContainer) {
      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();
    }
  }

  createFrontEditor() {
    if (this.editingOptionIndex === null) return '';
    if (!this.frontOptions?.length) return '';

    const option = this.styleOptions[this.editingOptionIndex];
    if (!option) return '';
    if (typeof option.selected !== 'number' || option.selected < 0 || option.selected >= this.frontOptions.length) {
      option.selected = 0;
    }
    const selectedIndex = option.selected;
    const fronts = this.frontOptions;

    return `
      <div class="style-tab">
        <div class="tab-inner-content-container">
          <div class="tabbed-form-container">
            <div class="tab-nav-container">
              ${this.createStyleNavigationTabs(this.editingOptionIndex)}
            </div>
            <div class="inner-content-container">
              <div>
                <div class="component-container inset-mto-contents">
                  <div class="front-options-container collar-options-container">
                    ${fronts.map((front, index) => `
                      <div
                        class="front-option collar-option is-clickable ${selectedIndex === index ? 'selected' : ''}"
                        data-front-index="${index}">
                        <h3 class="text-auto-width select-label-desktop">
                          ${selectedIndex === index ? 'selected' : 'select'}
                        </h3>
                        ${front.image ? `<img src="${front.image}" class="img" alt="${front.title}">` : ''}
                        <div class="text-container">
                          <div class="title-container-desktop">
                            <h2 class="title mb-0">${front.title}</h2>
                          </div>
                          <h3 class="text-auto-width select-label-mobile">
                            ${selectedIndex === index ? 'selected' : 'select'}
                          </h3>
                        </div>
                      </div>
                    `).join('')}
                  </div>
                  <p class="pt-5 pb-0 mb-0">${fronts[selectedIndex]?.description || ''}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-back-from-edit>
              previous
            </h3>
            ${this.styleOptions.length > 0 ? `
              <div class="next-container">
                <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="1">
                  next
                </h3>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * @param {number} index
   */
  handleFrontChoice(index) {
    if (this.editingOptionIndex === null) return;
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option || option.name !== 'front') return;

    option.selected = index;

    const contentContainer = this.productDetailsContainer?.querySelector('.content-container');
    if (contentContainer) {
      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();
    }
  }

  /**
   * @param {number} currentEditingIndex
   * @returns {string}
   */
  createStyleNavigationTabs(currentEditingIndex) {
    if (!this.styleOptions || this.styleOptions.length === 0) {
      return '';
    }

    return this.styleOptions.map((option, index) => {
      const isActive = index === currentEditingIndex ? 'active-tab' : '';
      let displayName = option.displayName || option.name;
      if (!option.displayName) {
        displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1).toLowerCase();
      }
      return `<h3 class="button--action has-text-left text-auto-width ${isActive}" data-edit-index="${index}">${displayName}</h3>`;
    }).join('');
  }

  // ==========================================
  // FIT TAB METHODS
  // ==========================================

  createFitTab() {
    /**
     * @param {string} fitName
     * @returns {string}
     */
    const getCurrentFitValue = (fitName) => {
      if (window.cachedSelections && window.cachedSelections.fit) {
        const existing = window.cachedSelections.fit.find(f => f.name === fitName);
        if (existing && existing.value && existing.value !== 'None') {
          if (this.isLastOrderSentinel(existing.value)) {
            return 'As Last';
          }
          return existing.value;
        }
      }
      return 'None';
    };

    const getMostRecentValue = (fitName) => {
      if (this.customerData && this.customerData.metafields) {
        const raw = this.customerData.metafields[fitName];
        if (raw !== null && raw !== undefined && raw !== '') {
          let value = String(raw).replace(/\.0$/, '');
          if (fitName === 'collar') value = this.convertDecimalToCollarFraction(value);
          if (fitName === 'fit') value = value.toLowerCase();
          return value || null;
        }
      }
      return null;
    };

    const fitNames = Object.keys(this.fitOptions);

    /** @type {Record<string, {text: string|((currentFitValue: string) => string), img: string}>} */
    const extraContent = {
      collar: {
        text: '<p class="mt-4 txt">Take a skin measurement of your neck & add 0.75" for comfort.',
        img: window.CUSTOMIZATION_IMAGES?.fitCollarImage || ''
      },
      chest: {
        text: "<p class='mt-4 txt'>Your chest measurement or coat size.</p>",
        img: window.CUSTOMIZATION_IMAGES?.fitChestImage || ''
      },
      fit: {
        text: (currentFitValue) => `
          <div class="fit-type-selector mt-0">
            <div class="fit-type-options p-0">
              <div class="fit-type-column fit-type-option ${currentFitValue === 'regular' ? 'selected' : ''}" data-fit-type="regular">
                <h2 class="fit-type-heading">REGULAR</h2>
                <div class="fit-type-divider"></div>
              </div>
              <div class="fit-type-column fit-type-option ${currentFitValue === 'trim' ? 'selected' : ''}" data-fit-type="trim">
                <h2 class="fit-type-heading">TRIM</h2>
                <div class="fit-type-divider"></div>
              </div>
            </div>
            <p class="mt-4 txt">Regular is a tailored fit with chest and sleeves cut closer to the body, along with a comfortable waist. Trim is a trimmer fit in the body and sleeves. Compared to Regular Fit, Trim Fit is:</p>
            <ul class="fit-list">
              <li>Trimmer at the chest by 1"</li>
              <li>Slimmer across the shoulders by 1"</li>
              <li>Trimmer at the waist by 2" on most sizes</li>
              <li>Higher armhole</li>
              <li>Slimmer sleeve</li>
              <li>Shorter in length by 1"</li>
            </ul>
          </div>
        `,
        img: window.CUSTOMIZATION_IMAGES?.fitFitImage || ''
      },
      sleeve: {
        text: "<p class='mt-4 txt'>Measure from the back of your neck down to the knuckle of your thumb and subtract 1.</p>",
        img: window.CUSTOMIZATION_IMAGES?.fitSleeveImage || ''
      }
    };

    const tabNavItems = fitNames.map((name, index) => `
      <h3 class="vertical-tab-nav-item button--action has-text-left text-auto-width ${index === 0 ? 'active' : ''}" data-tab-index="${index}">
        ${name}
      </h3>
    `).join('');

    const tabPanels = fitNames.map((name, index) => {
      const extra = extraContent[name] || { text: '', img: '' };
      const currentValue = getCurrentFitValue(name);
      const displayValue = (currentValue === 'None' || !currentValue) ? '' : currentValue;

      if (name === 'fit') {
        const fitText = typeof extra.text === 'function' ? extra.text(currentValue.toLowerCase()) : extra.text;
        const fitMostRecent = getMostRecentValue(name);
        return `
          <div class="vertical-tab-panel ${index === 0 ? 'active' : ''}" data-tab-index="${index}">
            <div class="fit-data-container">
              <div class="fit-item" data-fit-name="${name}" data-fit-value="${currentValue}">
                <input
                  type="hidden"
                  data-fit-input="${name}"
                  value="${currentValue}"
                />
              </div>
              ${fitMostRecent ? `<p class="use-most-recent-link button--minimal is-clickable" data-use-recent-fit="${name}" data-recent-val="${fitMostRecent}">Use last order: ${fitMostRecent}</p>` : ''}
              ${fitText ? `<div>${fitText}</div>` : ''}
              ${extra.img ? `<img src="${extra.img}" alt="${name}" class="fit-extra-image">` : ''}
            </div>
          </div>
        `;
      }

      const fitOptionsArray = this.fitOptions[name];
      if (!fitOptionsArray) {
        return '';
      }

      const mostRecentVal = getMostRecentValue(name);

      return `
        <div class="vertical-tab-panel ${index === 0 ? 'active' : ''}" data-tab-index="${index}">
          <div class="fit-data-container mb-6">
            <div class="fit-item" data-fit-name="${name}" data-fit-value="${currentValue}">
              <input
                type="text"
                class="fit-item-input"
                value="${displayValue}"
                placeholder="SIZE"
                readonly
                data-fit-input="${name}"
              />
              <h3>${name}</h3>
              <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 67.598 35.997" width="15" height="15" class="arrow"><path d="M0 2.2L2.2 0l31.6 31.597L65.398 0l2.2 2.2L33.8 35.997 0 2.2z"></path></svg>
              <div class="fit-menu row-items" style="display:none">
                ${fitOptionsArray.map(/** @param {string} option */ option => `
                  <div>
                    <div class="custom-dropdown-option">
                      <p class="mb-0"><span>${option}</span></p>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
            ${mostRecentVal ? `<p class="use-most-recent-link button--minimal is-clickable" data-use-recent-fit="${name}" data-recent-val="${mostRecentVal}">Use last order: ${mostRecentVal}</p>` : ''}
            ${extra.text ? `<div>${extra.text}</div>` : ''}
            ${extra.img ? `<img src="${extra.img}" alt="${name}" class="fit-extra-image">` : ''}
          </div>
        </div>
      `;
    }).join('');

    const html = `
      <div class="fit-tab vertical-tabs">
        <div class="tab-inner-content-container">
          <div class="inset-mto-contents">
            <div class="vertical-tab-wrapper">
              <div class="vertical-tab-nav">
                ${tabNavItems}
              </div>
              <div class="vertical-tab-content">
                ${tabPanels}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    setTimeout(() => {
      const fitTab = document.querySelector('.fit-tab');
      if (!fitTab) return;
      const tabNavItems = fitTab.querySelectorAll('.vertical-tab-nav-item');
      const tabPanels = fitTab.querySelectorAll('.vertical-tab-panel');
      tabNavItems.forEach(item => {
        if (item instanceof HTMLElement) {
          item.addEventListener('click', () => {
            const index = item.dataset.tabIndex;
            tabNavItems.forEach(nav => {
              if (nav instanceof HTMLElement) nav.classList.remove('active');
            });
            tabPanels.forEach(panel => {
              if (panel instanceof HTMLElement) panel.classList.remove('active');
            });
            item.classList.add('active');
            const panel = fitTab.querySelector(`.vertical-tab-panel[data-tab-index="${index}"]`);
            if (panel instanceof HTMLElement) {
              panel.classList.add('active');
            }
          });
        }
      });

      fitTab.querySelectorAll('[data-use-recent-fit]').forEach(btn => {
        if (!(btn instanceof HTMLElement)) return;
        btn.addEventListener('click', () => {
          const fitName = btn.dataset.useRecentFit;
          const value = btn.dataset.recentVal;
          if (!fitName || !value) return;

          if (fitName === 'fit') {
            const fitTypeOption = fitTab.querySelector(`.fit-type-option[data-fit-type="${value}"]`);
            if (fitTypeOption instanceof HTMLElement) fitTypeOption.click();
          } else {
            const input = fitTab.querySelector(`[data-fit-input="${fitName}"]`);
            const fitItem = fitTab.querySelector(`.fit-item[data-fit-name="${fitName}"]`);
            if (input instanceof HTMLInputElement) {
              input.value = value;
              if (fitItem instanceof HTMLElement) fitItem.dataset.fitValue = value;
              if (!window.cachedSelections) window.cachedSelections = { fit: [], style: [] };
              const existing = window.cachedSelections.fit.find(f => f.name === fitName);
              if (existing) {
                existing.value = value;
              } else {
                window.cachedSelections.fit.push({ name: fitName, value });
              }
              this.updateSummaryTabState();
            }
          }
        });
      });
    }, 50);

    return html;
  }

  createFitPathSelection() {
    const arrow = `<svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35.997 67.598" width="25" height="25"><path fill="#636b6c" d="M2.2 67.598l-2.2-2.2 31.597-31.6L0 2.2 2.2 0l33.797 33.798L2.2 67.598z"></path></svg>`;
    const sp = this.selectedPath;
    const nextBtn = sp ? `<h3 class="button--action button--minimal mb-0 large-next" data-next-tab="2">next</h3>` : '';
    return `
      <div class="fit-tab">
        <div class="tab-inner-content-container">
          <div class="tab-inner-content-container">
            <div class="welcome-header mb-6">
              <h1 class="mb-2">Welcome</h1>
              <p class="subtitle">Let's determine your perfect fit.</p>
            </div>
            <div class="option-container is-clickable" data-path-option="previous">
              <h2 class="mb-0">Use My Last Order Measurements</h2>
              ${arrow}
            </div>
            <div class="option-container is-clickable" data-path-option="manual">
              <h2 class="mb-0">Enter new measurements</h2>
              ${arrow}
            </div>
            <div class="option-container is-clickable" data-path-option="tryon">
              <h2 class="mb-0">Request Try-On Shirts</h2>
              ${arrow}
            </div>
            <div class="option-container is-clickable" data-path-option="bespoke">
              <h2 class="mb-0">Bespoke Options</h2>
              ${arrow}
            </div>
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-prev-tab="0">
              previous
            </h3>
            <div class="next-container">
              ${nextBtn}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // ==========================================
  // SUMMARY METHODS
  // ==========================================

  renderSummaryContent(useMostRecent = false) {
    const summaryDetails = this.productDetailsContainer?.querySelector('.summary-details') ||
      document.querySelector('.summary-details');
    if (!summaryDetails) {
      return;
    }

    let styleItems = '';
    if (this.styleOptions && this.styleOptions.length > 0) {
      this.styleOptions.forEach((option, index) => {
        let displayValue = '';

        if (option.name === 'monogram') {
          if (option.selected === 0) {
            displayValue = 'NO MONOGRAM';
          } else if (option.selected === 1) {
            displayValue = option.monogram ? `${option.monogram.toUpperCase()} MONOGRAM` : 'MONOGRAM';
          }
        } else if (option.selected !== -1 && option.options && option.options[option.selected]) {
          const selectedOption = option.options[option.selected];
          displayValue = (selectedOption.summaryName || selectedOption.name || selectedOption.title).toUpperCase();
        } else if (option.selected !== -1) {
          if (option.name === 'collar' && this.collarOptions && this.collarOptions[option.selected]) {
            const selectedCollar = this.collarOptions[option.selected];
            if (selectedCollar) {
              displayValue = selectedCollar.title.toUpperCase();
            }
          } else if (option.name === 'cuff' && this.cuffOptions && this.cuffOptions[option.selected]) {
            const selectedCuff = this.cuffOptions[option.selected];
            if (selectedCuff) {
              displayValue = selectedCuff.title.toUpperCase();
            }
          } else if (option.name === 'pocket' && this.pocketOptions && this.pocketOptions[option.selected]) {
            const selectedPocket = this.pocketOptions[option.selected];
            if (selectedPocket) {
              displayValue = selectedPocket.title.toUpperCase();
            }
          } else if (option.name === 'tail' && this.tailOptions && this.tailOptions[option.selected]) {
            const selectedTail = this.tailOptions[option.selected];
            if (selectedTail) {
              displayValue = selectedTail.title.toUpperCase();
            }
          } else if (option.name === 'front' && this.frontOptions && this.frontOptions[option.selected]) {
            const selectedFront = this.frontOptions[option.selected];
            if (selectedFront) {
              displayValue = selectedFront.title.toUpperCase();
            }
          }
        }

        if (!displayValue) {
          displayValue = 'Not selected';
        }

        styleItems += `
          <div class="summary-row">
            <h2 class="summary-value">${displayValue}</h2>
            <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="24" class="summary-edit-icon" data-edit-style="${index}">
              <path d="M0 0h24v24H0z" fill="none"></path>
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
            </svg>
          </div>
        `;
      });
    }

    let fitItems = '';
    const fitOrder = ['collar', 'chest', 'fit', 'sleeve'];

    fitOrder.forEach(fitName => {
      let displayValue = 'Not selected';
      const isFieldMostRecent = this.fitMostRecentFields.has(fitName);

      if (isFieldMostRecent) {
        displayValue = 'As Last';
      } else if (window.cachedSelections && window.cachedSelections.fit) {
        const fitItem = window.cachedSelections.fit.find(f => f.name === fitName);
        if (fitItem && fitItem.value) {
          if (fitName === 'collar') {
            const collarValue = this.convertDecimalToCollarFraction(fitItem.value);
            displayValue = `${collarValue}" COLLAR`;
          } else if (fitName === 'chest') {
            displayValue = `${fitItem.value}" CHEST`;
          } else if (fitName === 'fit') {
            displayValue = `${fitItem.value.toUpperCase()} FIT`;
          } else if (fitName === 'sleeve') {
            displayValue = `${fitItem.value}" SLEEVE`;
          }
        }
      }

      const fitLabel = fitName.toUpperCase();
      fitItems += `
        <div class="summary-row">
          <div class="summary-value-wrapper">
            ${isFieldMostRecent ? `<span class="summary-fit-label">${fitLabel}</span>` : ''}
            <h2 class="summary-value">${displayValue}</h2>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="24" class="summary-edit-icon" data-edit-fit="${fitName}">
            <path d="M0 0h24v24H0z" fill="none"></path>
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
          </svg>
        </div>
      `;
    });

    summaryDetails.innerHTML = `
      <div class="summary-grid">
        <div class="summary-column">
          <h3 class="summary-heading">STYLE</h3>
          ${styleItems}
        </div>
        <div class="summary-column">
          <h3 class="summary-heading">FIT</h3>
          ${fitItems}
        </div>
      </div>
    `;
  }

  createFitOverview() {
    /**
     * @param {string} fitName
     * @returns {string}
     */
    const getFitValue = (fitName) => {
      if (this.fitMostRecentFields.has(fitName)) return 'As Last';
      if (window.cachedSelections && window.cachedSelections.fit) {
        const fitItem = window.cachedSelections.fit.find(f => f.name === fitName);
        if (fitItem && fitItem.value) {
          if (this.isLastOrderSentinel(fitItem.value)) {
            return 'As Last';
          }
          if (fitName === 'collar') {
            return this.convertDecimalToCollarFraction(fitItem.value);
          }
          return fitItem.value;
        }
      }
      return 'None';
    };

    const fitOrder = ['collar', 'chest', 'fit', 'sleeve'];

    const col1Items = fitOrder.slice(0, 2).map(/** @param {string} fitName */ fitName => {
      const fitValue = getFitValue(fitName);
      const fitNameDisplay = fitName.charAt(0).toUpperCase() + fitName.slice(1);
      return `
        <div class="fit-item is-clickable">
          <h2 class="fit-item-value">${fitValue}</h2>
          <h3>${fitNameDisplay}</h3>
          <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="24" class="edit-button" data-edit-fit-overview="${fitName}">
            <path d="M0 0h24v24H0z" fill="none"></path>
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
          </svg>
        </div>
      `;
    }).join('');

    const col2Items = fitOrder.slice(2, 4).map(/** @param {string} fitName */ fitName => {
      const fitValue = getFitValue(fitName);
      const fitNameDisplay = fitName.charAt(0).toUpperCase() + fitName.slice(1);
      return `
        <div class="fit-item is-clickable">
          <h2 class="fit-item-value">${fitValue}</h2>
          <h3>${fitNameDisplay}</h3>
          <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="24" class="edit-button" data-edit-fit-overview="${fitName}">
            <path d="M0 0h24v24H0z" fill="none"></path>
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
          </svg>
        </div>
      `;
    }).join('');

    return `
      <div class="fit-overview-tab">
        <div class="summary-tab-container inset-mto-contents">
          <div class="welcome-header mb-6">
            <h1 class="mb-3">WELCOME BACK</h1>
            <p class="subtitle">Here is your current Made-To-Order fit profile:</p>
          </div>
          
          <div class="fit-data-container">
            ${col1Items}
            ${col2Items}
          </div>
        </div>
        <div class="footer-nav inset-mto-contents">
          <h3 class="button--action button--minimal close-mto-button">Exit</h3>
          <div class="prev-next-nav-container">
            <h3 class="button--action button--minimal mb-0" data-prev-tab="0">
              previous
            </h3>
            <div class="next-container">
              <h3 class="button--action button--minimal mb-0 large-next" data-next-tab="2">
                next
              </h3>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * @returns {boolean}
   */
  isCustomizerUserLoggedIn() {
    return !!(
      window.ShopifyUtils?.isCustomerLoggedIn?.() ||
      window.CUSTOMER_LOGGED_IN === true ||
      window.isCustomerLoggedIn === true ||
      String(window.isCustomerLoggedIn) === 'true'
    );
  }

  /**
   * True when hamilton_customization localStorage has fit data (created on add to cart).
   * Same rule for logged-in and guest users — account metafields do not affect fit landing.
   * @returns {boolean}
   */
  hasSavedFitData() {
    const data = this.loadCustomizationFromLocalStorage();
    if (!data?.fit) return false;

    const fitFields = ['collar', 'chest', 'fit', 'sleeve'];
    return fitFields.some((name) => {
      const value = data.fit[name];
      return value && value !== 'None';
    });
  }

  /**
   * Summary stays disabled until fit has values (collar, chest, fit, sleeve),
   * or the user chose As Last / try-on / bespoke. Does not change path workflows.
   * @returns {boolean}
   */
  isFitReadyForSummary() {
    // Existing path options that intentionally leave Fit for Summary
    if (this.selectedPath === 'previous' || this.selectedPath === 'tryon' || this.selectedPath === 'bespoke') {
      return true;
    }

    return ['collar', 'chest', 'fit', 'sleeve'].every((name) => {
      if (this.fitMostRecentFields.has(name)) return true;
      const cached = window.cachedSelections?.fit?.find((f) => f.name === name)?.value;
      const input = document.querySelector(`[data-fit-input="${name}"]`);
      const fromInput = input instanceof HTMLInputElement ? input.value.trim() : '';
      const value = String(fromInput || cached || '').trim();
      return value !== '' && value !== 'None';
    });
  }

  /** Enable/disable the Summary tab based on whether fit has values. */
  updateSummaryTabState() {
    const summaryTab = this.productDetailsContainer?.querySelector('.tab.button--action[data-tab="2"]');
    if (!(summaryTab instanceof HTMLElement)) return;

    if (this.isFitReadyForSummary()) {
      summaryTab.classList.remove('disabled');
    } else {
      summaryTab.classList.add('disabled');
      summaryTab.classList.remove('active');
    }
  }

  /**
   * Fit tab landing: Welcome Back when localStorage exists, otherwise four-option screen.
   * @returns {string}
   */
  renderFitTabContent() {
    if (this.hasSavedFitData()) {
      this.populateCachedSelectionsFromLocalStorage();
    }
    return this.hasSavedFitData() ? this.createFitOverview() : this.createFitPathSelection();
  }

  // ==========================================
  // TAB NAVIGATION METHODS
  // ==========================================

  /**
   * @param {number} tabIndex
   */
  async switchTab(tabIndex) {
    if (!this.productDetailsContainer) return;

    // Handle returning to landing screen
    if (tabIndex === -1) {
      this.activeTab = -1;
      this.selectedPath = null;

      const tabContainer = this.productDetailsContainer.querySelector('.tab-container');
      if (tabContainer && tabContainer instanceof HTMLElement) {
        tabContainer.style.display = 'none';
      }

      const contentContainer = this.productDetailsContainer.querySelector('.content-container');
      if (contentContainer && contentContainer instanceof HTMLElement) {
        contentContainer.innerHTML = this.createTabContent();
        this.setupCustomizationListeners();
      }

      this.updateAddToCartButtonVisibility();
      const externalBtn = this.productDetailsContainer.querySelector('#add-to-cart-with-api');
      if (externalBtn && externalBtn instanceof HTMLElement) externalBtn.style.display = 'none';
      return;
    }

    // Keep Summary locked when fit has no values
    if (tabIndex === 2 && !this.isFitReadyForSummary()) {
      return this.switchTab(1);
    }

    // Monogram Yes requires 3 initials before leaving Style for Fit/Summary
    if ((tabIndex === 1 || tabIndex === 2) && this.activeTab === 0) {
      if (!this.validateMonogram()) {
        return;
      }
    }

    this.activeTab = tabIndex;

    // Fit tab landing uses hamilton_customization localStorage only (see renderFitTabContent)
    if (tabIndex === 1) {
      this.populateCachedSelectionsFromLocalStorage();
    }

    const tabs = this.productDetailsContainer.querySelectorAll('.tab.button--action');
    tabs.forEach((tab, index) => {
      if (tab instanceof HTMLElement) {
        if (index === tabIndex) {
          tab.classList.add('active');
        } else {
          tab.classList.remove('active');
        }
      }
    });
    this.updateSummaryTabState();

    // Show tab-container and update its class
    const tabContainer = this.productDetailsContainer.querySelector('.tab-container');
    if (tabContainer && tabContainer instanceof HTMLElement) {
      tabContainer.style.display = '';
      tabContainer.className = `tab-container tab-${['one', 'two', 'three'][tabIndex]}`;
    }

    this.updateAddToCartButtonVisibility();

    const contentContainer = this.productDetailsContainer.querySelector('.content-container');
    if (contentContainer && contentContainer instanceof HTMLElement) {
      if (tabIndex === 1) {
        // Snapshot the server-loaded values on first visit so "most recent" always restores the true baseline
        if (this.fitMetafieldBaseline.length === 0 && window.cachedSelections?.fit?.length) {
          this.fitMetafieldBaseline = window.cachedSelections.fit
            .filter(f => f && f.value && f.value !== 'None')
            .map(f => ({ name: f.name, value: f.value }));
        }
        // Preserve selectedPath and fitMostRecentFields — cleared only when user picks a different path
        contentContainer.innerHTML = this.renderFitTabContent();
        this.setupCustomizationListeners();
        return;
      }

      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();

      if (tabIndex === 2) {
        if (this.selectedPath === 'previous') {
          // Only initialise all fields to most-recent the first time we arrive here.
          // If the user already changed individual fields, keep those choices.
          if (this.fitMostRecentFields.size === 0) {
            this.fitMostRecentFields = new Set(['collar', 'chest', 'fit', 'sleeve']);
          }
          // Restore cache only for fields still in most-recent mode (not ones the user changed)
          this.fitMostRecentFields.forEach(f => this.restoreMetafieldValueToCache(f));
          this.renderSummaryContent();
        } else if (this.selectedPath === 'manual' || !this.selectedPath) {
          this.renderSummaryContent();
        }
      }
      // Try-On / Bespoke are informational — hide ATC; As Last / manual show it
      this.updateAddToCartButtonVisibility();
    }
  }

  // ==========================================
  // EVENT HANDLERS & SETUP METHODS
  // ==========================================

  setupCustomizationListeners() {
    if (!this.productDetailsContainer) {
      return;
    }

    const exitBtn = this.productDetailsContainer.querySelector('.close-mto-button');
    if (exitBtn && exitBtn instanceof HTMLElement) {
      exitBtn.addEventListener('click', () => this.restoreOriginalContent());
    }

    // Main style / fit / summary tabs live outside .content-container; they persist across innerHTML updates.
    // Delegate so setupCustomizationListeners() can run repeatedly without stacking listeners.
    const customizationContainer = this.productDetailsContainer.querySelector('.customization-container');
    if (customizationContainer instanceof HTMLElement && !customizationContainer.dataset.mainTabDelegation) {
      customizationContainer.dataset.mainTabDelegation = 'true';
      customizationContainer.addEventListener('click', (e) => {
        const tab =
          e.target instanceof Element ? e.target.closest('.tab.button--action') : null;
        if (!(tab instanceof HTMLElement) || !customizationContainer.contains(tab)) return;
        if (tab.classList.contains('disabled')) return;
        e.preventDefault();
        const tabIndex = parseInt(tab.dataset.tab || '0', 10);
        this.switchTab(tabIndex);
      });
    }

    // Landing screen path option handlers
    const pathOptions = this.productDetailsContainer.querySelectorAll('[data-path-option]');
    pathOptions.forEach(option => {
      if (option instanceof HTMLElement) {
        option.addEventListener('click', () => {
          const path = option.dataset.pathOption;
          this.selectedPath = path || null;

          // Selecting any path other than "previous" exits "most recent" mode
          if (path !== 'previous') {
            this.fitMostRecentFields = new Set();
          }

          const tabContainer = this.productDetailsContainer ? this.productDetailsContainer.querySelector('.tab-container') : null;
          if (tabContainer && tabContainer instanceof HTMLElement) {
            tabContainer.style.display = '';
          }

          if (path === 'manual') {
            this.sanitizeFitCacheForManualPath();
            this.selectedPath = 'manual';
            this.updateSummaryTabState();
            const contentContainer = this.productDetailsContainer?.querySelector('.content-container');
            if (contentContainer && contentContainer instanceof HTMLElement) {
              contentContainer.innerHTML = this.createTabContent();
              this.setupCustomizationListeners();
            }
          } else {
            // previous / tryon / bespoke: go directly to Summary tab
            this.updateSummaryTabState();
            this.switchTab(2);
          }
        });
      }
    });

    const nextButtons = this.productDetailsContainer.querySelectorAll('[data-next-tab]');
    nextButtons.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const nextTab = parseInt(btn.dataset.nextTab || '0');

          // Style editor (collar, cuff, etc.): next returns to style summary
          if (this.activeTab === 0 && this.editingOptionIndex !== null && nextTab === 1) {
            if (!this.validateMonogram()) {
              return;
            }
            this.stopEditingOption();
            return;
          }

          // Style summary: validate monogram, then go to Fit tab
          if (this.activeTab === 0 && nextTab === 1) {
            if (!this.validateMonogram()) {
              return;
            }
          }

          if (this.activeTab === 1 && nextTab === 2) {
            const fitContentView = document.querySelector('.fit-content-view');
            if (fitContentView && fitContentView instanceof HTMLElement && fitContentView.style.display !== 'none') {
              const activeVerticalTab = document.querySelector('.vertical-tab-nav-item.active');
              const allVerticalTabs = Array.from(document.querySelectorAll('.vertical-tab-nav-item'));
              const currentIndex = activeVerticalTab ? allVerticalTabs.indexOf(activeVerticalTab) : -1;

              const requiredFields = ['collar', 'chest', 'fit', 'sleeve'];
              const currentFieldName = requiredFields[currentIndex];
              const currentInput = document.querySelector(`[data-fit-input="${currentFieldName}"]`);

              if (currentInput) {
                if (currentInput instanceof HTMLInputElement) {
                  const value = currentInput.value.trim();
                  if (!value || value === '' || value === 'None') {
                    if (currentFieldName === 'fit') {
                      const fitTypeOptions = document.querySelector('.fit-type-options');
                      if (fitTypeOptions && fitTypeOptions instanceof HTMLElement) {
                        fitTypeOptions.classList.add('error');
                      }
                    } else {
                      currentInput.classList.add('error');
                      currentInput.focus();
                    }
                    return;
                  } else {
                    if (currentFieldName === 'fit') {
                      const fitTypeOptions = document.querySelector('.fit-type-options');
                      if (fitTypeOptions && fitTypeOptions instanceof HTMLElement) {
                        fitTypeOptions.classList.remove('error');
                      }
                    } else {
                      currentInput.classList.remove('error');
                    }
                  }
                }
              }

              if (currentIndex < allVerticalTabs.length - 1) {
                const nextVerticalTab = allVerticalTabs[currentIndex + 1];
                if (nextVerticalTab instanceof HTMLElement) {
                  nextVerticalTab.click();
                }
                return;
              }
            }

            // Block Next → Summary only when fit still has no values
            this.updateSummaryTabState();
            if (!this.isFitReadyForSummary()) return;
          }

          this.switchTab(nextTab);
        });
      }
    });

    const prevButtons = this.productDetailsContainer.querySelectorAll('[data-prev-tab]');
    prevButtons.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const prevTab = parseInt(btn.dataset.prevTab || '0');

          if ((this.activeTab === 0 || this.editingOptionIndex !== null) && prevTab === 0) {
            if (!this.validateMonogram()) {
              return;
            }
          }

          if (this.activeTab === 1 && prevTab === 0) {
            const fitContentView = document.querySelector('.fit-content-view');
            if (fitContentView && fitContentView instanceof HTMLElement && fitContentView.style.display !== 'none') {
              const activeVerticalTab = document.querySelector('.vertical-tab-nav-item.active');
              const allVerticalTabs = Array.from(document.querySelectorAll('.vertical-tab-nav-item'));
              const currentIndex = activeVerticalTab ? allVerticalTabs.indexOf(activeVerticalTab) : -1;

              const requiredFields = ['collar', 'chest', 'fit', 'sleeve'];
              const currentFieldName = requiredFields[currentIndex];
              const currentInput = document.querySelector(`[data-fit-input="${currentFieldName}"]`);

              if (currentInput) {
                if (currentInput instanceof HTMLInputElement) {
                  const value = currentInput.value.trim();
                  if (!value || value === '' || value === 'None') {
                    if (currentFieldName === 'fit') {
                      const fitTypeOptions = document.querySelector('.fit-type-options');
                      if (fitTypeOptions && fitTypeOptions instanceof HTMLElement) {
                        fitTypeOptions.classList.add('error');
                      }
                    } else {
                      currentInput.classList.add('error');
                      currentInput.focus();
                    }
                    return;
                  } else {
                    if (currentFieldName === 'fit') {
                      const fitTypeOptions = document.querySelector('.fit-type-options');
                      if (fitTypeOptions && fitTypeOptions instanceof HTMLElement) {
                        fitTypeOptions.classList.remove('error');
                      }
                    } else {
                      currentInput.classList.remove('error');
                    }
                  }
                }
              }

              if (currentIndex > 0) {
                const prevVerticalTab = allVerticalTabs[currentIndex - 1];
                if (prevVerticalTab instanceof HTMLElement) {
                  prevVerticalTab.click();
                }
                return;
              }
            }
          }

          if (this.activeTab === 2 && prevTab === 1) {
            this.activeTab = prevTab;

            if (!this.productDetailsContainer) return;

            const tabs = this.productDetailsContainer.querySelectorAll('.tab.button--action');
            tabs.forEach((tab, index) => {
              if (tab instanceof HTMLElement) {
                if (index === prevTab) {
                  tab.classList.add('active');
                  tab.classList.remove('disabled');
                } else {
                  tab.classList.remove('active');
                }
              }
            });

            const tabContainer = this.productDetailsContainer.querySelector('.tab-container');
            if (tabContainer && tabContainer instanceof HTMLElement) {
              tabContainer.className = `tab-container tab-${['one', 'two', 'three'][prevTab]}`;
            }

            const contentContainer = this.productDetailsContainer.querySelector('.content-container');
            if (contentContainer && contentContainer instanceof HTMLElement) {
              contentContainer.innerHTML = this.renderFitTabContent();
              this.setupCustomizationListeners();
            }

            return;
          }

          this.switchTab(prevTab);
        });
      }
    });

    const mostRecentBtn = this.productDetailsContainer.querySelector('[data-most-recent]');
    if (mostRecentBtn && mostRecentBtn instanceof HTMLElement) {
      mostRecentBtn.addEventListener('click', () => {
        this.selectedPath = 'previous';
        this.updateSummaryTabState();
        this.switchTab(2);
      });
    }

    const editButtons = this.productDetailsContainer.querySelectorAll('[data-edit-index]');
    editButtons.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const index = parseInt(btn.dataset.editIndex || '0');
          this.startEditingOption(index);
        });
      }
    });

    const backButton = this.productDetailsContainer.querySelector('[data-back-from-edit]');
    if (backButton && backButton instanceof HTMLElement) {
      backButton.addEventListener('click', () => {
        if (this.editingOptionIndex !== null) {
          const option = this.styleOptions[this.editingOptionIndex];
          if (option && option.name === 'monogram') {
            if (!this.validateMonogram()) {
              return;
            }
          }
        }

        this.stopEditingOption();
      });
    }

    const monogramChoices = this.productDetailsContainer.querySelectorAll('[data-monogram-choice]');
    monogramChoices.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const choice = parseInt(btn.dataset.monogramChoice || '0');
          this.updateMonogramChoice(choice);
        });
      }
    });

    const monogramInput = this.productDetailsContainer.querySelector('[data-monogram-input]');
    if (monogramInput && monogramInput instanceof HTMLInputElement) {
      monogramInput.addEventListener('input', (e) => {
        if (e.target instanceof HTMLInputElement) {
          this.updateMonogramValue(e.target.value);
        }
      });
    }

    const collarChoices = this.productDetailsContainer.querySelectorAll('[data-collar-index]');
    collarChoices.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const choice = parseInt(btn.dataset.collarIndex || '0');
          this.handleCollarChoice(choice);
        });
      }
    });

    const cuffChoices = this.productDetailsContainer.querySelectorAll('[data-cuff-index]');
    cuffChoices.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const choice = parseInt(btn.dataset.cuffIndex || '0');
          this.handleCuffChoice(choice);
        });
      }
    });

    const pocketChoices = this.productDetailsContainer.querySelectorAll('[data-pocket-index]');
    pocketChoices.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const choice = parseInt(btn.dataset.pocketIndex || '0');
          this.handlePocketChoice(choice);
        });
      }
    });

    const tailChoices = this.productDetailsContainer.querySelectorAll('[data-tail-index]');
    tailChoices.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const choice = parseInt(btn.dataset.tailIndex || '0');
          this.handleTailChoice(choice);
        });
      }
    });

    const frontChoices = this.productDetailsContainer.querySelectorAll('[data-front-index]');
    frontChoices.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', () => {
          const choice = parseInt(btn.dataset.frontIndex || '0');
          this.handleFrontChoice(choice);
        });
      }
    });

    // Create persistent Add to Cart button if it doesn't exist
    let addToCartWithApiBtn = this.productDetailsContainer.querySelector('#add-to-cart-with-api');
    if (!addToCartWithApiBtn) {
      const btnContainer = document.createElement('div');
      btnContainer.className = 'external-add-to-cart-container';

      btnContainer.innerHTML = `
          <button type="button" class="button--action mb-0 product-customization-add-to-cart-btn" id="add-to-cart-with-api">
            ADD TO CART
          </button>
      `;

      // Attempt to place above footer-container as requested
      const footerContainer = this.productDetailsContainer.querySelector('.footer-container');
      const customizationContainer = this.productDetailsContainer.querySelector('.customization-container');

      if (customizationContainer && footerContainer && customizationContainer.contains(footerContainer)) {
        // Insert inside customization-container, before footer-container
        footerContainer.parentNode.insertBefore(btnContainer, footerContainer);
      } else if (footerContainer && footerContainer.parentNode === this.productDetailsContainer) {
        this.productDetailsContainer.insertBefore(btnContainer, footerContainer);
      } else {
        // Fallback
        this.productDetailsContainer.appendChild(btnContainer);
      }

      addToCartWithApiBtn = btnContainer.querySelector('#add-to-cart-with-api');
    }

    // const addToCartWithApiBtn = this.productDetailsContainer.querySelector('#add-to-cart-with-api');
    if (addToCartWithApiBtn && addToCartWithApiBtn instanceof HTMLElement) {
      // Prevent duplicate listener attachment
      // IMPORTANT: Do NOT return here, as it prevents other listeners (like fit options) from being attached!
      if (!addToCartWithApiBtn.hasAttribute('data-listener-attached')) {
        addToCartWithApiBtn.setAttribute('data-listener-attached', 'true');

        addToCartWithApiBtn.addEventListener('click', async () => {
          console.log('[AddToCart] Button clicked - starting process');

          // Prevent duplicate customize ATC while one request is in flight
          if (this._customizeAtcLocked || this._addToCartInProgress) return;
          this._customizeAtcLocked = true;

          const setCustomizeAtcBusy = (/** @type {boolean} */ busy) => {
            if (!(addToCartWithApiBtn instanceof HTMLElement)) return;
            addToCartWithApiBtn.style.pointerEvents = busy ? 'none' : '';
          };

          setCustomizeAtcBusy(true);
          const originalText = addToCartWithApiBtn.textContent;
          if (originalText !== null) {
            addToCartWithApiBtn.textContent = 'Adding...';
          }

          try {
            // Monogram Yes requires exactly 3 initials before add to cart
            if (!this.validateMonogram()) {
              return;
            }

            // Validate Variant/Size Selection
            const variantId = this.getVariantId();
            if (!variantId) {
              // Try to find an existing error message container or create one
              let errorContainer = this.productDetailsContainer.querySelector('.customization-error-message');
              if (!errorContainer) {
                errorContainer = document.createElement('div');
                errorContainer.className = 'customization-error-message';
                errorContainer.style.cssText = 'color: #c22527; font-size: 14px; margin-top: 10px; text-align: center; width: 100%;';
                addToCartWithApiBtn.parentNode.insertBefore(errorContainer, addToCartWithApiBtn.nextSibling);
              }
              errorContainer.textContent = 'Please select a size';
              errorContainer.style.display = 'block';

              // Hide after 3 seconds
              setTimeout(() => {
                errorContainer.style.display = 'none';
              }, 3000);

              return;
            }

            // Check for MTO measurement conflict BEFORE completing add so the
            // customer can cancel. Keep button locked during the modal.
            const currentMeasurements = this._resolveFitMeasurements();
            const { hasConflict, mtoItems } = await window.MtoCartConflict.checkCartMeasurementConflict(currentMeasurements);
            if (hasConflict) {
              const proceed = await window.MtoCartConflict.showMeasurementConflictModal();
              if (!proceed) return; // Customer cancelled
            }

            // If the customer confirmed the conflict modal, update existing cart
            // item measurements to the new selections first.
            if (hasConflict) {
              await window.MtoCartConflict.updateCartMeasurements(mtoItems, currentMeasurements);
            }
            await this.addToCartAndCallAPI();
          } catch (error) {
            console.error('Add to cart error:', error);
            // Surface the error inline so the customer knows what happened.
            let errorContainer = this.productDetailsContainer?.querySelector('.customization-error-message');
            if (!errorContainer) {
              errorContainer = document.createElement('div');
              errorContainer.className = 'customization-error-message';
              errorContainer.style.cssText = 'color:#c22527;font-size:14px;margin-top:10px;text-align:center;width:100%;';
              addToCartWithApiBtn.parentNode?.insertBefore(errorContainer, addToCartWithApiBtn.nextSibling);
            }
            errorContainer.textContent = error.message || 'An error occurred. Please try again.';
            errorContainer.style.display = 'block';
            setTimeout(() => { errorContainer.style.display = 'none'; }, 5000);
          } finally {
            this._customizeAtcLocked = false;
            setCustomizeAtcBusy(false);
            if (originalText !== null) {
              addToCartWithApiBtn.textContent = originalText;
            }
          }
        });
      }
    }

    // Attach edit icon click handler using event delegation on document
    // This ensures it works even when content is dynamically inserted
    if (!this.editIconListenerAttached) {
      const handleEditIconClick = (/** @type {MouseEvent} */ e) => {
        const target = e.target;
        if (!target || !(target instanceof Element)) return;

        // Check if clicking on SVG path or SVG element
        let clickedElement = target;
        if (target.tagName === 'path') {
          const parentSvg = target.closest('svg');
          if (parentSvg) {
            clickedElement = parentSvg;
          }
        }

        // Check for fit overview edit icon
        let fitOverviewEditIcon = null;

        if (clickedElement instanceof Element) {
          if (clickedElement.hasAttribute('data-edit-fit-overview')) {
            fitOverviewEditIcon = clickedElement;
          } else {
            fitOverviewEditIcon = clickedElement.closest('[data-edit-fit-overview]');
          }
        }

        // If still not found and clicking on a path, find SVG with the class (summary-edit-icon or edit-button)
        if (!fitOverviewEditIcon && target.tagName === 'path') {
          const svgWithClass = target.closest('svg.summary-edit-icon[data-edit-fit-overview], svg.edit-button[data-edit-fit-overview]');
          if (svgWithClass && svgWithClass instanceof Element) {
            const fitNameAttr = svgWithClass.getAttribute('data-edit-fit-overview');
            if (fitNameAttr) {
              fitOverviewEditIcon = svgWithClass;
            }
          }
        }

        if (fitOverviewEditIcon && fitOverviewEditIcon instanceof Element) {
          e.preventDefault();
          e.stopPropagation();

          // Get fit name - SVG elements use getAttribute, HTMLElement can use dataset
          let fitName = fitOverviewEditIcon.getAttribute('data-edit-fit-overview') || '';
          if (!fitName && fitOverviewEditIcon instanceof HTMLElement && fitOverviewEditIcon.dataset) {
            fitName = fitOverviewEditIcon.dataset.editFitOverview || '';
          }

          if (!fitName) return;

          // Remove any existing dropdowns
          document.querySelectorAll('.summary-dropdown').forEach(dropdown => {
            if (dropdown instanceof HTMLElement) {
              dropdown.remove();
            }
          });

          const editIconForCall = fitOverviewEditIcon instanceof HTMLElement ? fitOverviewEditIcon : /** @type {HTMLElement} */ (fitOverviewEditIcon);
          this.showInlineFitEditor(fitName, editIconForCall);
          return;
        }

        // Handle other edit icons (style, fit from summary tab)
        if (!target.closest('.summary-row') && !target.closest('.summary-dropdown')) {
          document.querySelectorAll('.summary-dropdown').forEach(dropdown => {
            if (dropdown instanceof HTMLElement) {
              dropdown.remove();
            }
          });
        }

        // Handle style edit icons - check SVG element or path inside SVG
        let styleEditIcon = null;
        if (clickedElement instanceof Element) {
          if (clickedElement.hasAttribute('data-edit-style')) {
            styleEditIcon = clickedElement;
          } else {
            styleEditIcon = clickedElement.closest('[data-edit-style]');
          }
        }

        // If clicking on path, find parent SVG with data-edit-style
        if (!styleEditIcon && target.tagName === 'path') {
          const svgWithDataAttr = target.closest('svg[data-edit-style]');
          if (svgWithDataAttr && svgWithDataAttr instanceof Element) {
            styleEditIcon = svgWithDataAttr;
          }
        }

        if (styleEditIcon && styleEditIcon instanceof Element) {
          e.preventDefault();
          e.stopPropagation();

          // Get style index - SVG elements use getAttribute, HTMLElement can use dataset
          let styleIndexStr = styleEditIcon.getAttribute('data-edit-style') || '';
          if (!styleIndexStr && styleEditIcon instanceof HTMLElement && styleEditIcon.dataset) {
            styleIndexStr = styleEditIcon.dataset.editStyle || '';
          }
          const styleIndex = parseInt(styleIndexStr || '0');

          document.querySelectorAll('.summary-dropdown').forEach(dropdown => {
            if (dropdown instanceof HTMLElement) {
              dropdown.remove();
            }
          });

          const editIconForCall = styleEditIcon instanceof HTMLElement ? styleEditIcon : /** @type {HTMLElement} */ (styleEditIcon);
          this.showInlineStyleEditor(styleIndex, editIconForCall);
          return;
        }

        // Handle fit edit icons from summary tab - check SVG element or path inside SVG
        let fitEditIcon = null;
        if (clickedElement instanceof Element) {
          if (clickedElement.hasAttribute('data-edit-fit')) {
            fitEditIcon = clickedElement;
          } else {
            fitEditIcon = clickedElement.closest('[data-edit-fit]');
          }
        }

        // If clicking on path, find parent SVG with data-edit-fit
        if (!fitEditIcon && target.tagName === 'path') {
          const svgWithDataAttr = target.closest('svg[data-edit-fit]');
          if (svgWithDataAttr && svgWithDataAttr instanceof Element) {
            fitEditIcon = svgWithDataAttr;
          }
        }

        if (fitEditIcon && fitEditIcon instanceof Element) {
          e.preventDefault();
          e.stopPropagation();

          // Get fit name - SVG elements use getAttribute, HTMLElement can use dataset
          let fitName = fitEditIcon.getAttribute('data-edit-fit') || '';
          if (!fitName && fitEditIcon instanceof HTMLElement && fitEditIcon.dataset) {
            fitName = fitEditIcon.dataset.editFit || '';
          }

          if (!fitName) return;

          document.querySelectorAll('.summary-dropdown').forEach(dropdown => {
            if (dropdown instanceof HTMLElement) {
              dropdown.remove();
            }
          });

          const editIconForCall = fitEditIcon instanceof HTMLElement ? fitEditIcon : /** @type {HTMLElement} */ (fitEditIcon);
          this.showInlineFitEditor(fitName, editIconForCall);
          return;
        }
      };

      // Attach to document for better event delegation
      document.addEventListener('click', handleEditIconClick, true);
      this.editIconListenerAttached = true;
    }

    // Note: data-fit-option handlers removed — the fit tab now shows the measurement form directly

  }

  // ==========================================
  // EDITING METHODS
  // ==========================================

  /**
   * @param {number} index
   */
  startEditingOption(index) {
    this.editingOptionIndex = index;
    if (!this.productDetailsContainer) return;
    const contentContainer = this.productDetailsContainer.querySelector('.content-container');
    if (contentContainer && contentContainer instanceof HTMLElement) {
      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();
    }
  }

  stopEditingOption() {
    this.editingOptionIndex = null;
    if (!this.productDetailsContainer) return;
    const contentContainer = this.productDetailsContainer.querySelector('.content-container');
    if (contentContainer && contentContainer instanceof HTMLElement) {
      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();
    }
  }

  /**
   * @param {number} choice
   */
  updateMonogramChoice(choice) {
    if (this.editingOptionIndex === null) return;
    if (!this.productDetailsContainer) return;
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option || option.name !== 'monogram') return;

    option.selected = choice;

    // Map directly to Monogram Yes/No variant id (cart price source of truth).
    // Avoid Swatch King / picker clicks — those were disabled because they caused UI churn.
    this.applyMonogramVariantSelection(choice);

    const monogramInput = this.productDetailsContainer.querySelector('[data-monogram-input]');
    const errorMsg = this.productDetailsContainer.querySelector('#monogram-error-message');
    if (choice === 0) {
      option.monogram = '';
      if (monogramInput && monogramInput instanceof HTMLInputElement) {
        monogramInput.classList.remove('error');
        monogramInput.value = '';
        monogramInput.disabled = true;
      }
      if (errorMsg instanceof HTMLElement) errorMsg.style.display = 'none';
    } else {
      if (monogramInput && monogramInput instanceof HTMLInputElement) {
        monogramInput.disabled = false;
        // removing disabled might not trigger focus, but we want to keep it open
        if (choice === 1) setTimeout(() => monogramInput.focus(), 50);
      }
    }

    // Update UI classes without re-rendering to prevent closing
    const optionsContainer = this.productDetailsContainer.querySelector('.content-options-container');
    if (optionsContainer) {
      const noOption = optionsContainer.querySelector('[data-monogram-choice="0"]');
      const yesOption = optionsContainer.querySelector('[data-monogram-choice="1"]');

      if (noOption) {
        if (choice === 0) noOption.classList.add('selected');
        else noOption.classList.remove('selected');

        const text = noOption.querySelector('h3');
        if (text) text.textContent = choice === 0 ? 'selected' : 'select';
      }
      if (yesOption) {
        if (choice === 1) yesOption.classList.add('selected');
        else yesOption.classList.remove('selected');

        const text = yesOption.querySelector('h3');
        if (text) text.textContent = choice === 1 ? 'selected' : 'select';
      }
    }
  }

  /**
   * @param {string} value
   */
  updateMonogramValue(value) {
    if (this.editingOptionIndex === null) return;
    if (!this.productDetailsContainer) return;
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option || option.name !== 'monogram') return;

    option.monogram = value.toUpperCase().slice(0, 3);

    const monogramInput = this.productDetailsContainer.querySelector('[data-monogram-input]');
    const errorMsg = this.productDetailsContainer.querySelector('#monogram-error-message');
    if (monogramInput && monogramInput instanceof HTMLInputElement) {
      if (monogramInput.value !== option.monogram) {
        monogramInput.value = option.monogram;
      }
      if (value.trim().length === 3) {
        monogramInput.classList.remove('error');
        if (errorMsg instanceof HTMLElement) errorMsg.style.display = 'none';
      } else if (value.trim().length > 0 && value.trim().length < 3) {
        monogramInput.classList.add('error');
      }
    }
  }

  /**
   * When Monogram Yes is selected, require exactly 3 initials.
   * Opens the monogram editor if needed so the error is visible.
   * @returns {boolean}
   */
  validateMonogram() {
    if (!this.productDetailsContainer) return true;
    const monogramOption = this.styleOptions.find(opt => opt.name === 'monogram');
    if (!monogramOption) return true;

    if (monogramOption.selected !== 1) return true;

    const initials = monogramOption.monogram ? monogramOption.monogram.trim() : '';
    if (initials.length === 3) return true;

    // Prefer an open summary initials field when present
    const summaryInput = this.productDetailsContainer.querySelector('[data-summary-monogram-input]');
    if (summaryInput instanceof HTMLInputElement) {
      summaryInput.classList.add('error');
      summaryInput.focus();
      return false;
    }

    // Not on the monogram editor — open it so the initials error is visible
    const monogramIndex = this.styleOptions.findIndex(opt => opt.name === 'monogram');
    const onMonogramEditor =
      this.activeTab === 0 &&
      this.editingOptionIndex !== null &&
      this.styleOptions[this.editingOptionIndex]?.name === 'monogram';

    if (!onMonogramEditor && monogramIndex !== -1) {
      // Ensure Style tab is active so the editor can render
      if (this.activeTab !== 0) {
        this.activeTab = 0;
        const tabs = this.productDetailsContainer.querySelectorAll('.tab.button--action');
        tabs.forEach((tab, index) => {
          if (!(tab instanceof HTMLElement)) return;
          tab.classList.toggle('active', index === 0);
        });
        const tabContainer = this.productDetailsContainer.querySelector('.tab-container');
        if (tabContainer instanceof HTMLElement) {
          tabContainer.className = 'tab-container tab-one';
        }
      }
      this.startEditingOption(monogramIndex);
    }

    const monogramInput = this.productDetailsContainer.querySelector('[data-monogram-input]');
    const errorMsg = this.productDetailsContainer.querySelector('#monogram-error-message');
    if (monogramInput instanceof HTMLInputElement) {
      monogramInput.classList.add('error');
      monogramInput.disabled = false;
      monogramInput.focus();
    }
    if (errorMsg instanceof HTMLElement) errorMsg.style.display = 'block';

    return false;
  }

  /**
   * @param {number} index
   */
  handleCollarChoice(index) {
    if (this.editingOptionIndex === null) return;
    if (!this.productDetailsContainer) return;
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option || option.name !== 'collar') return;

    option.selected = index;

    const contentContainer = this.productDetailsContainer.querySelector('.content-container');
    if (contentContainer && contentContainer instanceof HTMLElement) {
      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();
    }
  }

  /**
   * @param {number} index
   */
  handleCuffChoice(index) {
    if (this.editingOptionIndex === null) return;
    if (!this.productDetailsContainer) return;
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option || option.name !== 'cuff') return;
    option.selected = index;

    const contentContainer = this.productDetailsContainer.querySelector('.content-container');
    if (contentContainer && contentContainer instanceof HTMLElement) {
      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();
    }
  }

  /**
   * @param {number} index
   */
  handlePocketChoice(index) {
    if (this.editingOptionIndex === null) return;
    if (!this.productDetailsContainer) return;
    const option = this.styleOptions[this.editingOptionIndex];
    if (!option || option.name !== 'pocket') return;
    option.selected = index;

    const contentContainer = this.productDetailsContainer.querySelector('.content-container');
    if (contentContainer && contentContainer instanceof HTMLElement) {
      contentContainer.innerHTML = this.createTabContent();
      this.setupCustomizationListeners();
    }
  }

  /**
   * @param {number} choice
   */
  triggerMonogramRadioClick(choice) {
    const monogramValue = choice === 1 ? 'Yes' : 'No';

    const selectors = [
      `li[orig-value="${monogramValue}"][aria-label="${monogramValue}"]`,
      `li[data-value="${monogramValue}"]`,
      `li.swatch-view-item[orig-value="${monogramValue}"]`,
      `#swatch-option2 li[orig-value="${monogramValue}"]`,
      `#swatch-option2 li[data-value="${monogramValue}"]`
    ];

    let radioButton = null;
    for (const selector of selectors) {
      radioButton = document.querySelector(selector);
      if (radioButton) break;
    }

    if (radioButton && radioButton instanceof HTMLElement) {
      const clickableElement = radioButton.querySelector('div[swatch-option], div.swatch-button, div.swatch-selector') || radioButton;
      if (clickableElement && clickableElement instanceof HTMLElement) {
        clickableElement.click();
      } else if (radioButton instanceof HTMLElement) {
        radioButton.click();
      }
    }
  }

  /**
   * Select the appropriate variant based on monogram choice
   * @param {number} choice - 0 for No, 1 for Yes
   */
  selectMonogramVariant(choice) {
    // Find the variant picker form
    const variantPicker = document.querySelector('variant-picker form.variant-picker__form');
    if (!variantPicker) {
      console.warn('Variant picker not found');
      return;
    }

    // Find the Monogram option input
    const monogramValue = choice === 1 ? 'Yes' : 'No';

    // Try to find radio button for Monogram option
    const monogramInputs = variantPicker.querySelectorAll('input[type="radio"]');
    let monogramInput = null;

    for (const input of monogramInputs) {
      // Check if this input's name contains "Monogram" and value matches
      if (input.name && input.name.toLowerCase().includes('monogram') &&
        input.value === monogramValue) {
        monogramInput = input;
        break;
      }
    }

    // If not found by name, try by value alone (case insensitive)
    if (!monogramInput) {
      for (const input of monogramInputs) {
        if (input.value && input.value.toLowerCase() === monogramValue.toLowerCase()) {
          // Check if the parent fieldset legend contains "monogram"
          const fieldset = input.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend');
            if (legend && legend.textContent.toLowerCase().includes('monogram')) {
              monogramInput = input;
              break;
            }
          }
        }
      }
    }

    // Try dropdown/select if radio not found
    if (!monogramInput) {
      const selects = variantPicker.querySelectorAll('select.variant-option__select');
      for (const select of selects) {
        const label = select.closest('.variant-option')?.querySelector('label');
        if (label && label.textContent.toLowerCase().includes('monogram')) {
          // Find the option with matching value
          const option = Array.from(select.options).find(opt =>
            opt.value.toLowerCase() === monogramValue.toLowerCase()
          );
          if (option) {
            select.value = option.value;
            // Trigger change event
            select.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }
    }

    if (monogramInput) {
      // Check the radio button
      monogramInput.checked = true;

      // Trigger change event to update variant
      monogramInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Also trigger click event for any listeners
      monogramInput.dispatchEvent(new Event('click', { bubbles: true }));

    } else {
      console.warn(`Monogram variant option "${monogramValue}" not found in variant picker`);
    }
  }

  /**
   * @param {number} choice
   */
  updateMonogramPrice(choice) {
    const priceElement = document.querySelector(
      'product-price .price, .product-price .price, [ref="priceContainer"] .price, h3.price, .price-label'
    );
    if (!priceElement) return;

    // Prefer real Monogram Yes/No variant price so PDP matches cart
    const resolvedId = this.resolveMonogramVariantId(choice === 1);
    const product = this.getProductData();
    const variant = resolvedId && product?.variants
      ? product.variants.find((v) => String(v.id) === String(resolvedId))
      : null;

    let formatted = null;
    if (variant && typeof variant.price === 'number') {
      // Shopify product JSON prices are in cents
      formatted = '$' + (variant.price / 100).toFixed(2);
      if (choice === 0) {
        this.basePrice = variant.price / 100;
      }
    } else {
      if (typeof this.basePrice === 'undefined') {
        const text = priceElement.textContent.trim().replace(/[^0-9.]/g, '');
        const val = parseFloat(text);
        if (!isNaN(val)) this.basePrice = val;
      }
      if (!this.basePrice) this.basePrice = 295.0;
      formatted = '$' + (this.basePrice + (choice === 1 ? 18 : 0)).toFixed(2);
    }

    priceElement.textContent = formatted;

    const summaryLabel = this.productDetailsContainer?.querySelector('.price-label');
    if (summaryLabel) summaryLabel.textContent = formatted;
  }

  syncPriceLabel() {
    const shopifyPriceElement = document.querySelector('product-price .price, .product-price .price, [ref="priceContainer"] .price, h3.price');
    const priceLabel = this.productDetailsContainer?.querySelector('.price-label');

    if (shopifyPriceElement && priceLabel && priceLabel instanceof HTMLElement) {
      const currentPrice = shopifyPriceElement.textContent?.trim() || '';
      if (currentPrice) {
        priceLabel.textContent = currentPrice;
      }
    }
  }

  setupPriceSync() {
    if (!this.priceSyncSetup) {
      document.addEventListener('change', (e) => {
        const target = e.target;
        if (target instanceof HTMLElement) {
          if (target.matches('input[name="id"]') ||
            target.matches('input[type="radio"][name*="variant"]') ||
            target.closest('li[orig-value]')) {
            setTimeout(() => this.syncPriceLabel(), 100);
          }
        }
      });

      ['variant:change', 'variant:updated', 'product:variant:change', 'vsk:variant:changed'].forEach(eventName => {
        document.addEventListener(eventName, () => {
          setTimeout(() => this.syncPriceLabel(), 100);
        });
      });

      let lastPrice = '';
      const priceCheckInterval = setInterval(() => {
        const shopifyPriceElement = document.querySelector('product-price .price, .product-price .price, [ref="priceContainer"] .price, h3.price');
        if (shopifyPriceElement) {
          const currentPrice = shopifyPriceElement.textContent.trim();
          if (currentPrice && currentPrice !== lastPrice) {
            lastPrice = currentPrice;
            this.syncPriceLabel();
          }
        }
      }, 300);

      const originalRestore = this.restoreOriginalContent.bind(this);
      this.restoreOriginalContent = () => {
        clearInterval(priceCheckInterval);
        originalRestore();
      };

      this.priceSyncSetup = true;
    }
  }

  /**
   * @param {number} styleIndex
   * @param {HTMLElement} editIcon
   */
  showInlineStyleEditor(styleIndex, editIcon) {
    const option = this.styleOptions[styleIndex];
    if (!option) return;

    const summaryRow = editIcon.closest('.summary-row');
    if (!summaryRow || !(summaryRow instanceof HTMLElement)) return;

    // Remove any existing dropdowns
    document.querySelectorAll('.summary-dropdown').forEach(dropdown => {
      if (dropdown instanceof HTMLElement) {
        dropdown.remove();
      }
    });

    // Set position relative for dropdown positioning
    summaryRow.style.position = 'relative';

    let optionsHTML = '';

    if (option.name === 'collar' && this.collarOptions) {
      optionsHTML = this.collarOptions.map((collar, i) => `
        <div data-select-collar="${i}">
          <h2 class="h2-menu-option ${option.selected === i ? 'selected' : ''}">${collar.title}</h2>
        </div>
      `).join('');
    } else if (option.name === 'cuff' && this.cuffOptions) {
      optionsHTML = this.cuffOptions.map((cuff, i) => `
        <div class="summary-dropdown-item ${option.selected === i ? 'selected' : ''}" data-select-cuff="${i}">
          ${cuff.title.toUpperCase()}
        </div>
      `).join('');
    } else if (option.name === 'pocket' && this.pocketOptions) {
      optionsHTML = this.pocketOptions.map((pocket, i) => `
        <div data-select-pocket="${i}">
          <h2 class="h2-menu-option ${option.selected === i ? 'selected' : ''}">${pocket.title}</h2>
        </div>
      `).join('');
    } else if (option.name === 'tail' && this.tailOptions) {
      optionsHTML = this.tailOptions.map((tail, i) => `
        <div data-select-tail="${i}">
          <h2 class="h2-menu-option ${option.selected === i ? 'selected' : ''}">${tail.title}</h2>
        </div>
      `).join('');
    } else if (option.name === 'front' && this.frontOptions) {
      optionsHTML = this.frontOptions.map((front, i) => `
        <div data-select-front="${i}">
          <h2 class="h2-menu-option ${option.selected === i ? 'selected' : ''}">${front.title}</h2>
        </div>
      `).join('');
    } else if (option.name === 'monogram') {
      optionsHTML = `
        <div data-select-monogram="0">
          <h2 class="h2-menu-option ${option.selected === 0 ? 'selected' : ''}">no monogram</h2>
        </div>
        <div data-select-monogram="1">
          <h2 class="h2-menu-option ${option.selected === 1 ? 'selected' : ''}">yes</h2>
        </div>
      `;

      if (option.selected === 1) {
        optionsHTML += `
          <div class="summary-monogram-input">
            <input 
              type="text" 
              class="initial-input" 
              placeholder="3 initials" 
              value="${option.monogram || ''}"
              maxlength="3"
              minlength="3"
              data-summary-monogram-input
            />
          </div>
        `;
      }
    }

    const dropdown = document.createElement('div');
    // Use column layout for collar, pocket, and monogram (similar to fit dropdown)
    const useColumnLayout = option.name === 'collar' || option.name === 'pocket' || option.name === 'monogram';
    dropdown.className = useColumnLayout ? 'summary-dropdown fit-menu col-items' : 'summary-dropdown';
    dropdown.innerHTML = optionsHTML;

    summaryRow.appendChild(dropdown);

    // Set initial state for animation
    dropdown.style.display = 'flex';
    dropdown.style.visibility = 'visible';
    dropdown.style.position = 'absolute';
    dropdown.style.zIndex = '1000';
    dropdown.style.opacity = '0';

    // Trigger fade-in animation
    setTimeout(() => {
      if (dropdown instanceof HTMLElement) {
        dropdown.style.opacity = '1';
      }
    }, 10);

    dropdown.querySelectorAll('[data-select-collar]').forEach(item => {
      if (item instanceof HTMLElement) {
        item.style.cursor = 'pointer';
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(item.getAttribute('data-select-collar') || item.dataset.selectCollar || '0');
          option.selected = index;
          dropdown.remove();
          this.renderSummaryContent();
        });
      }
    });

    dropdown.querySelectorAll('[data-select-cuff]').forEach(item => {
      if (item instanceof HTMLElement) {
        item.addEventListener('click', () => {
          const index = parseInt(item.dataset.selectCuff || '0');
          option.selected = index;
          dropdown.remove();
          this.renderSummaryContent();
        });
      }
    });

    dropdown.querySelectorAll('[data-select-pocket]').forEach(item => {
      if (item instanceof HTMLElement) {
        item.style.cursor = 'pointer';
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(item.getAttribute('data-select-pocket') || item.dataset.selectPocket || '0');
          option.selected = index;
          dropdown.remove();
          this.renderSummaryContent();
        });
      }
    });

    dropdown.querySelectorAll('[data-select-tail]').forEach(item => {
      if (item instanceof HTMLElement) {
        item.style.cursor = 'pointer';
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(item.getAttribute('data-select-tail') || item.dataset.selectTail || '0');
          option.selected = index;
          dropdown.remove();
          this.renderSummaryContent();
        });
      }
    });

    dropdown.querySelectorAll('[data-select-front]').forEach(item => {
      if (item instanceof HTMLElement) {
        item.style.cursor = 'pointer';
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(item.getAttribute('data-select-front') || item.dataset.selectFront || '0');
          option.selected = index;
          dropdown.remove();
          this.renderSummaryContent();
        });
      }
    });

    dropdown.querySelectorAll('[data-select-monogram]').forEach(item => {
      if (item instanceof HTMLElement) {
        item.style.cursor = 'pointer';
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const choice = parseInt(item.getAttribute('data-select-monogram') || item.dataset.selectMonogram || '0');
          option.selected = choice;

          // Switch to Monogram Yes/No variant so cart price matches PDP
          this.applyMonogramVariantSelection(choice);

          if (choice === 0) {
            // No monogram selected - close dropdown and update
            option.monogram = '';
            dropdown.remove();
            this.renderSummaryContent();
          } else {
            // Yes selected - update visual state and show input field
            // Update selected state in dropdown
            console.log('[createStyleOptionsContent] YES clicked, calling updateVisualState');
            dropdown.querySelectorAll('[data-select-monogram]').forEach(opt => {
              console.log('[createStyleOptionsContent] Checking opt:', opt);
              if (!(opt instanceof HTMLElement)) return;
              const optChoice = parseInt(opt.getAttribute('data-select-monogram') || '0');
              const h2Element = opt.querySelector('h2.h2-menu-option');
              console.log('[createStyleOptionsContent] optChoice:', optChoice, 'targetChoice:', choice, 'h2:', h2Element);

              if (h2Element && h2Element instanceof HTMLElement) {
                if (optChoice === choice) {
                  console.log('[createStyleOptionsContent] Adding selected class to h2');
                  h2Element.classList.add('selected');
                } else {
                  h2Element.classList.remove('selected');
                }
              }
            });


            // Check if input field already exists
            let monogramInputContainer = dropdown.querySelector('.summary-monogram-input');
            if (!monogramInputContainer) {
              // Create and append input field
              monogramInputContainer = document.createElement('div');
              monogramInputContainer.className = 'summary-monogram-input';
              const input = document.createElement('input');
              input.type = 'text';
              input.className = 'initial-input';
              input.placeholder = '3 initials';
              input.value = option.monogram || '';
              input.maxLength = 3;
              input.minLength = 3;
              input.setAttribute('data-summary-monogram-input', '');
              monogramInputContainer.appendChild(input);
              dropdown.appendChild(monogramInputContainer);

              // Focus the input
              setTimeout(() => {
                if (input instanceof HTMLInputElement) {
                  input.focus();
                }
              }, 50);

              // Stop propagation for click
              input.addEventListener('click', (e) => {
                e.stopPropagation();
              });

              // Handle Enter Key
              input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  const initials = input.value.trim().toUpperCase();
                  if (initials.length === 3) {
                    option.monogram = initials;
                    input.classList.remove('error');
                    this.renderSummaryContent();
                    dropdown.remove();
                  } else {
                    input.classList.add('error');
                  }
                }
              });

              // Add input event listener
              input.addEventListener('input', (inputEvent) => {
                if (inputEvent.target instanceof HTMLInputElement) {
                  const cleaned = inputEvent.target.value.toUpperCase().slice(0, 3);
                  inputEvent.target.value = cleaned;
                  option.monogram = cleaned;
                  if (cleaned.length === 3) {
                    inputEvent.target.classList.remove('error');
                    setTimeout(() => {
                      dropdown.remove();
                      this.renderSummaryContent();
                    }, 300);
                  } else if (cleaned.length > 0) {
                    inputEvent.target.classList.add('error');
                  } else {
                    inputEvent.target.classList.remove('error');
                  }
                }
              });
            } else {
              // Input field exists, just focus it
              const existingInput = monogramInputContainer.querySelector('[data-summary-monogram-input]');
              if (existingInput && existingInput instanceof HTMLInputElement) {
                setTimeout(() => {
                  existingInput.focus();
                }, 50);
              }
            }
          }
        });
      }
    });

    // Handle existing input field if dropdown was opened with "yes" already selected
    const monogramInput = dropdown.querySelector('[data-summary-monogram-input]');
    if (monogramInput && monogramInput instanceof HTMLInputElement) {
      monogramInput.addEventListener('input', (e) => {
        if (e.target instanceof HTMLInputElement) {
          const cleaned = e.target.value.toUpperCase().slice(0, 3);
          e.target.value = cleaned;
          option.monogram = cleaned;
          if (cleaned.length === 3) {
            e.target.classList.remove('error');
            setTimeout(() => {
              dropdown.remove();
              this.renderSummaryContent();
            }, 300);
          } else if (cleaned.length > 0) {
            e.target.classList.add('error');
          } else {
            e.target.classList.remove('error');
          }
        }
      });
    }
  }

  /**
   * @param {string} fitName
   * @param {HTMLElement|Element} editIcon
   */
  showInlineFitEditor(fitName, editIcon) {
    // Support both summary-row (old) and fit-item (new) structures
    const container = editIcon.closest('.summary-row') || editIcon.closest('.fit-item');
    if (!container || !(container instanceof HTMLElement)) return;

    // Remove any existing dropdowns
    document.querySelectorAll('.summary-dropdown').forEach(dropdown => {
      if (dropdown instanceof HTMLElement) {
        dropdown.remove();
      }
    });

    let optionsHTML = '';
    const currentFitItem = window.cachedSelections?.fit?.find(f => f.name === fitName);
    const currentValue = currentFitItem?.value || '';
    const trueRecentVal = this.getTrueMostRecentValue(fitName);
    // Also auto-detect: if the current value matches the account's most-recent measurement,
    // treat the field as "most recent" mode so the dropdown pre-selects MOST RECENT.
    const isCurrentlyMostRecent = this.fitMostRecentFields.has(fitName) ||
      (trueRecentVal !== 'as last' && trueRecentVal !== 'most recent' && currentValue !== '' && currentValue === trueRecentVal);

    // Format size for display (convert decimals to fractions for collar)
    const formatSizeForDisplay = (/** @type {string} */ size) => {
      if (!size) return '';
      return String(size).trim().replace('.5', '½').replace('.25', '¼').replace('.75', '¾');
    };

    // Normalize value for comparison (convert fractions to decimals)
    const normalizeValue = (/** @type {string} */ val) => {
      if (!val) return '';
      return String(val).trim().replace('½', '.5').replace('¼', '.25').replace('¾', '.75');
    };

    // Build "As Last" option using the true last-order value (metafields for logged-in, baseline for guests)
    const mostRecentBaselineValue = trueRecentVal;
    const mostRecentOptionHTML = mostRecentBaselineValue
      ? (fitName === 'fit'
          ? `<div class="most-recent-option" data-select-fit-value="${mostRecentBaselineValue}" data-is-most-recent="true">
               <h2 class="h2-menu-option ${isCurrentlyMostRecent ? 'selected' : ''}">as last</h2>
             </div>`
          : `<div class="summary-dropdown-item most-recent-option ${isCurrentlyMostRecent ? 'selected' : ''}" data-select-fit-value="${mostRecentBaselineValue}" data-is-most-recent="true">
               as last
             </div>`)
      : '';

    if (fitName === 'fit') {
      const fitOptionsArray = this.fitOptions.fit;
      if (fitOptionsArray && fitOptionsArray.length > 0) {
        optionsHTML = fitOptionsArray.map(/** @param {string} fitType */ fitType => {
          const isSelected = !isCurrentlyMostRecent && currentValue.toLowerCase() === fitType.toLowerCase();
          return `
            <div data-select-fit-value="${fitType}">
              <h2 class="h2-menu-option ${isSelected ? 'selected' : ''}">${fitType}</h2>
            </div>
          `;
        }).join('') + mostRecentOptionHTML;
      }
    } else if (this.fitOptions[fitName] && Array.isArray(this.fitOptions[fitName])) {
      const fitOptionsArray = this.fitOptions[fitName];
      if (fitOptionsArray.length > 0) {
        const normalizedCurrent = normalizeValue(currentValue);

        optionsHTML = fitOptionsArray.map(/** @param {string} option */ option => {
          const optionStr = String(option).trim();
          const normalizedOption = normalizeValue(optionStr);
          const displayValue = fitName === 'collar' ? formatSizeForDisplay(optionStr) : optionStr;
          const isSelected = !isCurrentlyMostRecent && (
            normalizedCurrent === normalizedOption ||
            (normalizedCurrent && normalizedCurrent === optionStr) ||
            (currentValue && String(currentValue).trim() === optionStr)
          );

          return `
            <div class="summary-dropdown-item ${isSelected ? 'selected' : ''}" data-select-fit-value="${optionStr}">
              ${displayValue}
            </div>
          `;
        }).join('') + mostRecentOptionHTML;
      }
    }

    if (!optionsHTML) return;

    const dropdown = document.createElement('div');
    // Add fit-menu and col-items classes for fit type dropdown
    dropdown.className = fitName === 'fit' ? 'summary-dropdown fit-menu col-items' : 'summary-dropdown';
    dropdown.innerHTML = optionsHTML;

    container.style.position = 'relative';
    container.appendChild(dropdown);

    // Set initial state - CSS animation will handle fade-in
    dropdown.style.display = 'flex';
    dropdown.style.visibility = 'visible';
    dropdown.style.position = 'absolute';
    dropdown.style.zIndex = '1000';

    // Handle clicks on fit value items (works for both grid items and h2-menu-option)
    dropdown.querySelectorAll('[data-select-fit-value]').forEach(item => {
      if (item instanceof HTMLElement) {
        item.style.cursor = 'pointer';

        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const value = item.dataset.selectFitValue || '';
          const isMostRecent = item.dataset.isMostRecent === 'true';

          if (!window.cachedSelections) {
            window.cachedSelections = { fit: [], style: [] };
          }

          // Track most-recent mode per field
          if (isMostRecent) {
            this.fitMostRecentFields.add(fitName);
            this.selectedPath = 'previous';
            // Store the true most-recent value (metafields for logged-in, baseline for guests)
            const trueRecentVal = this.getTrueMostRecentValue(fitName);
            if (trueRecentVal) {
              const existing = window.cachedSelections.fit.find(f => f.name === fitName);
              if (existing) { existing.value = trueRecentVal; }
              else { window.cachedSelections.fit.push({ name: fitName, value: trueRecentVal }); }
            }
          } else {
            this.clearLastOrderForField(fitName);
            const valueToStore = value.trim();
            const existing = window.cachedSelections.fit.find(f => f.name === fitName);
            if (existing) { existing.value = valueToStore; }
            else { window.cachedSelections.fit.push({ name: fitName, value: valueToStore }); }
          }

          // Persist immediately so a page refresh doesn't lose this change
          this.saveCustomizationToLocalStorage(!!this.customerData);
          this.updateSummaryTabState();

          dropdown.remove();

          // Refresh the fit overview display
          const fitOverviewTab = document.querySelector('.fit-overview-tab');
          if (fitOverviewTab && this.productDetailsContainer) {
            const contentContainer = this.productDetailsContainer.querySelector('.content-container');
            if (contentContainer && contentContainer instanceof HTMLElement) {
              contentContainer.innerHTML = this.createFitOverview();
              this.setupCustomizationListeners();
            }
          } else {
            this.renderSummaryContent();
          }
        });
      }
    });

    // Close dropdown when clicking outside
    setTimeout(() => {
      const closeOnOutsideClick = (/** @type {MouseEvent} */ e) => {
        const target = e.target;
        if (target instanceof HTMLElement) {
          if (!container.contains(target) && !dropdown.contains(target)) {
            const editIcon = target.closest('[data-edit-fit-overview]');
            if (!editIcon) {
              dropdown.remove();
              document.removeEventListener('click', closeOnOutsideClick, true);
            }
          }
        }
      };
      setTimeout(() => {
        document.addEventListener('click', closeOnOutsideClick, true);
      }, 300);
    }, 100);
  }

  // ==========================================
  // CART & UTILITY METHODS
  // ==========================================

  ajaxRefreshCart() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

        const cartDrawer = document.querySelector('cart-drawer-component');
        if (!cartDrawer || !(cartDrawer instanceof HTMLElement)) {
          this.openCartDrawerFallback();
          return;
        }

        const cartDrawerAny = /** @type {any} */ (cartDrawer);
        if (typeof cartDrawerAny.updateItems === 'function') {
          cartDrawerAny.updateItems(cart.items || []);
        } else {
          fetch('/cart?view=drawer')
            .then(r => r.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newCart = doc.querySelector('cart-items-component');
              const currentCart = cartDrawer.querySelector('cart-items-component');
              if (newCart && currentCart && currentCart instanceof HTMLElement) {
                currentCart.innerHTML = newCart.innerHTML;
              }
            });
        }

        if (typeof cartDrawerAny.open === 'function') {
          cartDrawerAny.open();
        } else {
          this.openCartDrawerFallback();
        }
      })
      .catch(err => {
        alert('✓ Item added to cart successfully!\n\nClick the cart icon to view your cart.');
      });
  }

  openCartDrawerFallback() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:open'));

    setTimeout(() => {
      const cartTriggers = [
        'cart-drawer-component',
        '[aria-controls="cart-drawer"]',
        'button[data-open-cart]',
        '.cart-icon',
        '.cart-link',
        '.header-cart',
        '#cart-icon-bubble',
        'a[href="/cart"]'
      ];

      for (const selector of cartTriggers) {
        const trigger = document.querySelector(selector);
        if (trigger && trigger instanceof HTMLElement) {
          trigger.click();
          return;
        }
      }
    }, 100);
  }

  openCartDrawer() {
    this.ajaxRefreshCart();
  }

  /**
   * Add to Cart only on Summary for purchase paths (As Last / new measurements).
   * Try-On and Bespoke are informational — hide ATC there.
   * @returns {boolean}
   */
  shouldShowAddToCartOnSummary() {
    if (this.activeTab !== 2) return false;
    return this.selectedPath !== 'tryon' && this.selectedPath !== 'bespoke';
  }

  updateAddToCartButtonVisibility() {
    if (!this.productDetailsContainer) return;

    const showAtc = this.shouldShowAddToCartOnSummary();

    // Handle the custom API button we created
    const customBtn = this.productDetailsContainer.querySelector('#add-to-cart-with-api');
    if (customBtn) {
      customBtn.style.display = showAtc ? 'flex' : 'none';
    }

    const externalContainer = this.productDetailsContainer.querySelector('.external-add-to-cart-container');
    if (externalContainer instanceof HTMLElement) {
      externalContainer.style.display = showAtc ? '' : 'none';
    }

    const customizationContainer = this.productDetailsContainer.querySelector('.customization-container');
    if (!customizationContainer) return;

    const addToCartContainer = customizationContainer.querySelector('#customization-add-to-cart-container');
    if (!addToCartContainer || !(addToCartContainer instanceof HTMLElement)) return;

    if (showAtc) {
      const originalAddToCart = document.querySelector('.buy-buttons-block');

      if (originalAddToCart && originalAddToCart instanceof HTMLElement && !addToCartContainer.querySelector('.buy-buttons-block')) {
        // Hide the original so only the clone inside the customizer is clickable.
        // The original has no conflict check — leaving it visible causes double-adds.
        originalAddToCart.style.display = 'none';
        this._originalBuyButtonsBlock = originalAddToCart;

        const clonedAddToCart = originalAddToCart.cloneNode(true);
        if (clonedAddToCart instanceof HTMLElement) {
          clonedAddToCart.style.display = 'block';
          clonedAddToCart.style.visibility = 'visible';
          clonedAddToCart.style.opacity = '1';
          addToCartContainer.appendChild(clonedAddToCart);

          this.setupCustomizationFormSubmission(clonedAddToCart);

          const productForm = clonedAddToCart.querySelector('product-form-component, .shopify-product-form');
          if (productForm && productForm instanceof HTMLElement) {
            window.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: productForm.dataset?.sectionId } }));
          }
        }
      } else {
        const existing = addToCartContainer.querySelector('.buy-buttons-block');
        if (existing && existing instanceof HTMLElement) {
          existing.style.display = 'block';
          existing.style.visibility = 'visible';
          existing.style.opacity = '1';
        }
      }
    } else {
      // Hide the clone.
      const existing = addToCartContainer.querySelector('.buy-buttons-block');
      if (existing && existing instanceof HTMLElement) {
        existing.style.display = 'none';
      }
      // Restore the original so it's visible outside the customiser flow
      // (only when leaving Summary / customizer purchase UI).
      if (this.activeTab !== 2 && this._originalBuyButtonsBlock instanceof HTMLElement) {
        this._originalBuyButtonsBlock.style.display = '';
      }
    }
  }

  /**
   * @param {HTMLElement} formContainer
   */
  setupCustomizationFormSubmission(formContainer) {
    this.customizationFormContainer = formContainer;
    const self = this;

    if (!window._customizationFetchIntercepted) {
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        const url = args[0];

        if (typeof url === 'string' && url.includes('/cart/add') && args[1] && args[1].method === 'POST') {
          const customizationData = self.getCustomizationData();

          if (args[1] && args[1].body instanceof FormData) {
            const formDataBody = args[1].body;
            Object.keys(customizationData.styleOptions).forEach(key => {
              const value = customizationData.styleOptions[key];
              if (value != null && String(value).trim() !== '') {
                formDataBody.append(`properties[${key}]`, String(value));
              }
            });
            formDataBody.append('properties[_Customization]', JSON.stringify(customizationData));
            if (typeof window.hamiltonFinalizeCartProperties === 'function') {
              const variantId = formDataBody.get('id');
              /** @type {Record<string, string>} */
              const props = {};
              for (const [key, val] of formDataBody.entries()) {
                const match = key.match(/^properties\[(.+)\]$/);
                if (!match) continue;
                props[match[1]] = String(val);
              }
              for (const key of [...formDataBody.keys()]) {
                if (key.startsWith('properties[')) formDataBody.delete(key);
              }
              const cleaned = window.hamiltonFinalizeCartProperties(props, variantId);
              Object.entries(cleaned).forEach(([k, v]) => formDataBody.append(`properties[${k}]`, v));
            }
          } else if (typeof args[1].body === 'string') {
            try {
              const jsonData = JSON.parse(args[1].body);
              if (!jsonData.properties) {
                jsonData.properties = {};
              }
              Object.keys(customizationData.styleOptions).forEach(key => {
                const value = customizationData.styleOptions[key];
                if (value != null && String(value).trim() !== '') {
                  jsonData.properties[key] = String(value);
                }
              });
              jsonData.properties['_Customization'] = JSON.stringify(customizationData);
              if (typeof window.hamiltonFinalizeCartProperties === 'function') {
                jsonData.properties = window.hamiltonFinalizeCartProperties(jsonData.properties, jsonData.id);
              }
              args[1].body = JSON.stringify(jsonData);
            } catch (e) {
              // JSON parse error
            }
          }
        }

        return originalFetch.apply(this, args);
      };
      window._customizationFetchIntercepted = true;
    }

    if (!window._customizationXHRIntercepted) {
      const originalXHROpen = XMLHttpRequest.prototype.open;
      const originalXHRSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (/** @type {string} */ method, /** @type {string} */ url, /** @type {any[]} */ ...rest) {
        this._customizationUrl = url;
        return originalXHROpen.apply(this, [method, url, ...rest]);
      };

      XMLHttpRequest.prototype.send = function (/** @type {any} */ data) {
        const url = this._customizationUrl;

        if (url && url.includes('/cart/add.js')) {
          const customizationData = self.getCustomizationData();

          if (data instanceof FormData) {
            Object.keys(customizationData.styleOptions).forEach(key => {
              data.append(`properties[${key}]`, customizationData.styleOptions[key]);
            });
            data.append('properties[_Customization]', JSON.stringify(customizationData));
          } else if (typeof data === 'string') {
            try {
              const jsonData = JSON.parse(data);
              if (!jsonData.properties) {
                jsonData.properties = {};
              }
              Object.keys(customizationData.styleOptions).forEach(key => {
                jsonData.properties[key] = customizationData.styleOptions[key];
              });
              jsonData.properties['_Customization'] = JSON.stringify(customizationData);
              data = JSON.stringify(jsonData);
            } catch (e) {
              // JSON parse error
            }
          }
        }

        return originalXHRSend.apply(this, [data]);
      };
      window._customizationXHRIntercepted = true;
    }

    const forms = formContainer.querySelectorAll('form.shopify-product-form, form[action="/cart/add"]');

    forms.forEach(form => {
      const addCustomizationToForm = () => {
        const customizationData = this.getCustomizationData();

        Object.keys(customizationData.styleOptions).forEach(key => {
          const value = customizationData.styleOptions[key];

          const existingInput = form.querySelector(`input[name="properties[${key}]"]`);
          if (existingInput) {
            existingInput.remove();
          }

          const input = document.createElement('input');
          if (input instanceof HTMLInputElement) {
            input.type = 'hidden';
            input.name = `properties[${key}]`;
            input.value = value || '';
            form.appendChild(input);
          }
        });

        const existingCustomInput = form.querySelector('input[name="properties[_Customization]"]');
        if (existingCustomInput) {
          existingCustomInput.remove();
        }

        const customDataInput = document.createElement('input');
        customDataInput.type = 'hidden';
        customDataInput.name = 'properties[_Customization]';
        customDataInput.value = JSON.stringify(customizationData);
        form.appendChild(customDataInput);
      };

      form.addEventListener('submit', (e) => {
        // Prevent native form submit — customization add-to-cart is handled
        // exclusively by the button click handler via addToCartAndCallAPI.
        // Without this, a form submit event (keyboard Enter, programmatic
        // submit, or a second rapid click) would fire a bare Shopify form POST
        // in parallel with the async conflict-check / API path = duplicate add.
        e.preventDefault();
        addCustomizationToForm();
      }, { capture: true });

      const buttons = form.querySelectorAll('button[type="submit"], button[name="add"], #BuyButtons-ProductSubmitButton');
      buttons.forEach(button => {
        // Skip if we've already attached the conflict-checking handler to this button.
        if (button.hasAttribute('data-conflict-check-attached')) return;
        button.setAttribute('data-conflict-check-attached', 'true');

        button.addEventListener('click', async (e) => {
          // Prevent the native form submit synchronously — this MUST happen before
          // any await so the browser doesn't submit the form while we're checking.
          e.preventDefault();
          e.stopImmediatePropagation();

          // Run the MTO measurement conflict check (same logic as #add-to-cart-with-api).
          const currentMeasurements = self._resolveFitMeasurements();
          const { hasConflict, mtoItems } = await window.MtoCartConflict.checkCartMeasurementConflict(currentMeasurements);
          if (hasConflict) {
            const proceed = await window.MtoCartConflict.showMeasurementConflictModal();
            if (!proceed) return; // Customer cancelled — nothing added.
            try {
              await window.MtoCartConflict.updateCartMeasurements(mtoItems, currentMeasurements);
            } catch (err) {
              // Surface the error — a silent partial update is worse than a visible failure.
              alert(err.message || 'Unable to update cart measurements. Please refresh the page and try again.');
              return;
            }
          }

          // Disable button to prevent double-clicks while adding.
          const originalText = button.textContent;
          button.disabled = true;
          button.textContent = 'Adding...';

          try {
            // Use the same API path as #add-to-cart-with-api so customization
            // data, metafield sync, and cart drawer behaviour are all consistent.
            await self.addToCartAndCallAPI();
          } catch (err) {
            console.error('[NativeBtn] Add to cart error:', err);
          } finally {
            button.disabled = false;
            button.textContent = originalText;
          }
        }, { capture: true });
      });
    });

    formContainer.addEventListener('change', () => {
      const forms = formContainer.querySelectorAll('form.shopify-product-form, form[action="/cart/add"]');
      forms.forEach(form => {
        const customizationData = this.getCustomizationData();
        if (Object.keys(customizationData.styleOptions).length > 0) {
          Object.keys(customizationData.styleOptions).forEach(key => {
            let input = form.querySelector(`input[name="properties[${key}]"]`);
            if (!input || !(input instanceof HTMLInputElement)) {
              const newInput = document.createElement('input');
              if (newInput instanceof HTMLInputElement) {
                newInput.type = 'hidden';
                newInput.name = `properties[${key}]`;
                form.appendChild(newInput);
                input = newInput;
              }
            }
            if (input instanceof HTMLInputElement) {
              input.value = customizationData.styleOptions[key] || '';
            }
          });
        }
      });
    });
  }

  /**
   * @returns {{styleOptions: Record<string, string>}}
   */
  getCustomizationData() {
    /** @type {{styleOptions: Record<string, string>}} */
    const customData = {
      styleOptions: {}
    };

    if (this.styleOptions && Array.isArray(this.styleOptions)) {
      this.styleOptions.forEach(option => {
        if (!option || option.selected === -1 || option.selected === undefined) {
          return;
        }

        if (option.name === 'monogram') {
          const productCode = this.getMonogramProductCode(this.getVariantId());

          if (option.selected === 1 && option.monogram) {
            const initials = option.monogram.trim().toUpperCase();
            customData.styleOptions.monogram = productCode
              ? `${productCode} / Yes / ${initials}`
              : `Yes / ${initials}`;
          } else {
            customData.styleOptions.monogram = productCode ? `${productCode} / No` : 'No';
          }
        } else if (option.name === 'collar' && this.collarOptions && this.collarOptions[option.selected]) {
          const selectedCollar = this.collarOptions[option.selected];
          if (selectedCollar) {
            // Use collar_style, not collar — collar is the size measurement key.
            // Storing style under collar would overwrite the size in cart properties.
            customData.styleOptions.collar_style = selectedCollar.title || '';
          }
        } else if (option.name === 'cuff' && this.cuffOptions && this.cuffOptions[option.selected]) {
          const selectedCuff = this.cuffOptions[option.selected];
          if (selectedCuff) {
            customData.styleOptions.cuff = selectedCuff.title || '';
          }
        } else if (option.name === 'pocket' && this.pocketOptions && this.pocketOptions[option.selected]) {
          const selectedPocket = this.pocketOptions[option.selected];
          if (selectedPocket) {
            customData.styleOptions.pocket = selectedPocket.title || '';
          }
        } else if (option.name === 'tail' && this.tailOptions && this.tailOptions[option.selected]) {
          const selectedTail = this.tailOptions[option.selected];
          if (selectedTail) {
            customData.styleOptions.tail = selectedTail.title || '';
          }
        } else if (option.name === 'front' && this.frontOptions && this.frontOptions[option.selected]) {
          const selectedFront = this.frontOptions[option.selected];
          if (selectedFront) {
            customData.styleOptions.front = selectedFront.title || '';
          }
        } else if (option.selected !== -1 && option.options && option.options[option.selected]) {
          const selectedOption = option.options[option.selected];
          customData.styleOptions[option.name] = selectedOption.summaryName || selectedOption.name || selectedOption.title;
        }
      });
    }

    if (window.cachedSelections && window.cachedSelections.style && Array.isArray(window.cachedSelections.style)) {
      window.cachedSelections.style.forEach(styleItem => {
        if (styleItem && styleItem.group && styleItem.value) {
          // collar style must use collar_style key — collar is reserved for the size measurement.
          const key = styleItem.group === 'collar' ? 'collar_style' : styleItem.group;
          if (!customData.styleOptions[key]) {
            customData.styleOptions[key] = styleItem.value;
          }
        }
      });
    }

    return customData;
  }

  initAddToCartHook() {
    const button = document.querySelector("#BuyButtons-ProductSubmitButton-AUUtHT0I1ZGFVdHNEQ__add-to-cart");
    if (button && button instanceof HTMLElement) {
      button.addEventListener("click", (e) => {
        const savedSelections = JSON.parse(sessionStorage.getItem('cachedSelections') || '[]');
        this.updateCustomizationInput();
      });
    }

    // External add to cart container
    const externalAddToCart = document.querySelector('.external-add-to-cart-container');
    if (externalAddToCart && externalAddToCart instanceof HTMLElement) {
      externalAddToCart.addEventListener('click', async (e) => {
        e.preventDefault();
        try {
          // Ensure button shows loading state if possible
          const btnText = externalAddToCart.querySelector('.add-to-cart-text');
          const originalText = btnText ? btnText.textContent : '';

          if (btnText) btnText.textContent = 'Adding...';

          await this.addToCartAndCallAPI();

          if (btnText) btnText.textContent = originalText;

        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Failed to add to cart. Please try again.');

          const btnText = externalAddToCart.querySelector('.add-to-cart-text');
          if (btnText) btnText.textContent = 'Add to Cart';
        }
      });
    }
  }

  updateCustomizationInput() {
    const form = document.querySelector("#BuyButtons-ProductForm-AYnAwTkIySXJGZHJmU__buy_buttons_eYQEYi");
    if (!form || !(form instanceof HTMLFormElement)) return;

    form.querySelectorAll('input[name^="properties["]').forEach(el => {
      if (el instanceof HTMLElement) {
        el.remove();
      }
    });

    const customization = this.getCustomizationObject();

    customization.styleOptions && Object.entries(customization.styleOptions).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${key}]`;
      input.value = value;
      form.appendChild(input);
    });

    customization.fitOptions && Object.entries(customization.fitOptions).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${key}]`;
      input.value = value;
      form.appendChild(input);
    });
  }

  /**
   * @returns {{styleOptions: Record<string, string>, fitOptions: Record<string, string>}}
   */
  getCustomizationObject() {
    /** @type {Record<string, string>} */
    // collar style must use collar_style key — collar is reserved for the size measurement.
    const styleOptions = Object.fromEntries(
      (window.cachedSelections?.style || [])
        .filter(s => s?.group && s?.value)
        .map(/** @param {{group: string, value: string}} s */ s => [
          s.group === 'collar' ? 'collar_style' : s.group,
          s.value
        ])
    );
    /** @type {Record<string, string>} */
    const fitOptions = Object.fromEntries((window.cachedSelections?.fit || []).map(/** @param {{name: string, value: string}} f */ f => [f.name, f.value]));
    return { styleOptions, fitOptions };
  }

  // ==========================================
  // INITIALIZATION METHODS
  // ==========================================

  initEditButtons() {
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;

      const fitInput = target.closest('[data-fit-input]');
      const editBtn = target.closest('.edit-button');

      if (!fitInput && !editBtn) return;
      e.stopPropagation();

      const fitItem = (fitInput || editBtn)?.closest('.fit-item');
      if (!fitItem || !(fitItem instanceof HTMLElement)) return;

      const existingMenu = fitItem.querySelector('.fit-menu');

      if (existingMenu && existingMenu instanceof HTMLElement) {
        if (existingMenu.style.display === 'flex' || existingMenu.style.display === 'block') {
          existingMenu.style.display = 'none';
          return;
        }
      }

      document.querySelectorAll('.fit-menu').forEach(menu => {
        if (menu instanceof HTMLElement) {
          menu.style.display = 'none';
        }
      });

      const fitTypeElement = fitItem.querySelector('h3');
      const fitType = fitTypeElement?.textContent?.toLowerCase() || '';
      const fitName = fitItem.dataset.fitName || fitType;

      if (!fitType) return;

      if (existingMenu && existingMenu instanceof HTMLElement) {
        existingMenu.style.display = 'flex';
      }

      const menu = fitItem.querySelector('.fit-menu');
      if (menu && menu instanceof HTMLElement) {
        menu.querySelectorAll('.custom-dropdown-option').forEach(option => {
          if (option instanceof HTMLElement) {
            option.addEventListener('click', (optionEvent) => {
              optionEvent.stopPropagation();
              const value = option.textContent?.trim() || '';
              const valueInput = fitItem.querySelector('.fit-item-input');

              if (valueInput && valueInput instanceof HTMLInputElement) {
                valueInput.value = value;
                valueInput.classList.remove('error');
              }

              if (!window.cachedSelections) {
                window.cachedSelections = { fit: [], style: [] };
              }

              const existing = window.cachedSelections.fit.find(f => f.name === fitName);
              if (existing) {
                existing.value = value;
              } else {
                window.cachedSelections.fit.push({ name: fitName, value });
              }

              this.clearLastOrderForField(fitName);

              // Persist immediately so a page refresh doesn't lose this change
              this.saveCustomizationToLocalStorage(!!this.customerData);
              this.updateSummaryTabState();

              menu.style.display = 'none';
            });
          }
        });
      }
    });
  }

  initOutsideClick() {
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;

      // console.log('[initOutsideClick] Click detected on:', target);
      // console.log('[initOutsideClick] Closest fit-item:', target.closest('.fit-item'));
      // console.log('[initOutsideClick] Closest summary-dropdown:', target.closest('.summary-dropdown'));

      const openMenus = document.querySelectorAll('.fit-menu, .summary-dropdown');
      if (!target.closest('.fit-item') && !target.closest('.summary-dropdown')) {
        // console.log('[initOutsideClick] Click is OUTSIDE. Closing menus...');
        openMenus.forEach(menu => {
          if (menu instanceof HTMLElement) {
            // console.log('[initOutsideClick] Closing menu:', menu);
            menu.style.display = 'none';
            // If it's a summary dropdown, we might want to remove it entirely
            if (menu.classList.contains('summary-dropdown')) {
              menu.remove();
            }
          }
        });
      } else {
        // console.log('[initOutsideClick] Click is INSIDE. doing nothing.');
      }
    });

    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;

      const fitTypeOption = target.closest('.fit-type-option');
      if (!fitTypeOption || !(fitTypeOption instanceof HTMLElement)) return;

      const fitType = fitTypeOption.dataset.fitType;
      if (!fitType) return;

      const allOptions = document.querySelectorAll('.fit-type-option');
      allOptions.forEach(opt => {
        if (opt instanceof HTMLElement) {
          opt.classList.remove('selected');
        }
      });
      fitTypeOption.classList.add('selected');

      const fitTypeOptions = document.querySelector('.fit-type-options');
      if (fitTypeOptions && fitTypeOptions instanceof HTMLElement) {
        fitTypeOptions.classList.remove('error');
      }

      if (!window.cachedSelections) {
        window.cachedSelections = { fit: [], style: [] };
      }

      const existing = window.cachedSelections.fit.find(f => f.name === 'fit');
      if (existing) {
        existing.value = fitType;
      } else {
        window.cachedSelections.fit.push({ name: 'fit', value: fitType });
      }

      this.clearLastOrderForField('fit');

      // Persist immediately so a page refresh doesn't lose this change
      this.saveCustomizationToLocalStorage(!!this.customerData);

      const fitInput = document.querySelector('[data-fit-input="fit"]');
      if (fitInput && fitInput instanceof HTMLInputElement) {
        fitInput.value = fitType;
      }
      this.updateSummaryTabState();
    });
  }

  // ---------------------------------------------------------------------------
  // MTO Cart Measurement Conflict
  // ---------------------------------------------------------------------------

  /**
   * Resolve the customer's current fit measurement selections from the cached
   * state, honouring the fitMostRecentFields sentinel the same way
   * addToCartAndCallAPI does.
   *
   * @returns {Object}  e.g. { collar: '15.5', chest: '40', fit: 'regular', sleeve: '34' }
   */
  _resolveFitMeasurements() {
    const measurements = {};
    const fitArr = window.cachedSelections?.fit;
    if (Array.isArray(fitArr)) {
      fitArr.forEach(item => {
        if (item?.name && item?.value) {
          const value = this.fitMostRecentFields?.has(item.name)
            ? this.getTrueMostRecentValue(item.name)
            : item.value;
          if (value) measurements[item.name] = String(value).trim();
        }
      });
    }
    return measurements;
  }
}

// Export for use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductCustomizationTable;
}

// Expose globally for use in Liquid templates
if (typeof window !== 'undefined') {
  window.ProductCustomizationTable = ProductCustomizationTable;

  // Add global function to clear customization data on logout
  window.clearCustomizationDataOnLogout = function () {
    ProductCustomizationTable.clearCustomizationOnLogout();
  };

  // Listen for logout link clicks and clear data
  document.addEventListener('DOMContentLoaded', function () {
    // Find logout links and add click handlers
    const logoutSelectors = [
      'a[href*="/account/logout"]',
      'a[href*="logout"]',
      'button[data-logout]',
      '.logout-link',
      '.customer-logout',
      '[data-action="logout"]'
    ];

    logoutSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(element => {
        element.addEventListener('click', function () {
          ProductCustomizationTable.clearCustomizationOnLogout();
        });
      });
    });

    // Also check on page visibility change (user might have logged out in another tab)
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        const isCurrentlyLoggedIn = window.ShopifyUtils?.isCustomerLoggedIn?.() ||
          window.isCustomerLoggedIn === true ||
          String(window.isCustomerLoggedIn) === 'true';
        const wasLoggedInBefore = localStorage.getItem('customization_user_logged_in') === 'true';

        if (wasLoggedInBefore && !isCurrentlyLoggedIn) {
          ProductCustomizationTable.clearCustomizationOnLogout();
        }
      }
    });
  });

  // Clear data on beforeunload if user is logging out
  window.addEventListener('beforeunload', function () {
    // Check if navigating to logout URL
    const currentUrl = window.location.href;
    if (currentUrl.includes('logout')) {
      ProductCustomizationTable.clearCustomizationOnLogout();
    }
  });
}